/**
 * Internationalization (i18n) utility for GSX8
 *
 * Seven languages with automatic browser detection; the preference is saved to
 * localStorage for persistence.
 *
 * The AUTH keys (login / register / verify-email / forgot-password /
 * reset-password) are complete in every language — those pages are opened
 * straight from the KingCAD desktop clients with an explicit `?lang=`, so a
 * gap there would show a Croatian user an English sentence with no recourse
 * (issue kingcad#426). The remaining (map UI) keys exist in sl + en, plus a
 * Czech set added 2026-09 for the CZK web map — a DRAFT pending native
 * review (#597) — and a German set added 2026-11 for the language switch of
 * the user menu (#885), also a DRAFT pending native review. hr / sr / bs fall
 * back per key to English via `translate()`. A page module may add its own
 * keys with `registerTranslations()` (the Register služnosti, #885).
 *
 * #597 pass 3 (2026-09): every surface a user can reach on the CZK map reads
 * from here — page shell, access banner, user menu, AI sidebar client text,
 * context switching, app panel, measure/locate tools. The parity test
 * (`tests/unit/client/utils/i18n.test.ts`) fails on an sl key without cs, and
 * the sweep test (`i18nSweep.test.ts`) on a Slovenian literal left in one of
 * those files. Numbers and dates on the map go through `localeForLanguage`.
 */

import { BRAND } from '../../shared/utils/brand.js';

export type Language = 'sl' | 'en' | 'de' | 'cs' | 'hr' | 'sr' | 'bs';

/**
 * The exact set KingCAD may send as `?lang=`. Kept as a value (not just the
 * type) because both the URL param and the localStorage value are untrusted
 * strings that have to be validated at run time.
 */
export const SUPPORTED_LANGUAGES: readonly Language[] = ['sl', 'en', 'de', 'cs', 'hr', 'sr', 'bs'];

export function isSupportedLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

export const translations = {
  sl: {
    // Auth page - Tabs
    login: 'Prijava',
    register: 'Registracija',

    // Auth page - Form fields
    email: 'E-pošta',
    password: 'Geslo',
    firstName: 'Ime',
    lastName: 'Priimek',

    // Auth page - Placeholders
    emailPlaceholder: 'vasa.eposta@primer.si',
    passwordPlaceholder: 'Vnesite geslo',
    firstNamePlaceholder: 'Janez',
    lastNamePlaceholder: 'Novak',

    // Auth page - Buttons
    loginButton: 'Prijavi se',
    createAccount: 'Ustvari račun',
    creatingAccount: 'Ustvarjam račun...',
    forgotPassword: 'Pozabljeno geslo?',
    orContinueWith: 'ali nadaljuj z',

    // OAuth buttons
    continueWithGoogle: 'Nadaljuj z Google',
    continueWithMicrosoft: 'Nadaljuj z Microsoft',

    // Email verification status
    // #569 desktop browser login + email login link
    deviceLoginTitle: 'Prijava',
    deviceLoginTitleProduct: 'Prijava v {product}',
    deviceLoginLoading: 'Nalagam zahtevo za prijavo…',
    deviceLoginSignedInAs: 'Prijavljeni ste kot',
    deviceLoginQuestion: 'Dovolite prijavo v {product} na tej napravi?',
    deviceLoginMachine: 'Naprava',
    deviceLoginWindowsUser: 'Windows uporabnik',
    deviceLoginProgram: 'Program',
    deviceLoginRemote: 'oddaljena seja',
    deviceLoginExpiresIn: 'Zahteva poteče čez {min} min.',
    deviceLoginAllow: 'Dovoli',
    deviceLoginApproving: 'Potrjujem…',
    deviceLoginNotYou: 'Niste vi? Prijava z drugim računom',
    // #667 G16/G17: the waiting screen after »Dovoli«, the honest message when
    // the program never collected the approval, the footer switch, the one-day rule
    deviceLoginSwitchAccount: 'Prijava z drugim računom',
    deviceLoginApprovedWaiting: 'Odobreno. Čakam na program …',
    deviceLoginNotCollected: 'Program ni prevzel prijave. V programu pritisnite gumb znova in pustite okno odprto.',
    deviceLoginHintFresh: 'Namig: če se prijavite z Googlom ali prek e-pošte, te kode ne potrebujete.',
    deviceLoginCancel: 'Prekliči',
    deviceLoginDone: 'Končano — vrnite se v {product}.',
    deviceLoginDoneClose: 'Ta zavihek lahko zaprete.',
    deviceLoginExpired: 'Zahteva je potekla. V programu znova kliknite »Prijava v brskalniku«.',
    deviceLoginDenied: 'Prijava je bila preklicana.',
    deviceLoginConsumed: 'Ta zahteva je že bila uporabljena.',
    deviceLoginNotFound: 'Neznana zahteva za prijavo.',
    deviceLoginError: 'Prišlo je do napake. Poskusite znova.',
    deviceLoginBackHome: 'Na začetno stran',
    // #580 mailbox gate
    deviceConfirmNeeded: 'Za dodajanje nove naprave potrdite dostop do e-pošte računa.',
    deviceConfirmSend: 'Pošlji potrditveno kodo',
    deviceConfirmSending: 'Pošiljam…',
    deviceConfirmSentTo: 'Koda poslana na {email}. Preverite pošto (tudi neželeno).',
    deviceConfirmCodeLabel: 'Vnesite 6-mestno kodo iz e-pošte',
    deviceConfirmSubmit: 'Potrdi kodo',
    deviceConfirmChecking: 'Preverjam…',
    deviceConfirmResend: 'Ponovno pošlji',
    deviceConfirmExpiresIn: 'Koda poteče čez {min} min.',
    deviceConfirmWrong: 'Napačna koda. Poskusite znova.',
    deviceConfirmTooMany: 'Preveč napačnih poskusov. Uporabite povezavo iz e-pošte ali v programu začnite novo prijavo.',
    deviceConfirmExpiredCode: 'Koda je potekla. Pošljite novo.',
    deviceConfirmCooldown: 'Počakajte trenutek pred ponovnim pošiljanjem.',
    deviceConfirmCapped: 'Dovoljeno število pošiljanj je doseženo. Poskusite pozneje ali se prijavite z Googlom ali prek e-pošte.',
    deviceConfirmSendFailed: 'Pošiljanje e-pošte ni uspelo. Poskusite znova.',
    deviceConfirmProven: 'E-pošta potrjena — lahko dovolite prijavo.',
    deviceLinkQuestion: 'Dovolite prijavo v {product} za račun {email}?',
    deviceLinkConfirm: 'Potrdi napravo',
    deviceLinkClose: 'Zapri',
    deviceLinkInvalid: 'Povezava ni veljavna ali je potekla. V programu zahtevajte novo prijavo.',
    // #791 the device list on the approval page (block mode only)
    deviceLimitTitle: 'Seznam naprav je poln',
    deviceLimitBody: 'S tem računom je prijavljenih že {count} naprav (največ {cap}). Odstranite eno, da dovolite prijavo na tej.',
    deviceLimitCooldown: 'To napravo ste odstranili pred kratkim. Znova jo lahko dodate {date}.',
    deviceLimitRemove: 'Odstrani',
    deviceLimitRemoving: 'Odstranjujem…',
    deviceLimitRemoved: 'Naprava je odstranjena.',
    deviceLimitFreed: 'Mesto je prosto — lahko dovolite prijavo.',
    deviceLimitRemoveFailed: 'Naprave ni bilo mogoče odstraniti. Poskusite znova.',
    deviceLimitRefreshFailed: 'Naprava je odstranjena, stanja pa ni bilo mogoče osvežiti. Poskusite znova.',
    deviceLimitRetry: 'Poskusi znova',
    deviceLimitRemoveCapped: 'Dovoljeno število odstranitev naprav v tem mesecu je doseženo.',
    deviceLimitDeviceLine: '{name} · nazadnje {date}',
    deviceLinkLimited: 'S tem računom je prijavljenih že največ dovoljenih naprav. Prijavite se v brskalniku in odstranite eno napravo; ta prijava bo nato mogoča.',
    deviceLinkLimitedCooldown: 'To napravo ste pred kratkim odstranili; znova jo lahko dodate {date}.',
    deviceLinkManage: 'Prijava in upravljanje naprav',
    deviceSlowdownTitle: 'Pogoste prijave na različnih računalnikih',
    deviceSlowdownCode: 'Zaradi pogostih prijav na različnih računalnikih bo koda poslana ob {time}.',
    deviceSlowdownApprove: 'Zaradi pogostih prijav na različnih računalnikih bo prijava mogoča ob {time}.',
    deviceSlowdownExpires: 'Ta prijava bo medtem potekla — ob tej uri jo v programu zaženite znova.',
    deviceSlowdownNow: 'Zdaj lahko nadaljujete.',
    deviceLinkSlowdown: 'Zaradi pogostih prijav na različnih računalnikih bo prijava mogoča ob {time}. Če ta prijava medtem poteče, jo v programu zaženite znova.',
    loginLinkRedeeming: 'Prijavljam…',
    loginLinkSuccess: 'Prijavljeni ste',
    loginLinkContinue: 'Nadaljuj',
    loginLinkFailed: 'Povezava ni veljavna ali je potekla',
    loginLinkFailedHint: 'Povezava velja 15 minut in jo je mogoče uporabiti samo enkrat.',
    loginLinkRequestNew: 'Nazaj na prijavo',
    loginLinkOption: 'Pošlji mi kodo za prijavo',
    loginLinkSending: 'Pošiljam…',
    loginLinkSent: 'Če račun obstaja, smo kodo poslali na {email}. Preverite pošto.',
    loginLinkNeedEmail: 'Najprej vnesite e-naslov.',
    loginLinkError: 'Pošiljanje ni uspelo. Poskusite znova.',
    loginLinkSentCode: 'Vnesite kodo iz e-pošte tukaj ali kliknite povezavo v njej.',
    loginLinkCodeLabel: 'Koda iz e-pošte',
    loginLinkCodeSubmit: 'Potrdi kodo',
    loginLinkCodeChecking: 'Preverjam…',
    loginLinkCodeWrong: 'Koda ni pravilna.',
    loginLinkCodeExpired: 'Koda je potekla. Pošljite si novo.',
    loginLinkCodeUsed: 'Ta koda ne velja več: uporabljena je bila (na primer s povezavo v drugem brskalniku) ali pa jo je nadomestila novejša. Pošljite si novo.',
    loginLinkCodeRefused: 'S tem računom se tako ni mogoče prijaviti.',
    loginLinkCodeTooMany: 'Preveč poskusov. Počakajte minuto in poskusite znova.',
    loginLinkResend: 'Pošlji znova',
    loginLinkResendIn: 'Ponovno pošiljanje čez {min} min',
    loginLinkResendInS: 'Ponovno pošiljanje čez {s} s',
    // #668: mail-first login page, newest-code resend
    loginNoPasswordNeeded: 'Za prijavo ne potrebujete gesla.',
    loginWithPasswordToggle: 'Imate geslo? Prijava z geslom',
    loginLinkResent: 'Poslali smo novo kodo. Velja samo zadnja.',
    loginLinkResendUnsure: 'Povezava s strežnikom je bila prekinjena. Pošljite kodo znova. Če ste medtem prejeli novo e-pošto, uporabite povezavo v njej.',
    verifyingEmail: 'Preverjam e-pošto...',
    emailVerified: 'E-pošta potrjena!',
    emailVerifiedSuccess: 'Vaša e-pošta je bila uspešno potrjena. Zdaj se lahko prijavite.',
    verificationFailed: 'Preverjanje ni uspelo',
    verificationFailedMessage: 'Povezava za preverjanje je neveljavna ali potekla.',
    requestNewLink: 'Zahtevaj novo povezavo za preverjanje',

    // Verify email page
    continueToMap: 'Nadaljuj na zemljevid',
    backToLogin: 'Nazaj na prijavo',
    verificationLinkInvalid: 'Povezava za preverjanje je neveljavna ali potekla.',
    enterEmailForNewLink: 'Vnesite svoj e-naslov za prejemanje nove povezave za preverjanje:',
    resendVerificationEmail: 'Ponovno pošlji e-pošto za preverjanje',
    sending: 'Pošiljam...',
    verificationEmailSent: '✓ E-pošta za preverjanje poslana! Preverite svoj nabiralnik.',

    // Verify email page — pending activation (#448)
    pendingTitle: 'Preverite svoj e-poštni nabiralnik',
    pendingSentTo: 'Potrditveno povezavo smo poslali na',
    pendingExpiry: 'Povezava velja 24 ur. Račun bo aktiven, ko jo kliknete.',
    pendingWrongAddress: 'Napačen e-naslov?',
    pendingRegisterAgain: 'Registrirajte se znova s pravilnim naslovom.',
    sendFailedTitle: 'E-pošte ni bilo mogoče poslati',
    sendFailedInfo: 'Potrditvene e-pošte ni bilo mogoče poslati na',
    sendFailedRetry: 'Poskusite jo poslati znova spodaj.',
    continueToLogin: 'Nadaljuj na prijavo',
    resendAlreadyVerified: 'Ta e-naslov je že potrjen — lahko se prijavite.',
    resendInvalidEmail: 'E-naslov ni veljaven. Preverite, ali je pravilno vpisan.',
    resendTooMany: 'Preveč poskusov. Počakajte nekaj minut in poskusite znova.',
    emailNotVerifiedError: 'Vaš e-poštni naslov še ni potrjen. Račun bo aktiven, ko kliknete povezavo iz potrditvene e-pošte.',
    verifyEmailCta: 'Potrdite svoj e-naslov',
    resendNotReceived: 'Niste prejeli potrditvene e-pošte?',
    oauthNoEmail: 'Vaš Microsoft račun nima e-poštnega naslova. Ustvarite račun z e-naslovom.',
    emailAlreadyRegistered: 'Račun s tem e-naslovom že obstaja.',
    conflictSignIn: 'Prijavite se',
    conflictOr: 'ali',
    conflictReset: 'ponastavite geslo',
    conflictResetNote: 'Ponastavitev gesla hkrati potrdi vaš e-naslov.',

    // Password strength
    passwordStrength: 'Moč gesla',
    passwordTooWeak: 'Prešibko',
    passwordWeak: 'Šibko - Dodajte',
    passwordFair: 'Zadostno - Dodajte',
    passwordGood: 'Dobro - Geslo izpolnjuje zahteve',
    passwordStrong: 'Močno - Odlično geslo!',
    atLeast8Chars: 'vsaj 8 znakov',
    anUppercase: 'veliko začetnico',
    aLowercase: 'malo začetnico',
    aNumber: 'številko',

    // User menu
    logout: 'Odjava',
    loggingOut: 'Odjavljam...',
    emailAccount: 'E-poštni račun',
    googleAccount: 'Google račun',
    microsoftAccount: 'Microsoft račun',
    legacyAccount: 'Podedovan račun',

    // Errors
    loginFailed: 'Prijava ni uspela. Preverite svoje poverilnice.',
    registrationFailed: 'Registracija ni uspela. Prosimo, poskusite znova.',
    // #675: signup without a password, the name step, the KingCAD done page
    registerSubmit: 'Ustvari račun in pošlji kodo',
    registerNoPasswordNote: 'Gesla ne potrebujete. Kodo za prijavo boste prejeli po e-pošti.',
    registerCodeSent: 'Kodo smo poslali na {email}. Preverite pošto.',
    registerFixAddress: 'Napačen e-naslov?',
    registerFixAddressLink: 'Popravite ga',
    registerNameRequired: 'Vpišite ime in priimek.',
    registerDoneTitle: 'Račun je pripravljen',
    registerDoneBody: 'Vrnite se v program {product} in kliknite »Prijava v brskalniku«.',
    registerDoneOldBuild: 'Če vaša različica programa tega gumba nima, si geslo nastavite v profilu.',
    registerDoneProfileLink: 'Odpri profil',
    welcomeTitle: 'Kako vam rečemo?',
    welcomeIntro: 'Ime vidijo sodelavci pri opravilih, komentarjih in licencah.',
    welcomeSave: 'Shrani in nadaljuj',
    welcomeSkip: 'Preskoči',
    welcomeSaveFailed: 'Shranjevanje ni uspelo. Poskusite znova.',
    oauthEmailConflict: 'Račun s tem e-naslovom že obstaja. Prijavite se z geslom ali načinom, ki ste ga uporabili doslej.',
    emailVerificationFailed: 'Preverjanje e-pošte ni uspelo. Prosimo, poskusite znova ali zahtevajte novo povezavo za preverjanje.',
    networkError: 'Povezava s strežnikom ni uspela. Preverite internetno povezavo.',
    serverUnavailable: 'Strežnik trenutno ni dosegljiv. Poskusite znova čez nekaj trenutkov.',

    // Auth pages — desktop-client mode + inline copy (kingcad#426)
    optional: '(neobvezno)',
    desktopRegisterHint: 'Ta račun boste uporabljali za prijavo v programu {product}.',
    desktopVerifiedReturnHint: 'Račun je aktiviran — vrnite se v {product} in se prijavite.',
    alreadyHaveAccount: 'Že imate račun?',
    signInLink: 'Prijavite se',
    noAccountYet: 'Nimate računa?',
    createAccountLink: 'Ustvarite ga',

    // Forgot password page
    forgotPasswordTitle: 'Ste pozabili geslo?',
    forgotPasswordIntro: 'Vnesite svoj e-naslov in poslali vam bomo povezavo za ponastavitev gesla.',
    sendResetLink: 'Pošlji povezavo za ponastavitev',
    forgotPasswordFailed: 'Povezave za ponastavitev ni bilo mogoče poslati.',
    forgotCooldownNotice: 'Počakajte {seconds} s, preden zahtevate novo povezavo.',
    checkYourEmail: 'Preverite svojo e-pošto',
    resetLinkSent: 'Če račun s tem e-naslovom obstaja, smo vam poslali povezavo za ponastavitev gesla. Preverite svoj nabiralnik.',
    genericError: 'Prišlo je do napake. Poskusite znova.',

    // Reset password page
    resetPasswordTitle: 'Ponastavitev gesla',
    resetPasswordIntro: 'Spodaj vnesite novo geslo.',
    newPassword: 'Novo geslo',
    newPasswordPlaceholder: 'Vnesite novo geslo',
    confirmNewPassword: 'Potrdite novo geslo',
    confirmNewPasswordPlaceholder: 'Znova vnesite novo geslo',
    resetPasswordButton: 'Ponastavi geslo',
    resetting: 'Ponastavljam...',
    passwordResetTitle: 'Geslo je ponastavljeno!',
    passwordResetSuccess: 'Vaše geslo je bilo uspešno ponastavljeno. Zdaj se lahko prijavite z novim geslom.',
    invalidLinkTitle: 'Neveljavna ali potekla povezava',
    invalidLinkMessage: 'Ta povezava za ponastavitev gesla je neveljavna ali je potekla. Zahtevajte novo.',
    requestNewResetLink: 'Zahtevaj novo povezavo',
    passwordsDoNotMatch: 'Gesli se ne ujemata.',
    passwordMinLength: 'Geslo mora vsebovati vsaj 8 znakov.',
    passwordNeedsUppercase: 'Geslo mora vsebovati vsaj eno veliko črko.',
    passwordNeedsLowercase: 'Geslo mora vsebovati vsaj eno malo črko.',
    passwordNeedsNumber: 'Geslo mora vsebovati vsaj eno številko.',
    resetPasswordFailed: 'Gesla ni bilo mogoče ponastaviti.',
    strengthWeak: 'Šibko',
    strengthFair: 'Zadostno',
    strengthGood: 'Dobro',
    strengthStrong: 'Močno',

    // Map toolbar
    contours: 'Plastnice',
    zoomInToView: 'Približaj za ogled',
    zoomOutToView: 'Oddalji za ogled',
    zoomInForAll: 'Prikazane samo glavne trase, približaj za vse',
    gjiNoKindSelected: 'Nobena vrsta GJI ni izbrana',
    layerKindRaster: 'raster',
    layerKindVector: 'vektor',
    layerOpacity: 'Prosojnost',
    layersOnCount: 'Prižgano',
    view25d: 'Pogled 2.5D',

    // Right controls
    rightControlsPitch: 'Nagib',
    rightControlsContours: 'Plastnice',
    rightControlsHillshade: 'Senčenje',
    rightControlsTerrain: '2.5D Pogled',
    rightControlsBuildings3d: '2.5D Stavbe',
    rightControlsTitle: 'Nastavitve mape',

    // Toolbar
    toolbarParcelValue: 'Vrednost parcel',
    toolbarAdminStatus: 'Upravni status',
    toolbarConnections: 'Priključki',
    toolbarYearOfConstruction: 'Leto izgradnje',

    // Basemap
    basemapRaster: 'Ortofoto',
    basemapCuzkOrtofoto: 'Ortofoto ČÚZK',
    basemapMore: 'Več',
    basemapStyleTitle: 'Stilizacija bazne mape',

    // SearchBar
    searchPlaceholder: 'Išči po zemljevidu',

    // FeatureCard
    toastCopied: 'Kopirano!',
    toastShowingDistance: 'Prikazana razdalja!',

    // Print dialog (#611: the title is pre-filled with the nearest address)
    printTitle: 'Naslov za tisk',
    printDescription: 'Vpisan je najbližji naslov – po želji ga dopolnite.',
    printDescriptionSearching: 'Iščem najbližji naslov …',
    printDescriptionNoAddress: 'V krogu {m} m ni naslova – vpišite poljuben naslov.',
    printDescriptionLookupFailed: 'Najbližjega naslova ni bilo mogoče poiskati – vpišite poljuben naslov.',
    printDescriptionNearestHint: 'Najbližji naslov: {naslov}',
    printPlaceholder: 'npr., Moj Projekt',
    printCancel: 'Prekliči',
    printContinue: 'Nadaljuj s tiskanjem',
    printOrientation: 'Postavitev',
    printLandscape: 'Ležeče',
    printPortrait: 'Pokončno',
    printPaperSize: 'Format papirja',
    // ── V6 map shell (#597 pass 2) ────────────────────────────────────────
    // Section names shared by the icon rail, the sliding-panel headers, the
    // mobile bottom bar / sheet and the hamburger drawer.
    sectionActivity: 'Aktivnost',
    sectionLayers: 'Sloji',
    sectionPhotos: 'Slike',
    sectionTasks: 'Opravila',
    sectionBasemap: 'Zemljevid',
    sectionDocs: 'Dokumenti',
    sectionApp: 'Aplikacija',
    sectionPobude: 'Pobude',
    sectionTraffic: 'Promet',
    sectionAirQuality: 'Kakovost zraka',
    sectionTrees: 'Drevesa',
    sectionWorks: 'Investicije',
    sectionGraditve: 'Graditve',
    worksOpenGraditevPage: 'Odpri King.Graditev',
    sectionSewer: 'Kanalizacija',
    sectionOptGap: 'OPT brez omrežja',
    sectionOpnConditions: 'OPN pogoji za gradnjo',
    sectionParcelHistory: 'Zgodovina parcel',

    // Icon rail
    railTabOpnShort: 'OPN pogoji',
    railTabHistoryShort: 'Zgodovina',
    railNavigation: 'Navigacija',
    railSettings: 'Nastavitve',
    bottomBarAi: 'AI',

    // Floating top bar
    topbarAiAssistant: `${BRAND.name} asistent`,

    // Layers panel
    // CZK layers panel (#598) - the Czech reference layers from the map style
    czkGroupKatastr: 'Kataster nepremičnin',
    czkGroupSpravni: 'Upravna razdelitev',
    czkGroupAdresy: 'Naslovi',
    czkGroupTematicke: 'Tematski pogledi',
    czkLayerParcely: 'Parcele',
    czkLayerBudovy: 'Stavbe',
    czkLayerHranice: 'Meje parcel',
    czkLayerKatastralniUzemi: 'Katastrske občine',
    czkLayerStat: 'Država',
    czkLayerRegiony: 'Kohezijske regije',
    czkLayerVusc: 'Kraji (VÚSC)',
    czkLayerOkresy: 'Okraji',
    czkLayerOrp: 'ORP',
    czkLayerPou: 'POU',
    czkLayerObce: 'Občine',
    czkLayerMomc: 'Mestni deli (MOMC)',
    czkLayerPraha: 'Praga - okrožja',
    czkLayerZsj: 'Osnovne poselitvene enote',
    czkLayerAdresy: 'Naslovi',
    czkLayerDruhPozemku: 'Vrsta rabe zemljišča',
    czkLayerStavebniParcely: 'Stavbne parcele',
    czkLayerVyuzitiBudov: 'Namen stavb',
    czkLayerRokDokonceni: 'Leto dokončanja',

    layersPanelGroupGurs: 'GURS sloji',
    layersPanelGroupCuzk: 'ČÚZK sloji',
    layersPanelGroupPresets: 'Posebni pogledi',
    layersPanelGroupView: 'Pogled',
    layersPanelPresetUtilityConnections: 'Komunalni priključki',
    layersPanelBuildings3d: '3D stavbe',

    // Share view
    shareViewButton: 'Deli pogled',
    shareViewCopied: 'Povezava kopirana',

    // Generic chrome
    commonClose: 'Zapri',
    commonBack: 'Nazaj',
    contextAllProjects: 'Vsi projekti',
    docsNone: 'Ni razpoložljivih dokumentov',

    // Mobile hamburger drawer
    mobileMenuTitle: 'Meni',
    mobileMenuWorksSub: 'Območja in skice',
    mobileMenuSewerSub: 'Izbira variante',
    mobileMenuPobudeSub: 'MOL × VVO analiza',
    mobileMenuTrafficSub: 'Telraam senzorji',
    mobileMenuTreesSub: 'Kataster dreves — filtri',
    mobileMenuOptGapSub: 'Priključki brez trase / kabla v pogledu',
    mobileMenuOpnSub: 'Iz občinskega odloka (OPN)',
    mobileMenuAiAssistant: 'AI pomočnik',
    mobileMenuPzDraw: 'Nariši območje',
    mobileMenuPzSearch: 'Poišči parcelo ali naslov',

    // Quick actions (FAB / sidebar toolbar)
    fabAddPhotoAria: 'Dodaj fotografijo',
    fabAddPhoto: 'Dodaj foto',
    fabPhoto: 'Foto',
    fabNewTaskAria: 'Novo opravilo',
    fabTask: 'Opravilo',
    fabMeasure: 'Merjenje',
    fabAddPost: 'Dodaj objavo',

    // Feature popup chrome
    popupFullscreen: 'Celoten zaslon',
    popupOpenLink: 'Odpri',
    popupClickCoordinates: 'Koordinate klika',
    popupCopyToClipboard: 'Kopiraj v odložišče',
    popupShareFeature: 'Kopiraj povezavo do objekta',
    popupNavigateTo: 'Navigiraj do objekta',
    popupPrevCard: 'Prejšnja kartica',
    popupNextCard: 'Naslednja kartica',
    popupParcelLoadError: 'Napaka pri nalaganju parcele. Poskusite znova.',
    popupParcelNotFoundKo: 'Parcele KO {ko}, št. {st} ni mogoče najti.',
    popupParcelNotFound: 'Parcele ni mogoče najti.',
    popupObjectNotFound: 'Objekt ni bil najden',

    // Zemljiška knjiga link on the parcel card (#684)
    zkButton: 'Zemljiška knjiga',
    zkGetExtract: 'Pridobi izpisek iz ZK',
    zkKo: 'KO',
    zkParcel: 'parcela',
    zkCopy: 'Kopiraj',
    zkCopied: 'Kopirano',
    zkHint: 'Na strani e-Sodstva kliknite zaznamek »King.Prostor ZK«.',
    zkSetupLink: 'Nastavitev zaznamka',
    zkCopyOwners: 'Prepiši lastnike na parcelo',
    zkCopying: 'Prepisujem lastnike …',
    zkCopied2: 'Lastniki so prepisani ({n}). Vir: zemljiška knjiga.',
    zkCopyJoint: 'Skupna lastnina: deleži niso določeni, zato je parcela označena kot »lastniki nepopolni«, dokler lastnikov ne potrdite.',
    zkCopySeal: 'Na nepremičnini je plomba.',
    zkCopyNewerExport: 'Prepisano, a na tej parceli velja novejši izvoz geodeta.',
    zkCopyStavba: 'Lastnik parcele je vsakokratni lastnik stavbe (lastniki delov stavbe) — lastnike parcele dodajte ročno.',
    zkCopyReload: 'Stran je zastarela — osvežite jo in izpisek prepišite znova.',
    zkCopyNotInChain: 'Ta stavba ali del stavbe ni v verigi lastnikov te parcele.',
    zkCopyChainNoParcel: 'Najprej prepišite izpisek parcele.',
    zkVsakokratni: 'vsakokratni lastnik nepremičnine',
    zkCopyNotRegular: 'Prepišemo lahko samo redni izpis.',
    zkCopyOtherParcel: 'Izpisek je za drugo parcelo.',
    zkCopyIncomplete: 'Izpiska nismo prebrali v celoti, zato ga ne prepišemo.',
    zkCopyNoOwners: 'V izpisku ni lastnikov.',
    zkCopyOlder: 'Na tej parceli je že novejši izpisek.',
    zkCopyDuplicate: 'Ta izpisek je že prepisan.',
    zkCopyNoWork: 'Ta projekt še nima graditve.',
    zkCopyNoRight: 'Za prepis potrebujete pravico »Lastniki in služnosti«.',
    zkCopyError: 'Lastnikov ni bilo mogoče prepisati.',
    zkPopupBlocked: 'Brskalnik je preprečil odpiranje e-Sodstva. Dovolite pojavna okna in poskusite znova.',
    zkOpened: 'e-Sodstvo je odprto v novem zavihku. Po prijavi kliknite zaznamek »King.Prostor ZK«.',
    zkReading: 'Berem izpisek …',
    zkExtractOf: 'Izpisek z dne {date}',
    zkOwners: 'Lastniki',
    zkNoOwners: 'V izpisku ni vknjižene lastninske pravice.',

    // »Hišne številke« fold on the EK spojka card (#891)
    mpOwnersSection: 'Lastniki (delež, številka, naziv, naslov, vir)',
    mpOwnersSectionOne: 'Lastnik',
    mpOwnersLoading: 'nalagam …',
    mpOwnersError: 'Lastnikov ni bilo mogoče naložiti.',
    mpOwnersErrorAuth: 'Prijava ni več veljavna. Osvežite stran.',
    ekHsSection: 'Hišne številke',
    ekHsLoading: 'nalagam …',
    ekHsEmpty: 'Na to spojko ni vezana nobena hišna številka.',
    ekHsError: 'Hišnih številk ni bilo mogoče naložiti.',
    ekHsErrorForbidden: 'Nimate dostopa do podatkov te spojke.',
    ekHsErrorAuth: 'Prijava ni več veljavna. Osvežite stran.',
    ekHsGone: 'ni več v registru',
    ekHsNoPoint: 'brez lokacije',
    ekHsElsewhere: 'vezan tudi na {oznaka}',
    ekHsWithheld: '{n} v projektih brez dostopa',
    ekHsSpliter: 'spliter',
    // »Hišne številke v projektu« under the two EK layers (#891 phase 2)
    ekHsProjectNone: 'Izberite projekt.',
    ekHsProjectLinked: 'vezane: {n}',
    ekHsProjectUnlinked: 'nevezane: {n}',
    ekHsProjectInView: 'v pogledu',
    ekHsProjectNoArea: 'Projekt nima območja – nevezane niso prikazane.',
    ekHsProjectCapped: 'prikazanih {n} od {m} – povečajte prikaz',
    ekHsProjectForbidden: 'Nimate dostopa do podatkov tega projekta.',
    zkShare: 'delež',
    zkRights: 'Izvedene pravice in zaznambe',
    zkMainParcel: 'glavna nepremičnina',
    zkWarnings: 'Opozorila',
    zkError: 'Izpiska ni bilo mogoče prebrati.',
    popupErrorNoAccessTitle: 'Ni dostopa do podatkov',
    popupErrorNoAccessMessage: 'Nimate pravice za ogled tega sloja.',
    popupErrorServerTitle: 'Napaka strežnika',
    popupErrorServerMessage:
      'Prišlo je do napake pri pridobivanju podatkov.\nPoskusite znova ali kontaktirajte podporo.',
    popupErrorConnectionTitle: 'Napaka pri povezavi',
    popupErrorConnectionMessage: 'Podatkov ni bilo mogoče pridobiti.\nPreverite internetno povezavo.',
    // ── Map screens (#597 pass 3) ─────────────────────────────────────────
    // Everything a czk user could still meet in Slovenian or English after
    // pass 2: page shell, access banner, user menu, AI sidebar client text,
    // context switching, app panel, layers/popup leftovers, measure + locate.
    // Each sl value is the former literal, verbatim (English ones got copy).
    // Page shell
    pageTitleMap: 'Aktivnosti',
    pageAiOpenAria: `Odpri ${BRAND.name} asistenta`,
    pageErrorTitle: 'Napaka',
    pageMapLoadFailed: 'Zemljevida ni bilo mogoče naložiti. Poskusite znova.',
    pageInitFailed: 'Aplikacije ni bilo mogoče zagnati. Poskusite znova.',
    pageBackHome: 'Nazaj na začetno stran',
    pageNoAccessTitle: 'Ni dostopa',
    pageNoAccessBody: 'Nimate dostopa do nobene aplikacije. Kontaktirajte administratorja.',
    cadOverlayTitle: 'Načrt',
    mapCanvasAria: 'Zemljevid',
    commonRequestAccess: 'Zaprosi za dostop',
    basemapButtonAria: 'Izberi podlago zemljevida',

    // Access banner (MapAccessBanner)
    bannerSignin: 'Za ogled tega zemljevida se prijavite.',
    bannerSessionExpired: 'Seja je potekla. Za nadaljevanje se prijavite znova.',
    bannerForbidden: 'Nimate dostopa do te aplikacije.',
    bannerForbiddenProject: 'Nimate dostopa do tega projekta.',
    bannerNotFoundApp: 'Aplikacije ni mogoče najti. Preverite povezavo.',
    bannerNotFoundProject: 'Projekta ni mogoče najti. Preverite povezavo.',
    bannerNewVersion: 'Na voljo je nova različica.',
    bannerReload: 'Osveži',

    // User menu (UserAvatar) — was English on every page
    userMenuAuthEmailLegacy: 'E-poštni račun (starejši)',
    userMenuAuthEmail: 'E-poštni račun',
    userMenuAuthGoogle: 'Google račun',
    userMenuAuthMicrosoft: 'Microsoft račun',
    userMenuAuthEmailLink: 'E-poštni račun (prijava s kodo)',
    userMenuLogoutFailed: 'Odjava ni uspela. Poskusite znova.',
    // #885: the language switch in the user menu
    languageSwitchLabel: 'Jezik',
    languageSwitchBusy: 'Najprej shranite ali zaprite odprto urejanje, nato izberite jezik.',
    languageSwitchNoStorage: 'Brskalnik ne dovoli shraniti izbire jezika.',
    userMenuLoggingOut: 'Odjavljam…',
    userMenuLogout: 'Odjava',
    userMenuDashboard: 'Nadzorna plošča',
    userMenuProfile: 'Profil',
    userMenuLoading: 'Nalaganje…',
    userMenuUserFallback: 'Uporabnik',

    // AI sidebar — client text only; the assistant's answers are the server's
    aiAssistantLabel: 'Asistent',
    aiStatusAvailable: 'Na voljo',
    aiInputPlaceholder: 'Vprašajte nekaj...',
    aiNewChat: 'Nov pogovor',
    aiCloseAria: 'Zapri AI asistenta',
    aiSendAria: 'Pošlji',
    aiLockText: 'Prijavite se za AI pomočnika',
    aiGreeting: 'Pozdravljeni! Sem vaš AI asistent za prostorske podatke. Kako vam lahko pomagam?',
    aiQuick1Label: 'Stavba na naslovu',
    aiQuick1Question: 'Stavba na naslovu Prešernov trg 1, Ljubljana?',
    aiQuick2Label: 'Parcela na naslovu',
    aiQuick2Question: 'Parcela na naslovu Soča 28, Bovec?',
    aiQuick3Label: 'Okoljske omejitve na parceli',
    aiQuick3Question: 'Preveri okoljske omejitve na parceli 63/4, k.o. SOČA DESNA (2209)?',
    aiQuick4Label: 'Najbližji hidrant',
    aiQuick4Question: 'Najbližji hidrant za parcelo 2189/7, k.o. VIČ (1723)?',
    // The same four slots on a ČÚZK-backed map (search profile 'cz', #593):
    // Czech places, questions the cz search tools can answer.
    aiQuickCz1Label: 'Stavba na naslovu',
    aiQuickCz1Question: 'Stavba na naslovu Staroměstské náměstí 1, Praha?',
    aiQuickCz2Label: 'Parcela na naslovu',
    aiQuickCz2Question: 'Parcela na naslovu Karlova 1, Praha?',
    aiQuickCz3Label: 'Parcela po številki',
    aiQuickCz3Question: 'Kje je parcela 1090, k.ú. Staré Město (727024)?',
    aiQuickCz4Label: 'Katastrsko območje',
    aiQuickCz4Question: 'Kje je katastrsko območje Abertamy?',
    aiThinking: 'Razmišljam…',
    aiProcessing: 'Obdelujem…',
    aiErrorOverloaded: 'AI servis je trenutno preobremenjen. Poskusite znova čez nekaj sekund.',
    aiErrorNoResponse: 'AI servis se ni odzval. Poskusite znova.',
    aiErrorGeneric: 'Prišlo je do napake. Poskusite znova.',
    aiErrorInterrupted: 'Odgovor se je prekinil med razmišljanjem. Poskusite znova.',
    aiErrorEmpty: 'Ni bilo odgovora. AI servis je morda preobremenjen.',
    aiErrorComm: 'Napaka pri komunikaciji z AI.',
    aiErrorMonthlyLimit: 'Mesečna omejitev uporabe AI je dosežena. Poskusite znova naslednji mesec.',
    aiRetry: 'Poskusi znova',
    // AI tool indicators (one per server tool name)
    aiToolSearchParcel: 'Iščem parcelo…',
    aiToolSearchAddress: 'Iščem naslov…',
    aiToolSearchPlace: 'Iščem lokacijo…',
    aiToolGetParcelInfo: 'Pridobivam podatke o parceli…',
    aiToolFindOwner: 'Iščem lastnika…',
    aiToolSearchParcelsByOwner: 'Iščem parcele po lastniku…',
    aiToolGetBuildingInfo: 'Pridobivam podatke o stavbi…',
    aiToolGetInfrastructureInfo: 'Pridobivam podatke o infrastrukturi…',
    aiToolFindParcelsNear: 'Iščem bližnje parcele…',
    aiToolFindInfrastructureNear: 'Iščem bližnjo infrastrukturo…',
    aiToolSearchEkFeatures: 'Iščem EK objekte…',
    aiToolGetRouteInfo: 'Pridobivam podatke o trasi…',
    aiToolGetCableInfo: 'Pridobivam podatke o kablu…',
    aiToolGetSpliceInfo: 'Pridobivam podatke o spojki…',
    aiToolFindEkFeaturesNear: 'Iščem bližnje EK objekte…',
    aiToolGetProjectEkSummary: 'Pridobivam povzetek projekta…',
    aiToolFindHouseConnectionsNear: 'Iščem hišne priključke…',
    aiToolGetRoutePipes: 'Pridobivam cevi trase…',
    aiToolGetRouteCables: 'Pridobivam kable trase…',
    aiToolGetSpliceCables: 'Pridobivam kable spojke…',
    aiToolGetCableFibers: 'Pridobivam vlakna kabla…',
    aiToolGetCableReserves: 'Pridobivam rezerve kabla…',
    aiToolLocateCableFault: 'Računam mesto okvare…',
    aiToolQueryEkLengths: 'Računam dolžine EK omrežja…',
    aiToolSearchLayers: 'Iščem sloje…',
    aiToolGetLayerSchema: 'Pridobivam shemo sloja…',
    aiToolGetAppMapCatalog: 'Pridobivam katalog slojev…',
    aiToolGetEnvironmentalConstraints: 'Preverjam okoljske omejitve…',
    aiToolGetEnvironmentalConstraintsForParcel: 'Preverjam omejitve za parcelo…',
    aiToolFindArsoFeaturesNear: 'Iščem ARSO objekte v bližini…',
    aiToolGetParcelZoning: 'Preverjam namensko rabo…',
    aiToolGetParcelConditions: 'Berem pogoje gradnje iz odloka…',
    aiToolListProjectOpinions: 'Berem mnenja…',
    aiToolReadOpinionDocument: 'Berem dokument mnenja…',
    aiToolGetGraditevConstraints: 'Preverjam omejitve na območju posega…',
    aiToolGetGraditevConstraintDetails: 'Berem omejitev in izvlečke predpisov…',
    aiToolListGraditevReports: 'Iščem tehnična poročila načrtov…',
    aiToolReadGraditevReport: 'Berem tehnično poročilo…',
    aiToolFindInLaw: 'Iščem po besedilu predpisov…',
    aiToolGetProjectConsentSummary: 'Preverjam stanje soglasij parcel…',
    aiToolGetRouteConsentStatus: 'Preverjam soglasja vzdolž trase…',
    aiToolFindParcelsMissingConsent: 'Iščem parcele brez soglasij…',
    aiToolControlMapLayers: 'Preklapljam sloje…',
    aiToolSwitchBasemap: 'Menjam osnovno karto…',
    aiToolToggleSpecialView: 'Preklapljam pogled…',

    // Context switching (header switcher, project + app dialogs)
    ctxSearchAria: 'Iskanje konteksta',
    ctxRecent: 'Nedavno',
    ctxOtherApps: 'Druge aplikacije',
    ctxNoResults: 'Ni zadetkov',
    ctxNoResultsAlt: 'Ni rezultatov',
    ctxApps: 'Aplikacije',
    ctxAppsCount: 'Aplikacije ({n})',
    ctxProjects: 'Projekti',
    ctxCurrentApp: 'Trenutna aplikacija',
    ctxCurrentAppSr: '(trenutna aplikacija)',
    ctxSwitchTitle: 'Zamenjaj kontekst',
    ctxSearchPlaceholder: 'Iskanje...',
    ctxAllApps: 'Vse aplikacije',
    ctxLoading: 'Nalagam...',
    ctxProjectsOfCount: '{n} od {total} projektov',
    ctxFindProject: 'Poišči projekt …',
    ctxFindApp: 'Poišči aplikacijo …',
    ctxDashboardLink: 'Nadzorna plošča →',
    ctxHints: '↑↓ premikanje · Enter izbira · Esc zapri',
    ctxHintsRequired: '↑↓ premikanje · Enter izbira',
    ctxLoadingProjects: 'Nalaganje projektov …',
    ctxNoProjects: 'Ni projektov',
    ctxRequiredNote:
      'Izberite projekt, da se prikažejo podatki. Dostop imate do posameznih projektov, ne do celotne aplikacije.',
    ctxOtherProjectsTitle: 'Drugi projekti v tej aplikaciji',
    ctxNoAccessNote: 'Do teh projektov nimate dostopa.',
    ctxUnnamed: '(brez imena)',
    ctxLastOpened: 'nazadnje odprto',
    ctxProjectChoiceAria: 'Izbira projekta',
    ctxDeniedAppNote: 'Do aplikacije »{app}« nimate dostopa. Spodaj so aplikacije, ki so vam na voljo.',

    // App panel (Aplikacija)
    appPanelPublicPill: 'Javno',
    appPanelExplore: 'Raziskuj',
    appPanelFindParcel: 'Poišči parcelo',
    appPanelFindAddress: 'Poišči naslov',
    appPanelOrgQuestion: 'Ste del organizacije?',
    appPanelLoginBody: 'Prijavite se in odprite svoje aplikacije, projekte in podatke svojega omrežja.',
    appPanelNetwork: 'Omrežje',
    appPanelNetworkProject: 'Omrežje projekta',
    appPanelRecent: 'Zadnje dogajanje',
    appPanelAll: 'Vse →',
    appPanelDashboard: 'Nadzorna plošča',
    appPanelMyApps: 'Moje aplikacije',
    appPanelProject: 'Projekt',
    appPanelNow: 'zdaj',
    appPanelMinAgo: 'pred {n} min',
    appPanelHoursAgo: 'pred {n} h',
    appPanelYesterday: 'včeraj',
    appPanelDaysAgo: 'pred {n} dnevi',
    appPanelProjectsOne: 'projekt',
    appPanelProjectsTwo: 'projekta',
    appPanelProjectsFew: 'projekti',
    appPanelProjectsMany: 'projektov',
    appPanelActivityCount: 'aktivnosti',
    appPanelLastActivity: 'zadnja aktivnost',
    appPanelPostFallback: 'Objava',
    appPanelReopened: 'Ponovno odprto',
    // Activity vocabulary shared by the app panel pulse and the post card
    activityNew: 'Novo',
    activityEdited: 'Posodobljeno',
    activityCommented: 'Komentar',
    activityResolved: 'Razrešeno',
    activityUnresolved: 'Nerazrešeno',
    activityAssigned: 'Dodeljeno',
    activityReacted: 'Odziv',
    postTypeNote: 'Zapis',
    postTypeIssue: 'Opravilo',
    postTypeInspection: 'Pregled',
    postStatusOpen: 'Odprto',
    postNoTitle: 'brez naslova',
    postPhotoUnit: 'foto',

    // Layers panel + layer controls leftovers
    layersPanelShowAll: 'Prikaži vse',
    layersPanelGjiKinds: 'Vrste GJI',
    layerControlsEmpty: 'Ni slojev za prikaz',

    // Feature popup leftovers
    popupFooterLayer: 'Sloj',
    popupFeatureFallback: 'Objekt {index}',
    popupFeatureTitleFallback: 'Objekt',

    // Measure tool
    measureUndoAria: 'Odstrani zadnjo točko meritve',
    measureUndoTitle: 'Odstrani zadnjo točko (ali desni klik na karto)',

    // Locate me — was English
    locateUnsupported: 'Vaš brskalnik ne podpira določanja lokacije.',
    locateGeneric: 'Vaše lokacije ni bilo mogoče pridobiti.',
    locatePermissionDenied: 'Dostop do lokacije je zavrnjen. Omogočite ga v nastavitvah brskalnika.',
    locateUnavailable: 'Podatki o lokaciji niso na voljo.',
    locateTimeout: 'Določanje lokacije je poteklo. Poskusite znova.',
    locateTimeoutSearching: 'Določanje lokacije je poteklo. Iščem naprej…',
    locateUnavailableSearching: 'Podatki o lokaciji niso na voljo. Iščem naprej…',
    // ── Site pages (#597 pass 4: auth titles + refusals, logout, dashboard,
    // profile, commits, access pages, invitation). sl values are the former
    // literals verbatim; pages that were English got Slovenian copy.
    // Page titles — "<title> - King.Prostor"; the access/invite pages compose theirs
    pageTitleLogin: 'Prijava',
    pageTitleRegister: 'Registracija',
    pageTitleVerifyEmail: 'Potrditev e-pošte',
    pageTitleForgotPassword: 'Pozabljeno geslo',
    pageTitleResetPassword: 'Ponastavitev gesla',
    pageTitleLoginLink: 'Prijava',
    pageTitleDeviceLogin: 'Prijava naprave',
    pageTitleLogout: 'Odjava',
    pageTitleDashboard: 'Nadzorna plošča',
    pageTitleProfile: 'Profil',
    pageTitleCommits: '{name} · zgodovina sprememb',
    pageTitleRequestAccess: 'Zahteva za dostop - {name}',
    pageTitleLimitedAccess: 'Omejen dostop',
    pageTitleLimitedAccessFor: 'Omejen dostop - {name}',
    pageTitleNoAccess: 'Ni dostopa - {name}',
    pageTitleInvite: 'Vabilo · {name}',
    pageTitleInvitePlain: 'Vabilo',
    // Auth refusals — mapped from status + code, the server string is never shown
    authErrInvalidCredentials: 'Napačen e-naslov ali geslo.',
    authErrAccountDisabled: 'Račun je onemogočen.',
    authErrTooMany: 'Preveč poskusov prijave. Poskusite znova čez {seconds} s.',
    authErrWeakPassword: 'Geslo mora imeti vsaj 8 znakov, veliko in malo črko ter številko.',
    authErrEmailTaken: 'Račun s tem e-naslovom že obstaja.',

    // Logout page (was English)
    logoutLoading: 'Nalaganje…',
    logoutTitle: 'Odjava',
    logoutButton: 'Odjavi se',
    logoutWorking: 'Odjavljam…',
    logoutCancel: 'Prekliči',
    logoutGoToLogin: 'Na prijavo',
    logoutNotSignedIn: 'Niste prijavljeni.',
    logoutLoadFailed: 'Podatkov o uporabniku ni bilo mogoče naložiti.',

    // Relative time (utils/formatRelativeTime.ts) — one / two / three-plus
    relTimeNow: 'ravnokar',
    relTimeYesterday: 'včeraj',
    relTimeSecondsOne: 'pred {n} sekundo',
    relTimeSecondsTwo: 'pred {n} sekundama',
    relTimeSecondsMany: 'pred {n} sekundami',
    relTimeMinutesOne: 'pred {n} minuto',
    relTimeMinutesTwo: 'pred {n} minutama',
    relTimeMinutesMany: 'pred {n} minutami',
    relTimeHoursOne: 'pred {n} uro',
    relTimeHoursTwo: 'pred {n} urama',
    relTimeHoursMany: 'pred {n} urami',
    relTimeDaysOne: 'pred {n} dnem',
    relTimeDaysTwo: 'pred {n} dnevoma',
    relTimeDaysMany: 'pred {n} dnemi',
    relTimeWeeksOne: 'pred {n} tednom',
    relTimeWeeksTwo: 'pred {n} tednoma',
    relTimeWeeksMany: 'pred {n} tedni',
    relTimeMonthsOne: 'pred {n} mesecem',
    relTimeMonthsTwo: 'pred {n} mesecema',
    relTimeMonthsMany: 'pred {n} meseci',
    relTimeYearsOne: 'pred {n} letom',
    relTimeYearsTwo: 'pred {n} letoma',
    relTimeYearsMany: 'pred {n} leti',

    // Dashboard (/d): home, app and project views
    dashWelcome: 'Dobrodošel, {name}',
    // Dashboard: public map page + »Nadaljujte« (docs/plans/kingprostor-home/)
    dashContinue: 'Nadaljujte',
    dashPublicMap: 'Javni zemljevid',
    dashPublicMapBody: 'Parcele, stavbe, naslovi in prostorski podatki za vso državo.',
    dashOpenPublicMap: 'Odpri zemljevid',
    dashLayerDocs: 'Dokumentacija slojev',
    dashCountrySlovenia: 'Slovenija',
    dashCountryCzechia: 'Češka',
    dashMapStatLayers: 'Slojev na zemljevidu',
    dashMapStatThemes: 'Tem',
    dashMapStatPublishers: 'Izdajateljev',
    dashMapOnMap: 'Kaj je na zemljevidu',
    dashMapCatalogue: 'Katalog slojev',
    dashMapShowAllThemes: 'Prikaži vse teme ({n})',
    dashMapColData: 'Podatki',
    dashMapColSource: 'Datum vira',
    dashMapSources: 'Viri in licence',
    dashMapPublishers: 'Izdajatelji',
    dashMapStatParcels: 'Parcele z dne',
    dashMapCountsNote: 'Šteti so sloji, ki jih prikazuje ta zemljevid. Dokumentacija slojev obsega tudi sloje za KingCAD in Češko.',
    dashMapWeekly: 'Tedensko osveženi podatki',
    dashMapColOnMap: 'Na zemljevidu',
    dashMapAdmin: 'Skrbnik',
    dashMapSummaryFailed: 'Podatkov o slojih trenutno ni mogoče prikazati.',
    dashAppsCount: '{n} aplikacij',
    dashProjectsCount: '{n} projektov',
    dashRecentlyVisited: 'Nedavno obiskano',
    dashOpenMap: 'Odpri mapo',
    dashShowLess: 'Prikaži manj',
    dashShowMore: 'Prikaži več',
    dashShowMoreN: 'Prikaži več ({n})',
    dashShowAllN: 'Prikaži vse ({n})',
    dashShowAllMore: 'Prikaži vse ({n} več)',
    dashApps: 'Aplikacije',
    dashSearchApps: 'Iskanje aplikacij...',
    dashProjAbbrev: '{n} proj',
    dashRecentActivity: 'Nedavna aktivnost',
    dashAllApps: 'Vse aplikacije',
    dashNoRecentActivity: 'Ni nedavnih aktivnosti.',
    dashHome: 'Domov',
    dashMap: 'Mapa',
    dashSettings: 'Nastavitve',
    dashSettingsAdminTitle: 'Nastavitve aplikacije (skrbnik)',
    dashReports: 'Poročila',
    dashSluznosti: 'Služnosti',
    dashSluznostiTitle: 'Register služnosti (parcele, lastniki, pogodbe)',
    dashReportsWriteTitle: 'Poročila (dostop za pisanje)',
    dashProjectSettingsTitle: 'Nastavitve projekta (skrbnik)',
    dashWorks: 'Graditve',
    dashWorksNew: 'Nova graditev',
    dashWorksNewTitle: 'Nova graditev — vpišete ime, ostalo dodate po korakih',
    dashWorksEmpty: 'Ni graditev. Začnite z »Nova graditev« — vpišete ime, ostalo dodate po korakih.',
    dashWorksOpinions: 'Mnenja {done}/{total}',
    dashParcelsCount: '{n} parcel',
    dashProjects: 'Projekti',
    dashNewProject: 'Nov projekt',
    dashSearchProjects: 'Iskanje projektov...',
    dashNoProjects: 'Ni projektov.',
    dashUpdated: 'Posodobljeno {when}',
    dashProjectName: 'Ime projekta',
    dashProjectNamePlaceholder: 'npr. Novo naselje Polje',
    dashProjectKind: 'Vrsta projekta',
    dashDescription: 'Opis',
    dashOptional: '(neobvezno)',
    dashDescriptionPlaceholder: 'Kratek opis projekta',
    dashCancel: 'Prekliči',
    dashCreateProject: 'Ustvari projekt',
    dashCreating: 'Ustvarjam...',
    dashActivity: 'Aktivnost',
    dashFilterAll: 'Vse',
    dashFilterTasks: 'Opravila',
    dashFilterPhotos: 'Slike',
    dashFilterComments: 'Komentarji',
    dashFilterOpinions: 'Mnenja',
    dashLatestChanges: 'Zadnje spremembe',
    dashAllChanges: 'Vse spremembe',
    dashNoEdits30: 'Ni urejanj v zadnjih 30 dneh',
    dashEditProject: 'Uredi projekt',
    dashDrawOnMap: 'Prikaži projekt na skupni mapi aplikacije',
    dashDrawOnMapHint:
      'Če je izklopljeno, se projekt ne riše na skupni mapi z vsemi projekti. Še vedno je na seznamu, odpreš ga lahko posamično, v namizni aplikaciji (KingCAD) pa ostane nespremenjen.',
    dashSave: 'Shrani',
    dashSaving: 'Shranjujem...',
    dashShowOnMap: 'Prikaži na zemljevidu',
    dashElaboratTitle: 'Objekti za GJI elaborat',
    dashElaboratSubtitle:
      'V eksploataciji brez GURS identifikatorja, lastnik GVO (po šifrantu) ali brez lastnika · skupaj {n}',
    dashElaboratAllOk: 'Vsi objekti v eksploataciji, ki jih vpisuje GVO, imajo GURS identifikator',
    dashReportLink: 'Poročilo →',
    dashOpinionsTitle: 'Mnenja / Soglasja',
    dashEditing: 'Urejanje →',
    dashNoOpinions: 'Ni mnenj za ta projekt.',
    dashDocsAbbrev: '{n} dok.',
    dashNoDocs: 'Ni dokumentov.',
    dashTagTask: 'opravilo',
    dashTagNote: 'zapis',
    dashTagInspection: 'pregled',
    dashUsersOne: '{n} uporabnik',
    dashUsersTwo: '{n} uporabnika',
    dashUsersFew: '{n} uporabniki',
    dashUsersMany: '{n} uporabnikov',
    dashNever: 'nikoli',
    dashUnknownUser: 'Neznani uporabnik',
    dashUnknownUserAlt: 'Neznan uporabnik',
    dashSectionToday: 'Danes',
    dashSectionWeek: 'Zadnjih 7 dni',
    dashSectionMonth: 'Zadnjih 30 dni',
    dashChangesOne: '{n} sprememba',
    dashChangesTwo: '{n} spremembi',
    dashChangesFew: '{n} spremembe',
    dashChangesMany: '{n} sprememb',
    dashCommitOne: '{n} commit',
    dashCommitMany: '{n} commits',
    dashAndOtherOne: ' in še {n}',
    dashAndOthers: ' in še {n}',
    dashActiveProjectsZero: '0 projektov aktivnih v zadnjih 30 dneh',
    dashActiveProjectsOne: '1 projekt aktiven v zadnjih 30 dneh',
    dashActiveProjectsTwo: '2 projekta aktivna v zadnjih 30 dneh',
    dashActiveProjectsFew: '{n} projekti aktivni v zadnjih 30 dneh',
    dashActiveProjectsMany: '{n} projektov aktivnih v zadnjih 30 dneh',
    dashOpinionsSummary: '{yes} od {total} pridobljenih',
    dashStageConditionsRequest: 'Vloga za pogoje',
    dashStageConditions: 'Pogoji',
    dashStageOpinionRequest: 'Vloga za mnenje',
    dashStageOpinion: 'Mnenje',
    dashStatusObtained: 'Pridobljeno',
    dashStatusRejected: 'Zavrnjeno',
    dashStatusPending: 'V obdelavi',
    dashRequestFailed: 'Zahteva ni uspela.',
    dashErrorStatus: 'Napaka ({status})',

    // Profile
    profileEditName: 'Uredi ime',
    profileFirstName: 'Ime',
    profileLastName: 'Priimek',
    profileMemberSince: 'Član od {date}',
    profileAppsOne: '{n} aplikacija',
    profileAppsTwo: '{n} aplikaciji',
    profileAppsFew: '{n} aplikacije',
    profileAppsMany: '{n} aplikacij',
    profileAdminPanel: 'Skrbniška plošča',
    profileDesktopPasswordTitle: 'Geslo za starejše različice programa',
    profileDesktopPasswordBody: 'Za prijavo ga ne potrebujete. Nastavite ga samo, če uporabljate starejšo različico programa (pred 27.2).',
    profilePasswordChangeSignsOut: 'Po nastavitvi ali spremembi gesla boste na vseh napravah odjavljeni in se boste morali znova prijaviti.',
    profileSendPasswordLink: 'Pošlji povezavo za nastavitev gesla',
    profileSendPasswordChangeLink: 'Pošlji povezavo za spremembo gesla',
    profileSending: 'Pošiljanje ...',
    profilePasswordLinkSent: 'Povezavo smo poslali na {email}. Odprite sporočilo in sledite navodilom.',
    // #699: device code, program, last used (date). No computer or Windows user name.
    profileDevices: 'Moje naprave',
    profileDevicesOne: '{n} naprava',
    profileDevicesTwo: '{n} napravi',
    profileDevicesFew: '{n} naprave',
    profileDevicesMany: '{n} naprav',
    profileNoDevices: 'Noben računalnik s KingCAD trenutno ni prijavljen s tem računom.',
    profileDeviceLabel: 'Naprava {code}',
    profileLastUsed: 'nazadnje',
    profileDevicesError: 'Seznama naprav ni bilo mogoče naložiti.',
    profileSignOutAll: 'Odjavi me povsod',
    profileSignOutAllConfirm:
      'Vse naprave bodo odjavljene; na vsaki se boste morali znova prijaviti prek brskalnika. Nadaljujem?',
    profileSignOutAllYes: 'Da, odjavi povsod',
    profileSigningOut: 'Odjavljam …',
    profileSignedOutAll: 'Vse naprave so odjavljene.',
    profileSignOutAllNeedsBrowser: 'Za to dejanje se prijavite v brskalniku (geslo, Google ali povezava po e-pošti).',
    // #791 the device list: name + last used, »Odstrani« per device
    profileDevicesIntro: 'Računalniki s KingCAD, prijavljeni s tem računom. Odjava računalnika ga ne odstrani s seznama — to naredi »Odstrani«.',
    profileDevicesPrivacyLink: 'Katere podatke beležimo in kako dolgo',
    profileDevicesLimit: 'Uporabljate {count} od {cap} naprav.',
    profileDeviceRemove: 'Odstrani',
    profileDeviceRemoveConfirm: 'Napravo {name} odstranite s seznama? Na njej se boste morali znova prijaviti, in to šele po čakalni dobi.',
    profileDeviceRemoveYes: 'Da, odstrani',
    profileDeviceRemoving: 'Odstranjujem …',
    profileDeviceRemoved: 'Naprava {name} je odstranjena.',
    profileDeviceRemoveCapped: 'Dovoljeno število odstranitev naprav v tem mesecu je doseženo.',
    profileDeviceRemoveFailed: 'Naprave ni bilo mogoče odstraniti. Poskusite znova.',
    profileNoApps: 'Nimate dostopa do nobene aplikacije',
    profileConnectionError: 'Napaka pri povezavi. Poskusite znova.',
    profileTooMany: 'Preveč zahtev. Počakajte minuto in poskusite znova.',
    profileSendFailed: 'Pošiljanje ni uspelo. Poskusite znova.',
    profileSaveFailed: 'Napaka pri shranjevanju.',
    profileConnectionFailed: 'Napaka pri povezavi.',
    profileLoadAppsFailed: 'Aplikacij ni bilo mogoče naložiti.',
    profileDateUnknown: '–',

    // Commits (/commits) — was English
    commitsCrumb: 'Zgodovina sprememb',
    commitsSearchUsers: 'Iskanje uporabnikov...',
    commitsOn: 'Spremembe {date}',
    commitsInserted: '+{n} vstavljenih',
    commitsUpdated: '~{n} posodobljenih',
    commitsDeleted: '-{n} izbrisanih',
    commitsNoChanges: 'Brez sprememb',
    commitsPageInfo: 'Stran {page} od {pages} · Skupaj {total} commits',
    commitsContributors: 'Prispevali',
    commitsMissingParam: 'Manjka parameter sxapp ali sxproject',
    commitsAppInfoError: 'Napaka pri nalaganju informacij o aplikaciji',
    commitsProjectNotFound: 'Projekt ni najden',
    commitsProjectInfoError: 'Napaka pri nalaganju informacij o projektu',
    commitsLoadError: 'Napaka pri nalaganju zgodovine commitov',
    commitsDateTime: '{date} ob {time}',
    commitsAllUsers: 'Vsi uporabniki',
    commitsAllDates: 'Vsi datumi',
    commitsWeekdays: 'Po,To,Sr,Če,Pe,So,Ne',
    commitsMonths: 'Januar,Februar,Marec,April,Maj,Junij,Julij,Avgust,September,Oktober,November,December',

    // Access pages (app / project request, limited, org) + invitation
    accessRequestTitle: 'Zahteva za dostop',
    accessNoAccessTo: 'Nimate dostopa do:',
    accessMessageLabel: 'Sporočilo administratorju',
    accessMessagePlaceholder: 'Na kratko napišite, kdo ste in zakaj potrebujete dostop.',
    accessSend: 'Pošlji zahtevo',
    accessSending: 'Pošiljanje...',
    accessSentTitle: 'Zahteva poslana',
    accessSentBody: 'Administrator bo pregledal vašo zahtevo in vas obvestil po e-pošti.',
    accessAlreadyRequested: 'Zahteva za dostop je že bila poslana.',
    accessSubmitFailed: 'Napaka pri pošiljanju zahteve.',
    accessLimitedTitle: 'Omejen dostop',
    accessLimitedBody: 'Nimate dostopa do celotne aplikacije:',
    accessLoading: 'Nalaganje...',
    accessYouHave: 'Imate dostop do {projects}:',
    accessNoProjects: 'Nimate dostopa do nobenega projekta v tej aplikaciji.',
    accessAppMissing: 'Aplikacija ne obstaja.',
    accessProjectsLoadError: 'Napaka pri nalaganju projektov.',
    accessProjectsZero: 'noben projekt',
    accessContactAdmin: 'Za dostop kontaktirajte administratorja:',
    accessContactOrgAdmin: 'Za dostop kontaktirajte administratorja organizacije.',
    accessCheckFailed: 'Dostopa ni bilo mogoče preveriti. Kontaktirajte podporo.',
    accessRequestFull: 'Zahtevaj poln dostop',
    // Map leftovers found by the site sweep
    popupNoProperties: 'Ni lastnosti',
    popupErrorSessionTitle: 'Prijava ni veljavna',
    popupErrorSessionMessage: 'Prijavite se znova. Če težava ostane, kontaktirajte podporo.',
    popupNoData: 'Ni podatka',
    commonYes: 'Da',
    commonNo: 'Ne',
    measureUndoLabel: 'Korak nazaj',
    inviteNotFound: 'Vabilo ni najdeno',
    inviteNotFoundBody: 'Povezava do vabila je morda napačna ali pa je bilo vabilo izbrisano.',
    inviteNoLongerValid: 'Vabilo ni več veljavno',
    inviteAccepted: 'To vabilo je bilo že sprejeto.',
    inviteCancelled: 'To vabilo je bilo preklicano.',
    inviteExpired: 'To vabilo je poteklo.',
    inviteAskNew: 'Prosite skrbnika, da pošlje novo vabilo.',
    inviteTitle: 'Vabilo v aplikacijo',
    inviteOrg: 'Organizacija: {org}',
    inviteFrom: 'Vabi vas:',
    inviteForEmail: 'Za e-pošto:',
    inviteValidUntil: 'Veljavno do:',
    inviteSignInOrRegister: 'Za sprejem vabila ustvarite račun s tem e-naslovom. Gesla ne potrebujete. Če račun že imate, se prijavite.',
    inviteCreateAccount: 'Ustvari račun',
    inviteSignedInAs: 'Prijavljeni ste kot {email}. Pritisnite gumb za sprejem vabila.',
    inviteAccept: 'Sprejmi vabilo',
    inviteAccepting: 'Sprejemam…',
    inviteWrongAccount:
      'Prijavljeni ste kot {current}, vabilo pa je za {invited}. Odjavite se in se prijavite z naslovom, na katerega je bilo poslano vabilo.',
    inviteSignOut: 'Odjavi se',
    inviteErr401: 'Prijava ni veljavna ali računa ni mogoče uporabiti.',
    inviteErr403: 'To vabilo je izdano za drug e-naslov.',
    inviteErr404: 'Vabila ni mogoče najti.',
    inviteErr409: 'To vabilo ni več veljavno.',
    inviteErrGeneric: 'Vabila ni bilo mogoče sprejeti. Poskusite znova ali obvestite skrbnika.',
    inviteInvalidPath: 'Neveljavna pot.',
    inviteLoadFailed: 'Napaka pri nalaganju vabila.',
  },

  en: {
    // Auth page - Tabs
    login: 'Login',
    register: 'Register',

    // Auth page - Form fields
    email: 'Email',
    password: 'Password',
    firstName: 'First Name',
    lastName: 'Last Name',

    // Auth page - Placeholders
    emailPlaceholder: 'your.email@example.com',
    passwordPlaceholder: 'Enter your password',
    firstNamePlaceholder: 'John',
    lastNamePlaceholder: 'Doe',

    // Auth page - Buttons
    loginButton: 'Log In',
    createAccount: 'Create Account',
    creatingAccount: 'Creating account...',
    forgotPassword: 'Forgot password?',
    orContinueWith: 'or continue with',

    // OAuth buttons
    continueWithGoogle: 'Continue with Google',
    continueWithMicrosoft: 'Continue with Microsoft',

    // Email verification status
    // #569 desktop browser login + email login link
    deviceLoginTitle: 'Sign in',
    deviceLoginTitleProduct: 'Sign in to {product}',
    deviceLoginLoading: 'Loading the sign-in request…',
    deviceLoginSignedInAs: 'You are signed in as',
    deviceLoginQuestion: 'Allow {product} to sign in on this device?',
    deviceLoginMachine: 'Device',
    deviceLoginWindowsUser: 'Windows user',
    deviceLoginProgram: 'Program',
    deviceLoginRemote: 'remote session',
    deviceLoginExpiresIn: 'The request expires in {min} min.',
    deviceLoginAllow: 'Allow',
    deviceLoginApproving: 'Confirming…',
    deviceLoginNotYou: 'Not you? Sign in with another account',
    // #667 G16/G17
    deviceLoginSwitchAccount: 'Sign in with another account',
    deviceLoginApprovedWaiting: 'Approved. Waiting for the program…',
    deviceLoginNotCollected: 'The program did not pick up the sign-in. Press the button in the program again and leave the window open.',
    deviceLoginHintFresh: 'Tip: if you sign in with Google or by email, you do not need this code.',
    deviceLoginCancel: 'Cancel',
    deviceLoginDone: 'Done — return to {product}.',
    deviceLoginDoneClose: 'You can close this tab.',
    deviceLoginExpired: 'The request has expired. Click "Sign in with browser" in the program again.',
    deviceLoginDenied: 'The sign-in was cancelled.',
    deviceLoginConsumed: 'This request has already been used.',
    deviceLoginNotFound: 'Unknown sign-in request.',
    deviceLoginError: 'Something went wrong. Please try again.',
    deviceLoginBackHome: 'Back to home',
    // #580 mailbox gate
    deviceConfirmNeeded: 'To add a new device, confirm access to the account mailbox.',
    deviceConfirmSend: 'Send confirmation code',
    deviceConfirmSending: 'Sending…',
    deviceConfirmSentTo: 'Code sent to {email}. Check your mail (spam folder too).',
    deviceConfirmCodeLabel: 'Enter the 6-digit code from the email',
    deviceConfirmSubmit: 'Confirm code',
    deviceConfirmChecking: 'Checking…',
    deviceConfirmResend: 'Send again',
    deviceConfirmExpiresIn: 'The code expires in {min} min.',
    deviceConfirmWrong: 'Wrong code. Try again.',
    deviceConfirmTooMany: 'Too many wrong attempts. Use the link from the email, or start a new sign-in from the program.',
    deviceConfirmExpiredCode: 'The code has expired. Send a new one.',
    deviceConfirmCooldown: 'Please wait a moment before resending.',
    deviceConfirmCapped: 'The send limit was reached. Try later, or sign in with Google or by email.',
    deviceConfirmSendFailed: 'Sending the email failed. Please try again.',
    deviceConfirmProven: 'Mailbox confirmed — you can allow the sign-in.',
    deviceLinkQuestion: 'Allow {product} to sign in for account {email}?',
    deviceLinkConfirm: 'Confirm device',
    deviceLinkClose: 'Close',
    deviceLinkInvalid: 'This link is invalid or has expired. Request a new sign-in from the program.',
    // #791 the device list on the approval page (block mode only)
    deviceLimitTitle: 'The device list is full',
    deviceLimitBody: '{count} devices are already signed in with this account (at most {cap}). Remove one to allow the sign-in on this one.',
    deviceLimitCooldown: 'You removed this device recently. It can be added again on {date}.',
    deviceLimitRemove: 'Remove',
    deviceLimitRemoving: 'Removing…',
    deviceLimitRemoved: 'The device has been removed.',
    deviceLimitFreed: 'A slot is free — you can allow the sign-in.',
    deviceLimitRemoveFailed: 'The device could not be removed. Try again.',
    deviceLimitRefreshFailed: 'The device has been removed, but the state could not be refreshed. Try again.',
    deviceLimitRetry: 'Try again',
    deviceLimitRemoveCapped: 'The number of device removals allowed this month has been reached.',
    deviceLimitDeviceLine: '{name} · last used {date}',
    deviceLinkLimited: 'The maximum number of devices is already signed in with this account. Sign in through the browser and remove one device; this sign-in will then be possible.',
    deviceLinkLimitedCooldown: 'You removed this device recently; it can be added again on {date}.',
    deviceLinkManage: 'Sign in and manage devices',
    deviceSlowdownTitle: 'Frequent sign-ins on different computers',
    deviceSlowdownCode: 'Because of frequent sign-ins on different computers, the code will be sent at {time}.',
    deviceSlowdownApprove: 'Because of frequent sign-ins on different computers, the sign-in will be possible at {time}.',
    deviceSlowdownExpires: 'This sign-in request will have expired by then — start it again in the program at that time.',
    deviceSlowdownNow: 'You can continue now.',
    deviceLinkSlowdown: 'Because of frequent sign-ins on different computers, the sign-in will be possible at {time}. If this request expires in the meantime, start it again in the program.',
    loginLinkRedeeming: 'Signing you in…',
    loginLinkSuccess: 'You are signed in',
    loginLinkContinue: 'Continue',
    loginLinkFailed: 'This link is invalid or has expired',
    loginLinkFailedHint: 'A link is valid for 15 minutes and can be used only once.',
    loginLinkRequestNew: 'Back to sign in',
    loginLinkOption: 'Send me a sign-in code',
    loginLinkSending: 'Sending…',
    loginLinkSent: 'If the account exists, we sent a code to {email}. Check your inbox.',
    loginLinkNeedEmail: 'Enter your email address first.',
    loginLinkError: 'Sending failed. Please try again.',
    loginLinkSentCode: 'Enter the code from the email here, or click the link in it.',
    loginLinkCodeLabel: 'Code from the email',
    loginLinkCodeSubmit: 'Confirm code',
    loginLinkCodeChecking: 'Checking…',
    loginLinkCodeWrong: 'That code is not right.',
    loginLinkCodeExpired: 'The code has expired. Send yourself a new one.',
    loginLinkCodeUsed: 'This code is no longer valid: it was used (for example through the link in another browser) or replaced by a newer one. Send yourself a new one.',
    loginLinkCodeRefused: 'This account cannot sign in this way.',
    loginLinkCodeTooMany: 'Too many attempts. Wait a minute and try again.',
    loginLinkResend: 'Send again',
    loginLinkResendIn: 'Send again in {min} min',
    loginLinkResendInS: 'Send again in {s} s',
    // #668: mail-first login page, newest-code resend
    loginNoPasswordNeeded: 'You do not need a password to sign in.',
    loginWithPasswordToggle: 'Have a password? Sign in with a password',
    loginLinkResent: 'We sent a new code. Only the newest one is valid.',
    loginLinkResendUnsure: 'The connection to the server was interrupted. Send the code again. If a new email has arrived in the meantime, use the link in it.',
    verifyingEmail: 'Verifying your email...',
    emailVerified: 'Email Verified!',
    emailVerifiedSuccess: 'Your email has been successfully verified. You can now log in.',
    verificationFailed: 'Verification Failed',
    verificationFailedMessage: 'The verification link is invalid or has expired.',
    requestNewLink: 'Request a new verification link',

    // Verify email page
    continueToMap: 'Continue to Map',
    backToLogin: 'Back to Login',
    verificationLinkInvalid: 'The verification link is invalid or has expired.',
    enterEmailForNewLink: 'Enter your email to receive a new verification link:',
    resendVerificationEmail: 'Resend Verification Email',
    sending: 'Sending...',
    verificationEmailSent: '✓ Verification email sent! Please check your inbox.',

    // Verify email page — pending activation (#448)
    pendingTitle: 'Check your inbox',
    pendingSentTo: 'We sent a verification link to',
    pendingExpiry: 'The link is valid for 24 hours. Your account activates when you click it.',
    pendingWrongAddress: 'Wrong email address?',
    pendingRegisterAgain: 'Register again with the correct address.',
    sendFailedTitle: 'The email could not be sent',
    sendFailedInfo: 'The verification email could not be sent to',
    sendFailedRetry: 'Try sending it again below.',
    continueToLogin: 'Continue to Login',
    resendAlreadyVerified: 'This email address is already verified — you can sign in.',
    resendInvalidEmail: 'That email address is not valid. Please check it and try again.',
    resendTooMany: 'Too many attempts. Please wait a few minutes and try again.',
    emailNotVerifiedError: 'Your email address has not been verified yet. Your account activates when you click the link in the verification email.',
    verifyEmailCta: 'Verify your email',
    resendNotReceived: "Didn't receive the verification email?",
    oauthNoEmail: 'Your Microsoft account has no email address. Create an account with an email address.',
    emailAlreadyRegistered: 'An account with this email already exists.',
    conflictSignIn: 'Sign in',
    conflictOr: 'or',
    conflictReset: 'reset your password',
    conflictResetNote: 'Resetting the password also verifies your email address.',

    // Password strength
    passwordStrength: 'Password Strength',
    passwordTooWeak: 'Too weak',
    passwordWeak: 'Weak - Add',
    passwordFair: 'Fair - Add',
    passwordGood: 'Good - Password meets requirements',
    passwordStrong: 'Strong - Excellent password!',
    atLeast8Chars: 'at least 8 characters',
    anUppercase: 'an uppercase letter',
    aLowercase: 'a lowercase letter',
    aNumber: 'a number',

    // User menu
    logout: 'Logout',
    loggingOut: 'Logging out...',
    emailAccount: 'Email Account',
    googleAccount: 'Google Account',
    microsoftAccount: 'Microsoft Account',
    legacyAccount: 'Legacy Account',

    // Errors
    loginFailed: 'Login failed. Please check your credentials.',
    registrationFailed: 'Registration failed. Please try again.',
    // #675: signup without a password, the name step, the KingCAD done page
    registerSubmit: 'Create account and send code',
    registerNoPasswordNote: 'No password needed. You will get a sign-in code by email.',
    registerCodeSent: 'We sent a code to {email}. Check your inbox.',
    registerFixAddress: 'Wrong email address?',
    registerFixAddressLink: 'Fix it',
    registerNameRequired: 'Enter your first and last name.',
    registerDoneTitle: 'Your account is ready',
    registerDoneBody: 'Return to {product} and click »Sign in in the browser«.',
    registerDoneOldBuild: 'If your version of the program has no such button, set a password in your profile.',
    registerDoneProfileLink: 'Open profile',
    welcomeTitle: 'What should we call you?',
    welcomeIntro: 'Colleagues see your name on tasks, comments and licences.',
    welcomeSave: 'Save and continue',
    welcomeSkip: 'Skip',
    welcomeSaveFailed: 'Saving failed. Please try again.',
    oauthEmailConflict: 'An account with this email already exists. Please sign in with your password or the method you have used before.',
    emailVerificationFailed: 'Failed to verify email. Please try again or request a new verification link.',
    networkError: 'Could not connect to the server. Please check your internet connection.',
    serverUnavailable: 'Server is currently unavailable. Please try again in a few moments.',

    // Auth pages — desktop-client mode + inline copy (kingcad#426)
    optional: '(optional)',
    desktopRegisterHint: 'You will use this account to sign in to {product}.',
    desktopVerifiedReturnHint: 'Your account is active — return to {product} and sign in.',
    alreadyHaveAccount: 'Already have an account?',
    signInLink: 'Sign in',
    noAccountYet: "Don't have an account?",
    createAccountLink: 'Create one',

    // Forgot password page
    forgotPasswordTitle: 'Forgot Password?',
    forgotPasswordIntro: "Enter your email address and we'll send you a link to reset your password.",
    sendResetLink: 'Send Reset Link',
    forgotPasswordFailed: 'The reset link could not be sent.',
    forgotCooldownNotice: 'Please wait {seconds} s before requesting another reset link.',
    checkYourEmail: 'Check Your Email',
    resetLinkSent: "If an account exists with that email address, we've sent you a password reset link. Please check your inbox.",
    genericError: 'An error occurred. Please try again.',

    // Reset password page
    resetPasswordTitle: 'Reset Password',
    resetPasswordIntro: 'Enter your new password below.',
    newPassword: 'New Password',
    newPasswordPlaceholder: 'Enter new password',
    confirmNewPassword: 'Confirm New Password',
    confirmNewPasswordPlaceholder: 'Confirm new password',
    resetPasswordButton: 'Reset Password',
    resetting: 'Resetting...',
    passwordResetTitle: 'Password Reset!',
    passwordResetSuccess: 'Your password has been successfully reset. You can now log in with your new password.',
    invalidLinkTitle: 'Invalid or Expired Link',
    invalidLinkMessage: 'This password reset link is invalid or has expired. Please request a new one.',
    requestNewResetLink: 'Request New Reset Link',
    passwordsDoNotMatch: 'Passwords do not match.',
    passwordMinLength: 'Password must be at least 8 characters.',
    passwordNeedsUppercase: 'Password must contain at least one uppercase letter.',
    passwordNeedsLowercase: 'Password must contain at least one lowercase letter.',
    passwordNeedsNumber: 'Password must contain at least one number.',
    resetPasswordFailed: 'The password could not be reset.',
    strengthWeak: 'Weak',
    strengthFair: 'Fair',
    strengthGood: 'Good',
    strengthStrong: 'Strong',

    // Map toolbar
    contours: 'Contours',
    zoomInToView: 'Zoom in to view',
    zoomOutToView: 'Zoom out to view',
    zoomInForAll: 'Main routes only, zoom in for all',
    gjiNoKindSelected: 'No GJI kind selected',
    layerKindRaster: 'raster',
    layerKindVector: 'vector',
    layerOpacity: 'Opacity',
    layersOnCount: 'On',
    view25d: 'View 2.5D',

    // Right controls
    rightControlsPitch: 'Pitch',
    rightControlsContours: 'Contours',
    rightControlsHillshade: 'Hillshade',
    rightControlsTerrain: '2.5D View',
    rightControlsBuildings3d: '2.5D Buildings',
    rightControlsTitle: 'Map Controls',

    // Toolbar
    toolbarParcelValue: 'Plot Value',
    toolbarAdminStatus: 'Admin Status',
    toolbarConnections: 'Connections',
    toolbarYearOfConstruction: 'Year of Construction',

    // Basemap
    basemapRaster: 'Orthophoto',
    basemapCuzkOrtofoto: 'ČÚZK orthophoto',
    basemapMore: 'More',
    basemapStyleTitle: 'Basemap Style',

    // SearchBar
    searchPlaceholder: 'Search map',

    // FeatureCard
    toastCopied: 'Copied to Clipboard!',
    toastShowingDistance: 'Showing distance!',

    // Print dialog (#611: the title is pre-filled with the nearest address)
    printTitle: 'Print title',
    printDescription: 'The nearest address is filled in – edit it as you like.',
    printDescriptionSearching: 'Looking up the nearest address …',
    printDescriptionNoAddress: 'No address within {m} m – enter any title.',
    printDescriptionLookupFailed: 'Could not look up the nearest address – enter any title.',
    printDescriptionNearestHint: 'Nearest address: {naslov}',
    printPlaceholder: 'e.g., Project Map',
    printCancel: 'Cancel',
    printContinue: 'Continue to Print',
    printOrientation: 'Orientation',
    printLandscape: 'Landscape',
    printPortrait: 'Portrait',
    printPaperSize: 'Paper size',
    // ── V6 map shell (#597 pass 2) ────────────────────────────────────────
    sectionActivity: 'Activity',
    sectionLayers: 'Layers',
    sectionPhotos: 'Photos',
    sectionTasks: 'Tasks',
    sectionBasemap: 'Basemap',
    sectionDocs: 'Documents',
    sectionApp: 'Application',
    sectionPobude: 'Initiatives',
    sectionTraffic: 'Traffic',
    sectionAirQuality: 'Air quality',
    sectionTrees: 'Trees',
    sectionWorks: 'Investments',
    sectionGraditve: 'Constructions',
    worksOpenGraditevPage: 'Open King.Graditev',
    sectionSewer: 'Sewer',
    sectionOptGap: 'OPT without network',
    sectionOpnConditions: 'OPN building conditions',
    sectionParcelHistory: 'Parcel history',

    // Icon rail
    railTabOpnShort: 'OPN conditions',
    railTabHistoryShort: 'History',
    railNavigation: 'Navigation',
    railSettings: 'Settings',
    bottomBarAi: 'AI',

    // Floating top bar
    topbarAiAssistant: `${BRAND.name} assistant`,

    // Layers panel
    // CZK layers panel (#598) - the Czech reference layers from the map style
    czkGroupKatastr: 'Cadastre of real estate',
    czkGroupSpravni: 'Administrative division',
    czkGroupAdresy: 'Addresses',
    czkGroupTematicke: 'Thematic views',
    czkLayerParcely: 'Parcels',
    czkLayerBudovy: 'Buildings',
    czkLayerHranice: 'Parcel boundaries',
    czkLayerKatastralniUzemi: 'Cadastral districts',
    czkLayerStat: 'State',
    czkLayerRegiony: 'Cohesion regions',
    czkLayerVusc: 'Regions (VÚSC)',
    czkLayerOkresy: 'Districts',
    czkLayerOrp: 'ORP',
    czkLayerPou: 'POU',
    czkLayerObce: 'Municipalities',
    czkLayerMomc: 'City districts (MOMC)',
    czkLayerPraha: 'Prague - boroughs',
    czkLayerZsj: 'Basic settlement units',
    czkLayerAdresy: 'Addresses',
    czkLayerDruhPozemku: 'Land type',
    czkLayerStavebniParcely: 'Building parcels',
    czkLayerVyuzitiBudov: 'Building use',
    czkLayerRokDokonceni: 'Year of completion',

    layersPanelGroupGurs: 'GURS layers',
    layersPanelGroupCuzk: 'ČÚZK layers',
    layersPanelGroupPresets: 'Special views',
    layersPanelGroupView: 'View',
    layersPanelPresetUtilityConnections: 'Utility connections',
    layersPanelBuildings3d: '3D buildings',

    // Share view
    shareViewButton: 'Share view',
    shareViewCopied: 'Link copied',

    // Generic chrome
    commonClose: 'Close',
    commonBack: 'Back',
    contextAllProjects: 'All projects',
    docsNone: 'No documents available',

    // Mobile hamburger drawer
    mobileMenuTitle: 'Menu',
    mobileMenuWorksSub: 'Areas and sketches',
    mobileMenuSewerSub: 'Variant selection',
    mobileMenuPobudeSub: 'MOL × VVO analysis',
    mobileMenuTrafficSub: 'Telraam sensors',
    mobileMenuTreesSub: 'Tree cadastre — filters',
    mobileMenuOptGapSub: 'Connections without a route / cable in view',
    mobileMenuOpnSub: 'From the municipal ordinance (OPN)',
    mobileMenuAiAssistant: 'AI assistant',
    mobileMenuPzDraw: 'Draw an area',
    mobileMenuPzSearch: 'Find a parcel or address',

    // Quick actions (FAB / sidebar toolbar)
    fabAddPhotoAria: 'Add a photo',
    fabAddPhoto: 'Add photo',
    fabPhoto: 'Photo',
    fabNewTaskAria: 'New task',
    fabTask: 'Task',
    fabMeasure: 'Measure',
    fabAddPost: 'Add a post',

    // Feature popup chrome
    popupFullscreen: 'Full screen',
    popupOpenLink: 'Open',
    popupClickCoordinates: 'Click coordinates',
    popupCopyToClipboard: 'Copy to clipboard',
    popupShareFeature: 'Copy a link to this feature',
    popupNavigateTo: 'Navigate to this feature',
    popupPrevCard: 'Previous card',
    popupNextCard: 'Next card',
    popupParcelLoadError: 'Error loading the parcel. Please try again.',
    popupParcelNotFoundKo: 'Parcel KO {ko}, no. {st} could not be found.',
    popupParcelNotFound: 'The parcel could not be found.',
    popupObjectNotFound: 'Object not found',

    // Zemljiška knjiga link on the parcel card (#684)
    zkButton: 'Land register',
    zkGetExtract: 'Get the land-register extract',
    zkKo: 'KO',
    zkParcel: 'parcel',
    zkCopy: 'Copy',
    zkCopied: 'Copied',
    zkHint: 'On the e-Sodstvo page, click the »King.Prostor ZK« bookmark.',
    zkSetupLink: 'Set up the bookmark',
    zkCopyOwners: 'Copy the owners to the parcel',
    zkCopying: 'Copying the owners …',
    zkCopied2: 'The owners are copied ({n}). Source: land register.',
    zkCopyJoint: 'Joint ownership: no shares are set, so the parcel reads »owners incomplete« until you confirm the owners.',
    zkCopySeal: 'The property carries a pending entry.',
    zkCopyNewerExport: 'Copied, but a newer surveyor export holds this parcel.',
    zkCopyStavba: 'The parcel belongs to the owner of a building (the owners of its parts) — add the owners of the parcel by hand.',
    zkCopyReload: 'This page is out of date — reload it and copy the extract again.',
    zkCopyNotInChain: 'This building or building part is not in the ownership chain of this parcel.',
    zkCopyChainNoParcel: 'Copy the parcel’s extract first.',
    zkVsakokratni: 'whoever owns the property',
    zkCopyNotRegular: 'Only a regular extract can be copied.',
    zkCopyOtherParcel: 'The extract is for another parcel.',
    zkCopyIncomplete: 'The extract was not read in full, so it is not copied.',
    zkCopyNoOwners: 'The extract lists no owners.',
    zkCopyOlder: 'This parcel already has a newer extract.',
    zkCopyDuplicate: 'This extract is already copied.',
    zkCopyNoWork: 'This project has no graditev yet.',
    zkCopyNoRight: 'Copying needs the »Owners and easements« right.',
    zkCopyError: 'The owners could not be copied.',
    zkPopupBlocked: 'The browser blocked the e-Sodstvo window. Allow pop-ups and try again.',
    zkOpened: 'e-Sodstvo is open in a new tab. After signing in, click the »King.Prostor ZK« bookmark.',
    zkReading: 'Reading the extract …',
    zkExtractOf: 'Extract of {date}',
    zkOwners: 'Owners',
    zkNoOwners: 'The extract holds no registered ownership.',

    // »Hišne številke« fold on the EK spojka card (#891)
    mpOwnersSection: 'Owners (share, number, name, address, source)',
    mpOwnersSectionOne: 'Owner',
    mpOwnersLoading: 'loading …',
    mpOwnersError: 'The owners could not be loaded.',
    mpOwnersErrorAuth: 'Your sign-in has expired. Reload the page.',
    ekHsSection: 'House numbers',
    ekHsLoading: 'loading …',
    ekHsEmpty: 'No house number is linked to this splice.',
    ekHsError: 'The house numbers could not be loaded.',
    ekHsErrorForbidden: 'You have no access to this splice.',
    ekHsErrorAuth: 'Your sign-in has expired. Reload the page.',
    ekHsGone: 'no longer in the register',
    ekHsNoPoint: 'no location',
    ekHsElsewhere: 'also linked to {oznaka}',
    ekHsWithheld: '{n} in projects without access',
    ekHsSpliter: 'splitter',
    ekHsProjectNone: 'Choose a project.',
    ekHsProjectLinked: 'linked: {n}',
    ekHsProjectUnlinked: 'unlinked: {n}',
    ekHsProjectInView: 'in view',
    ekHsProjectNoArea: 'The project has no area – unlinked addresses are not shown.',
    ekHsProjectCapped: '{n} of {m} shown – zoom in',
    ekHsProjectForbidden: 'You have no access to this project’s data.',
    zkShare: 'share',
    zkRights: 'Derived rights and notices',
    zkMainParcel: 'main property',
    zkWarnings: 'Warnings',
    zkError: 'The extract could not be read.',
    popupErrorNoAccessTitle: 'No access to data',
    popupErrorNoAccessMessage: 'You do not have permission to view this layer.',
    popupErrorServerTitle: 'Server error',
    popupErrorServerMessage: 'An error occurred while fetching the data.\nTry again or contact support.',
    popupErrorConnectionTitle: 'Connection error',
    popupErrorConnectionMessage: 'The data could not be retrieved.\nCheck your internet connection.',
    // ── Map screens (#597 pass 3) ─────────────────────────────────────────
    // Page shell
    pageTitleMap: 'Activity',
    pageAiOpenAria: `Open the ${BRAND.name} assistant`,
    pageErrorTitle: 'Error',
    pageMapLoadFailed: 'The map could not be loaded. Please try again.',
    pageInitFailed: 'The application could not be started. Please try again.',
    pageBackHome: 'Back to the home page',
    pageNoAccessTitle: 'No access',
    pageNoAccessBody: 'You have no access to any application. Contact your administrator.',
    cadOverlayTitle: 'Drawing',
    mapCanvasAria: 'Map',
    commonRequestAccess: 'Request access',
    basemapButtonAria: 'Choose the basemap',

    // Access banner (MapAccessBanner)
    bannerSignin: 'Sign in to view this map.',
    bannerSessionExpired: 'Your session has expired. Sign in again to continue.',
    bannerForbidden: 'You do not have access to this application.',
    bannerForbiddenProject: 'You do not have access to this project.',
    bannerNotFoundApp: 'The application could not be found. Check the link.',
    bannerNotFoundProject: 'The project could not be found. Check the link.',
    bannerNewVersion: 'A new version is available.',
    bannerReload: 'Refresh',

    // User menu (UserAvatar)
    userMenuAuthEmailLegacy: 'Email account (older)',
    userMenuAuthEmail: 'Email account',
    userMenuAuthGoogle: 'Google Account',
    userMenuAuthMicrosoft: 'Microsoft Account',
    userMenuAuthEmailLink: 'Email account (code sign-in)',
    userMenuLogoutFailed: 'Logout failed. Please try again.',
    // #885: the language switch in the user menu
    languageSwitchLabel: 'Language',
    languageSwitchBusy: 'Save or close the open edit first, then choose the language.',
    languageSwitchNoStorage: 'The browser does not allow the language choice to be saved.',
    userMenuLoggingOut: 'Logging out...',
    userMenuLogout: 'Logout',
    userMenuDashboard: 'Dashboard',
    userMenuProfile: 'Profile',
    userMenuLoading: 'Loading...',
    userMenuUserFallback: 'User',

    // AI sidebar — client text only
    aiAssistantLabel: 'Assistant',
    aiStatusAvailable: 'Available',
    aiInputPlaceholder: 'Ask something...',
    aiNewChat: 'New conversation',
    aiCloseAria: 'Close the AI assistant',
    aiSendAria: 'Send',
    aiLockText: 'Sign in to use the AI assistant',
    aiGreeting: 'Hello! I am your AI assistant for spatial data. How can I help you?',
    aiQuick1Label: 'Building at an address',
    aiQuick1Question: 'Which building is at Prešernov trg 1, Ljubljana?',
    aiQuick2Label: 'Parcel at an address',
    aiQuick2Question: 'Which parcel is at Soča 28, Bovec?',
    aiQuick3Label: 'Environmental constraints on a parcel',
    aiQuick3Question: 'Check the environmental constraints on parcel 63/4, cadastral municipality SOČA DESNA (2209).',
    aiQuick4Label: 'Nearest hydrant',
    aiQuick4Question: 'Which is the nearest hydrant to parcel 2189/7, cadastral municipality VIČ (1723)?',
    aiQuickCz1Label: 'Building at an address',
    aiQuickCz1Question: 'Which building is at Staroměstské náměstí 1, Praha?',
    aiQuickCz2Label: 'Parcel at an address',
    aiQuickCz2Question: 'Which parcel is at Karlova 1, Praha?',
    aiQuickCz3Label: 'Parcel by number',
    aiQuickCz3Question: 'Where is parcel 1090, cadastral unit Staré Město (727024)?',
    aiQuickCz4Label: 'Cadastral unit',
    aiQuickCz4Question: 'Where is the cadastral unit Abertamy?',
    aiThinking: 'Thinking…',
    aiProcessing: 'Working…',
    aiErrorOverloaded: 'The AI service is overloaded right now. Try again in a few seconds.',
    aiErrorNoResponse: 'The AI service did not respond. Try again.',
    aiErrorGeneric: 'Something went wrong. Try again.',
    aiErrorInterrupted: 'The answer was cut off while thinking. Try again.',
    aiErrorEmpty: 'No answer arrived. The AI service may be overloaded.',
    aiErrorComm: 'Communication with the AI failed.',
    aiErrorMonthlyLimit: 'The monthly AI usage limit has been reached. Try again next month.',
    aiRetry: 'Try again',
    // AI tool indicators
    aiToolSearchParcel: 'Searching for the parcel…',
    aiToolSearchAddress: 'Searching for the address…',
    aiToolSearchPlace: 'Searching for the place…',
    aiToolGetParcelInfo: 'Fetching parcel details…',
    aiToolFindOwner: 'Searching for the owner…',
    aiToolSearchParcelsByOwner: 'Searching parcels by owner…',
    aiToolGetBuildingInfo: 'Fetching building details…',
    aiToolGetInfrastructureInfo: 'Fetching infrastructure details…',
    aiToolFindParcelsNear: 'Searching nearby parcels…',
    aiToolFindInfrastructureNear: 'Searching nearby infrastructure…',
    aiToolSearchEkFeatures: 'Searching EK objects…',
    aiToolGetRouteInfo: 'Fetching route details…',
    aiToolGetCableInfo: 'Fetching cable details…',
    aiToolGetSpliceInfo: 'Fetching splice details…',
    aiToolFindEkFeaturesNear: 'Searching nearby EK objects…',
    aiToolGetProjectEkSummary: 'Fetching the project summary…',
    aiToolFindHouseConnectionsNear: 'Searching house connections…',
    aiToolGetRoutePipes: 'Fetching route pipes…',
    aiToolGetRouteCables: 'Fetching route cables…',
    aiToolGetSpliceCables: 'Fetching splice cables…',
    aiToolGetCableFibers: 'Fetching cable fibres…',
    aiToolGetCableReserves: 'Fetching cable reserves…',
    aiToolLocateCableFault: 'Locating the fault…',
    aiToolQueryEkLengths: 'Computing EK network lengths…',
    aiToolSearchLayers: 'Searching layers…',
    aiToolGetLayerSchema: 'Fetching the layer schema…',
    aiToolGetAppMapCatalog: 'Fetching the layer catalogue…',
    aiToolGetEnvironmentalConstraints: 'Checking environmental constraints…',
    aiToolGetEnvironmentalConstraintsForParcel: 'Checking constraints for the parcel…',
    aiToolFindArsoFeaturesNear: 'Searching nearby ARSO objects…',
    aiToolGetParcelZoning: 'Checking the land use…',
    aiToolGetParcelConditions: 'Reading building conditions from the ordinance…',
    aiToolListProjectOpinions: 'Reading the opinions…',
    aiToolReadOpinionDocument: 'Reading the opinion document…',
    aiToolGetGraditevConstraints: 'Checking the restrictions of the area…',
    aiToolGetGraditevConstraintDetails: 'Reading the restriction and the regulation extracts…',
    aiToolListGraditevReports: 'Looking for the technical reports…',
    aiToolReadGraditevReport: 'Reading the technical report…',
    aiToolFindInLaw: 'Searching the text of the regulations…',
    aiToolGetProjectConsentSummary: 'Checking the parcel consents…',
    aiToolGetRouteConsentStatus: 'Checking consents along the route…',
    aiToolFindParcelsMissingConsent: 'Looking for parcels without consent…',
    aiToolControlMapLayers: 'Switching layers…',
    aiToolSwitchBasemap: 'Switching the basemap…',
    aiToolToggleSpecialView: 'Switching the view…',

    // Context switching
    ctxSearchAria: 'Context search',
    ctxRecent: 'Recent',
    ctxOtherApps: 'Other applications',
    ctxNoResults: 'No matches',
    ctxNoResultsAlt: 'No results',
    ctxApps: 'Applications',
    ctxAppsCount: 'Applications ({n})',
    ctxProjects: 'Projects',
    ctxCurrentApp: 'Current application',
    ctxCurrentAppSr: '(current application)',
    ctxSwitchTitle: 'Switch context',
    ctxSearchPlaceholder: 'Search...',
    ctxAllApps: 'All applications',
    ctxLoading: 'Loading...',
    ctxProjectsOfCount: '{n} of {total} projects',
    ctxFindProject: 'Find a project …',
    ctxFindApp: 'Find an application …',
    ctxDashboardLink: 'Dashboard →',
    ctxHints: '↑↓ move · Enter select · Esc close',
    ctxHintsRequired: '↑↓ move · Enter select',
    ctxLoadingProjects: 'Loading projects …',
    ctxNoProjects: 'No projects',
    ctxRequiredNote:
      'Choose a project to show its data. You have access to individual projects, not to the whole application.',
    ctxOtherProjectsTitle: 'Other projects in this application',
    ctxNoAccessNote: 'You do not have access to these projects.',
    ctxUnnamed: '(unnamed)',
    ctxLastOpened: 'last opened',
    ctxProjectChoiceAria: 'Project choice',
    ctxDeniedAppNote: 'You do not have access to the application »{app}«. The applications available to you are listed below.',

    // App panel
    appPanelPublicPill: 'Public',
    appPanelExplore: 'Explore',
    appPanelFindParcel: 'Find a parcel',
    appPanelFindAddress: 'Find an address',
    appPanelOrgQuestion: 'Part of an organisation?',
    appPanelLoginBody: 'Sign in to open your applications, projects and your network data.',
    appPanelNetwork: 'Network',
    appPanelNetworkProject: 'Project network',
    appPanelRecent: 'Latest activity',
    appPanelAll: 'All →',
    appPanelDashboard: 'Dashboard',
    appPanelMyApps: 'My applications',
    appPanelProject: 'Project',
    appPanelNow: 'now',
    appPanelMinAgo: '{n} min ago',
    appPanelHoursAgo: '{n} h ago',
    appPanelYesterday: 'yesterday',
    appPanelDaysAgo: '{n} days ago',
    appPanelProjectsOne: 'project',
    appPanelProjectsTwo: 'projects',
    appPanelProjectsFew: 'projects',
    appPanelProjectsMany: 'projects',
    appPanelActivityCount: 'activities',
    appPanelLastActivity: 'last activity',
    appPanelPostFallback: 'Post',
    appPanelReopened: 'Reopened',
    // Activity vocabulary
    activityNew: 'New',
    activityEdited: 'Updated',
    activityCommented: 'Comment',
    activityResolved: 'Resolved',
    activityUnresolved: 'Unresolved',
    activityAssigned: 'Assigned',
    activityReacted: 'Reaction',
    postTypeNote: 'Note',
    postTypeIssue: 'Task',
    postTypeInspection: 'Inspection',
    postStatusOpen: 'Open',
    postNoTitle: 'untitled',
    postPhotoUnit: 'photos',

    // Layers panel + layer controls leftovers
    layersPanelShowAll: 'Show all',
    layersPanelGjiKinds: 'GJI kinds',
    layerControlsEmpty: 'No layers to show',

    // Feature popup leftovers
    popupFooterLayer: 'Layer',
    popupFeatureFallback: 'Feature {index}',
    popupFeatureTitleFallback: 'Feature',

    // Measure tool
    measureUndoAria: 'Remove the last measurement point',
    measureUndoTitle: 'Remove the last point (or right-click the map)',

    // Locate me
    locateUnsupported: 'Geolocation is not supported by your browser.',
    locateGeneric: 'Unable to retrieve your location.',
    locatePermissionDenied: 'Location permission denied. Enable location access in your browser settings.',
    locateUnavailable: 'Location information is unavailable.',
    locateTimeout: 'Location request timed out. Please try again.',
    locateTimeoutSearching: 'Location request timed out. Still searching…',
    locateUnavailableSearching: 'Location information is unavailable. Still searching…',
    // ── Site pages (#597 pass 4) ──
    pageTitleLogin: 'Sign in',
    pageTitleRegister: 'Register',
    pageTitleVerifyEmail: 'Verify e-mail',
    pageTitleForgotPassword: 'Forgot password',
    pageTitleResetPassword: 'Reset password',
    pageTitleLoginLink: 'Sign in',
    pageTitleDeviceLogin: 'Device sign-in',
    pageTitleLogout: 'Sign out',
    pageTitleDashboard: 'Dashboard',
    pageTitleProfile: 'Profile',
    pageTitleCommits: '{name} · change history',
    pageTitleRequestAccess: 'Access request - {name}',
    pageTitleLimitedAccess: 'Limited access',
    pageTitleLimitedAccessFor: 'Limited access - {name}',
    pageTitleNoAccess: 'No access - {name}',
    pageTitleInvite: 'Invitation · {name}',
    pageTitleInvitePlain: 'Invitation',
    authErrInvalidCredentials: 'Wrong e-mail or password.',
    authErrAccountDisabled: 'This account is disabled.',
    authErrTooMany: 'Too many sign-in attempts. Try again in {seconds} s.',
    authErrWeakPassword: 'The password needs at least 8 characters with an uppercase letter, a lowercase letter and a digit.',
    authErrEmailTaken: 'An account with this e-mail already exists.',

    // Logout page
    logoutLoading: 'Loading...',
    logoutTitle: 'Sign out',
    logoutButton: 'Sign out',
    logoutWorking: 'Signing out...',
    logoutCancel: 'Cancel',
    logoutGoToLogin: 'Go to login',
    logoutNotSignedIn: 'You are not signed in.',
    logoutLoadFailed: 'Failed to load user information.',

    // Relative time
    relTimeNow: 'just now',
    relTimeYesterday: 'yesterday',
    relTimeSecondsOne: '{n} second ago',
    relTimeSecondsTwo: '{n} seconds ago',
    relTimeSecondsMany: '{n} seconds ago',
    relTimeMinutesOne: '{n} minute ago',
    relTimeMinutesTwo: '{n} minutes ago',
    relTimeMinutesMany: '{n} minutes ago',
    relTimeHoursOne: '{n} hour ago',
    relTimeHoursTwo: '{n} hours ago',
    relTimeHoursMany: '{n} hours ago',
    relTimeDaysOne: '{n} day ago',
    relTimeDaysTwo: '{n} days ago',
    relTimeDaysMany: '{n} days ago',
    relTimeWeeksOne: '{n} week ago',
    relTimeWeeksTwo: '{n} weeks ago',
    relTimeWeeksMany: '{n} weeks ago',
    relTimeMonthsOne: '{n} month ago',
    relTimeMonthsTwo: '{n} months ago',
    relTimeMonthsMany: '{n} months ago',
    relTimeYearsOne: '{n} year ago',
    relTimeYearsTwo: '{n} years ago',
    relTimeYearsMany: '{n} years ago',

    // Dashboard
    dashWelcome: 'Welcome, {name}',
    dashContinue: 'Continue',
    dashPublicMap: 'Public map',
    dashPublicMapBody: 'Parcels, buildings, addresses and spatial data for the whole country.',
    dashOpenPublicMap: 'Open map',
    dashLayerDocs: 'Layer documentation',
    dashCountrySlovenia: 'Slovenia',
    dashCountryCzechia: 'Czechia',
    dashMapStatLayers: 'Layers on the map',
    dashMapStatThemes: 'Themes',
    dashMapStatPublishers: 'Publishers',
    dashMapOnMap: 'What is on the map',
    dashMapCatalogue: 'Layer catalogue',
    dashMapShowAllThemes: 'Show all themes ({n})',
    dashMapColData: 'Data',
    dashMapColSource: 'Source date',
    dashMapSources: 'Sources and licences',
    dashMapPublishers: 'Publishers',
    dashMapStatParcels: 'Parcels as of',
    dashMapCountsNote: 'The figures count the layers this map shows. The layer documentation also covers the layers for KingCAD and Czechia.',
    dashMapWeekly: 'Weekly refreshed data',
    dashMapColOnMap: 'On the map',
    dashMapAdmin: 'Administrator',
    dashMapSummaryFailed: 'The layer facts cannot be shown right now.',
    dashAppsCount: '{n} applications',
    dashProjectsCount: '{n} projects',
    dashRecentlyVisited: 'Recently visited',
    dashOpenMap: 'Open map',
    dashShowLess: 'Show less',
    dashShowMore: 'Show more',
    dashShowMoreN: 'Show more ({n})',
    dashShowAllN: 'Show all ({n})',
    dashShowAllMore: 'Show all ({n} more)',
    dashApps: 'Applications',
    dashSearchApps: 'Search applications...',
    dashProjAbbrev: '{n} proj',
    dashRecentActivity: 'Recent activity',
    dashAllApps: 'All applications',
    dashNoRecentActivity: 'No recent activity.',
    dashHome: 'Home',
    dashMap: 'Map',
    dashSettings: 'Settings',
    dashSettingsAdminTitle: 'Application settings (admin)',
    dashReports: 'Reports',
    dashSluznosti: 'Easements',
    dashSluznostiTitle: 'Easement register (parcels, owners, contracts)',
    dashReportsWriteTitle: 'Reports (write access)',
    dashProjectSettingsTitle: 'Project settings (admin)',
    dashWorks: 'Constructions',
    dashWorksNew: 'New construction',
    dashWorksNewTitle: 'New construction — type the name, add the rest step by step',
    dashWorksEmpty: 'No constructions yet. Start with »New construction« — type the name, add the rest step by step.',
    dashWorksOpinions: 'Opinions {done}/{total}',
    dashParcelsCount: '{n} parcels',
    dashProjects: 'Projects',
    dashNewProject: 'New project',
    dashSearchProjects: 'Search projects...',
    dashNoProjects: 'No projects.',
    dashUpdated: 'Updated {when}',
    dashProjectName: 'Project name',
    dashProjectNamePlaceholder: 'e.g. New estate Polje',
    dashProjectKind: 'Project type',
    dashDescription: 'Description',
    dashOptional: '(optional)',
    dashDescriptionPlaceholder: 'Short project description',
    dashCancel: 'Cancel',
    dashCreateProject: 'Create project',
    dashCreating: 'Creating...',
    dashActivity: 'Activity',
    dashFilterAll: 'All',
    dashFilterTasks: 'Tasks',
    dashFilterPhotos: 'Photos',
    dashFilterComments: 'Comments',
    dashFilterOpinions: 'Opinions',
    dashLatestChanges: 'Latest changes',
    dashAllChanges: 'All changes',
    dashNoEdits30: 'No edits in the last 30 days',
    dashEditProject: 'Edit project',
    dashDrawOnMap: "Show the project on the application's shared map",
    dashDrawOnMapHint:
      'When off, the project is not drawn on the shared map with all projects. It stays in the list, you can open it on its own, and in the desktop application (KingCAD) nothing changes.',
    dashSave: 'Save',
    dashSaving: 'Saving...',
    dashShowOnMap: 'Show on the map',
    dashElaboratTitle: 'Objects for the GJI report',
    dashElaboratSubtitle:
      'In operation without a GURS identifier, owner GVO (by code list) or without an owner · total {n}',
    dashElaboratAllOk: 'Every object in operation recorded by GVO has a GURS identifier',
    dashReportLink: 'Report →',
    dashOpinionsTitle: 'Opinions / Consents',
    dashEditing: 'Edit →',
    dashNoOpinions: 'No opinions for this project.',
    dashDocsAbbrev: '{n} doc.',
    dashNoDocs: 'No documents.',
    dashTagTask: 'task',
    dashTagNote: 'note',
    dashTagInspection: 'inspection',
    dashUsersOne: '{n} user',
    dashUsersTwo: '{n} users',
    dashUsersFew: '{n} users',
    dashUsersMany: '{n} users',
    dashNever: 'never',
    dashUnknownUser: 'Unknown user',
    dashUnknownUserAlt: 'Unknown user',
    dashSectionToday: 'Today',
    dashSectionWeek: 'Last 7 days',
    dashSectionMonth: 'Last 30 days',
    dashChangesOne: '{n} change',
    dashChangesTwo: '{n} changes',
    dashChangesFew: '{n} changes',
    dashChangesMany: '{n} changes',
    dashCommitOne: '{n} commit',
    dashCommitMany: '{n} commits',
    dashAndOtherOne: ' and {n} other',
    dashAndOthers: ' and {n} others',
    dashActiveProjectsZero: '0 projects active in the last 30 days',
    dashActiveProjectsOne: '1 project active in the last 30 days',
    dashActiveProjectsTwo: '2 projects active in the last 30 days',
    dashActiveProjectsFew: '{n} projects active in the last 30 days',
    dashActiveProjectsMany: '{n} projects active in the last 30 days',
    dashOpinionsSummary: '{yes} of {total} obtained',
    dashStageConditionsRequest: 'Request for conditions',
    dashStageConditions: 'Conditions',
    dashStageOpinionRequest: 'Request for an opinion',
    dashStageOpinion: 'Opinion',
    dashStatusObtained: 'Obtained',
    dashStatusRejected: 'Rejected',
    dashStatusPending: 'In progress',
    dashRequestFailed: 'The request failed.',
    dashErrorStatus: 'Error ({status})',

    // Profile
    profileEditName: 'Edit name',
    profileFirstName: 'First name',
    profileLastName: 'Last name',
    profileMemberSince: 'Member since {date}',
    profileAppsOne: '{n} application',
    profileAppsTwo: '{n} applications',
    profileAppsFew: '{n} applications',
    profileAppsMany: '{n} applications',
    profileAdminPanel: 'Admin panel',
    profileDesktopPasswordTitle: 'Password for older versions of the program',
    profileDesktopPasswordBody: 'You do not need it to sign in. Set one only if you use an older version of the program (before 27.2).',
    profilePasswordChangeSignsOut: 'After you set or change the password you will be signed out on every device and will have to sign in again.',
    profileSendPasswordLink: 'Send a link to set a password',
    profileSendPasswordChangeLink: 'Send a link to change the password',
    profileSending: 'Sending ...',
    profilePasswordLinkSent: 'We sent the link to {email}. Open the message and follow the instructions.',
    profileDevices: 'My devices',
    profileDevicesOne: '{n} device',
    profileDevicesTwo: '{n} devices',
    profileDevicesFew: '{n} devices',
    profileDevicesMany: '{n} devices',
    profileNoDevices: 'No KingCAD computer is currently signed in with this account.',
    profileDeviceLabel: 'Device {code}',
    profileLastUsed: 'last used',
    profileDevicesError: 'The device list could not be loaded.',
    profileSignOutAll: 'Sign me out everywhere',
    profileSignOutAllConfirm:
      'All devices will be signed out; on each one you will have to sign in again through the browser. Continue?',
    profileSignOutAllYes: 'Yes, sign out everywhere',
    profileSigningOut: 'Signing out …',
    profileSignedOutAll: 'All devices are signed out.',
    profileSignOutAllNeedsBrowser: 'Sign in through the browser for this action (password, Google or an e-mail link).',
    // #791 the device list: name + last used, »Remove« per device
    profileDevicesIntro: 'KingCAD computers signed in with this account. Signing a computer out does not take it off the list — »Remove« does.',
    profileDevicesPrivacyLink: 'What data we record and for how long',
    profileDevicesLimit: 'You are using {count} of {cap} devices.',
    profileDeviceRemove: 'Remove',
    profileDeviceRemoveConfirm: 'Remove the device {name} from the list? You will have to sign in on it again, and only after the waiting period.',
    profileDeviceRemoveYes: 'Yes, remove',
    profileDeviceRemoving: 'Removing …',
    profileDeviceRemoved: 'The device {name} has been removed.',
    profileDeviceRemoveCapped: 'The number of device removals allowed this month has been reached.',
    profileDeviceRemoveFailed: 'The device could not be removed. Try again.',
    profileNoApps: 'You have no access to any application',
    profileConnectionError: 'Connection error. Please try again.',
    profileTooMany: 'Too many requests. Wait a minute and try again.',
    profileSendFailed: 'Sending failed. Please try again.',
    profileSaveFailed: 'Saving failed.',
    profileConnectionFailed: 'Connection error.',
    profileLoadAppsFailed: 'The applications could not be loaded.',
    profileDateUnknown: 'N/A',

    // Commits
    commitsCrumb: 'Change history',
    commitsSearchUsers: 'Search users...',
    commitsOn: 'Commits on {date}',
    commitsInserted: '+{n} inserted',
    commitsUpdated: '~{n} updated',
    commitsDeleted: '-{n} deleted',
    commitsNoChanges: 'No changes',
    commitsPageInfo: 'Page {page} of {pages} · {total} commits in total',
    commitsContributors: 'Contributors',
    commitsMissingParam: 'Missing sxapp or sxproject parameter',
    commitsAppInfoError: 'The application details could not be loaded',
    commitsProjectNotFound: 'Project not found',
    commitsProjectInfoError: 'The project details could not be loaded',
    commitsLoadError: 'The commit history could not be loaded',
    commitsDateTime: '{date} at {time}',
    commitsAllUsers: 'All users',
    commitsAllDates: 'All dates',
    commitsWeekdays: 'Mo,Tu,We,Th,Fr,Sa,Su',
    commitsMonths: 'January,February,March,April,May,June,July,August,September,October,November,December',

    // Access pages + invitation
    accessRequestTitle: 'Access request',
    accessNoAccessTo: 'You do not have access to:',
    accessMessageLabel: 'Message to the administrator',
    accessMessagePlaceholder: 'Briefly say who you are and why you need access.',
    accessSend: 'Send request',
    accessSending: 'Sending...',
    accessSentTitle: 'Request sent',
    accessSentBody: 'The administrator will review your request and notify you by e-mail.',
    accessAlreadyRequested: 'An access request has already been sent.',
    accessSubmitFailed: 'The request could not be sent.',
    accessLimitedTitle: 'Limited access',
    accessLimitedBody: 'You do not have access to the whole application:',
    accessLoading: 'Loading...',
    accessYouHave: 'You have access to {projects}:',
    accessNoProjects: 'You do not have access to any project in this application.',
    accessAppMissing: 'The application does not exist.',
    accessProjectsLoadError: 'The projects could not be loaded.',
    accessProjectsZero: 'no project',
    accessContactAdmin: 'For access, contact an administrator:',
    accessContactOrgAdmin: "For access, contact the organisation's administrator.",
    accessCheckFailed: 'Access could not be verified. Contact support.',
    accessRequestFull: 'Request full access',
    popupNoProperties: 'No properties available',
    popupErrorSessionTitle: 'Sign-in not valid',
    popupErrorSessionMessage: 'Sign in again. If the problem persists, contact support.',
    popupNoData: 'No data',
    commonYes: 'Yes',
    commonNo: 'No',
    measureUndoLabel: 'Step back',
    inviteNotFound: 'Invitation not found',
    inviteNotFoundBody: 'The invitation link may be wrong, or the invitation was deleted.',
    inviteNoLongerValid: 'Invitation no longer valid',
    inviteAccepted: 'This invitation has already been accepted.',
    inviteCancelled: 'This invitation was cancelled.',
    inviteExpired: 'This invitation has expired.',
    inviteAskNew: 'Ask the administrator to send a new invitation.',
    inviteTitle: 'Invitation to an application',
    inviteOrg: 'Organisation: {org}',
    inviteFrom: 'Invited by:',
    inviteForEmail: 'For e-mail:',
    inviteValidUntil: 'Valid until:',
    inviteSignInOrRegister: 'To accept the invitation, create an account with this e-mail address. No password is needed. If you already have an account, sign in.',
    inviteCreateAccount: 'Create an account',
    inviteSignedInAs: 'You are signed in as {email}. Press the button to accept the invitation.',
    inviteAccept: 'Accept invitation',
    inviteAccepting: 'Accepting…',
    inviteWrongAccount:
      'You are signed in as {current}, but the invitation is for {invited}. Sign out and sign in with the address the invitation was sent to.',
    inviteSignOut: 'Sign out',
    inviteErr401: 'Your sign-in is not valid or the account cannot be used.',
    inviteErr403: 'This invitation was issued for a different e-mail address.',
    inviteErr404: 'The invitation could not be found.',
    inviteErr409: 'This invitation is no longer valid.',
    inviteErrGeneric: 'The invitation could not be accepted. Try again or tell the administrator.',
    inviteInvalidPath: 'Invalid path.',
    inviteLoadFailed: 'The invitation could not be loaded.',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // AUTH-ONLY DICTIONARIES (kingcad#426)
  //
  // The KingCAD desktop clients open /register, /login, /verify-email,
  // /forgot-password and /reset-password with an explicit ?lang= in these five
  // languages, so every auth key below is present. Map-UI keys are absent on
  // purpose in de / hr / sr / bs — translate() falls back to English per key.
  // Czech is the exception: it also carries the map-UI set (#597), because the
  // CZK web map serves Czech users in their browser's language.
  // ─────────────────────────────────────────────────────────────────────────

  de: {
    // Auth page - Tabs
    login: 'Anmelden',
    register: 'Registrieren',

    // Auth page - Form fields
    email: 'E-Mail',
    password: 'Passwort',
    firstName: 'Vorname',
    lastName: 'Nachname',

    // Auth page - Placeholders
    emailPlaceholder: 'ihre.email@beispiel.de',
    passwordPlaceholder: 'Passwort eingeben',
    firstNamePlaceholder: 'Max',
    lastNamePlaceholder: 'Mustermann',

    // Auth page - Buttons
    loginButton: 'Anmelden',
    createAccount: 'Konto erstellen',
    creatingAccount: 'Konto wird erstellt...',
    forgotPassword: 'Passwort vergessen?',
    orContinueWith: 'oder weiter mit',

    // OAuth buttons
    continueWithGoogle: 'Weiter mit Google',
    continueWithMicrosoft: 'Weiter mit Microsoft',

    // Email verification status
    // #569 desktop browser login + email login link
    deviceLoginTitle: 'KingCAD-Geräteanmeldung',
    deviceLoginLoading: 'Anmeldeanfrage wird geladen…',
    deviceLoginSignedInAs: 'Sie sind angemeldet als',
    deviceLoginQuestion: '{product} auf diesem Gerät anmelden?',
    deviceLoginMachine: 'Gerät',
    deviceLoginWindowsUser: 'Windows-Benutzer',
    deviceLoginProgram: 'Programm',
    deviceLoginRemote: 'Remotesitzung',
    deviceLoginExpiresIn: 'Die Anfrage läuft in {min} Min. ab.',
    deviceLoginAllow: 'Erlauben',
    deviceLoginApproving: 'Wird bestätigt…',
    deviceLoginNotYou: 'Nicht Sie? Mit anderem Konto anmelden',
    deviceLoginCancel: 'Abbrechen',
    deviceLoginDone: 'Fertig — kehren Sie zu {product} zurück.',
    deviceLoginDoneClose: 'Sie können diesen Tab schließen.',
    deviceLoginExpired: 'Die Anfrage ist abgelaufen. Klicken Sie im Programm erneut auf „Im Browser anmelden“.',
    deviceLoginDenied: 'Die Anmeldung wurde abgebrochen.',
    deviceLoginConsumed: 'Diese Anfrage wurde bereits verwendet.',
    deviceLoginNotFound: 'Unbekannte Anmeldeanfrage.',
    deviceLoginError: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    deviceLoginBackHome: 'Zur Startseite',
    // #580 mailbox gate
    deviceConfirmNeeded: 'Um ein neues Gerät hinzuzufügen, bestätigen Sie den Zugriff auf das Konto-Postfach.',
    deviceConfirmSend: 'Bestätigungscode senden',
    deviceConfirmSending: 'Wird gesendet…',
    deviceConfirmSentTo: 'Code an {email} gesendet. Prüfen Sie Ihre Post (auch Spam).',
    deviceConfirmCodeLabel: 'Geben Sie den 6-stelligen Code aus der E-Mail ein',
    deviceConfirmSubmit: 'Code bestätigen',
    deviceConfirmChecking: 'Wird geprüft…',
    deviceConfirmResend: 'Erneut senden',
    deviceConfirmExpiresIn: 'Der Code läuft in {min} Min. ab.',
    deviceConfirmWrong: 'Falscher Code. Versuchen Sie es erneut.',
    deviceConfirmTooMany: 'Zu viele Fehlversuche. Nutzen Sie den Link aus der E-Mail oder starten Sie im Programm eine neue Anmeldung.',
    deviceConfirmExpiredCode: 'Der Code ist abgelaufen. Senden Sie einen neuen.',
    deviceConfirmCooldown: 'Bitte warten Sie einen Moment vor dem erneuten Senden.',
    deviceConfirmCapped: 'Das Sendelimit ist erreicht. Versuchen Sie es später oder melden Sie sich mit Google oder per E-Mail an.',
    deviceConfirmSendFailed: 'Das Senden der E-Mail ist fehlgeschlagen. Bitte erneut versuchen.',
    deviceConfirmProven: 'Postfach bestätigt — Sie können die Anmeldung erlauben.',
    deviceLinkQuestion: '{product} für das Konto {email} anmelden?',
    deviceLinkConfirm: 'Gerät bestätigen',
    deviceLinkClose: 'Schließen',
    deviceLinkInvalid: 'Dieser Link ist ungültig oder abgelaufen. Fordern Sie im Programm eine neue Anmeldung an.',
    loginLinkRedeeming: 'Anmeldung läuft…',
    loginLinkSuccess: 'Sie sind angemeldet',
    loginLinkContinue: 'Weiter',
    loginLinkFailed: 'Dieser Link ist ungültig oder abgelaufen',
    loginLinkFailedHint: 'Ein Link ist 15 Minuten gültig und kann nur einmal verwendet werden.',
    loginLinkRequestNew: 'Zurück zur Anmeldung',
    loginLinkOption: 'Anmeldecode senden',
    loginLinkSending: 'Wird gesendet…',
    loginLinkSent: 'Falls das Konto existiert, haben wir einen Code an {email} gesendet. Prüfen Sie Ihr Postfach.',
    loginLinkNeedEmail: 'Geben Sie zuerst Ihre E-Mail-Adresse ein.',
    loginLinkError: 'Senden fehlgeschlagen. Bitte versuchen Sie es erneut.',
    // #667 code door — on the main road since #668, so in every language
    loginLinkSentCode: 'Geben Sie den Code aus der E-Mail hier ein oder klicken Sie auf den Link darin.',
    loginLinkCodeLabel: 'Code aus der E-Mail',
    loginLinkCodeSubmit: 'Code bestätigen',
    loginLinkCodeChecking: 'Wird geprüft…',
    loginLinkCodeWrong: 'Der Code ist nicht richtig.',
    loginLinkCodeExpired: 'Der Code ist abgelaufen. Senden Sie sich einen neuen.',
    loginLinkCodeUsed: 'Dieser Code gilt nicht mehr: Er wurde verwendet (zum Beispiel über den Link in einem anderen Browser) oder durch einen neueren ersetzt. Senden Sie sich einen neuen.',
    loginLinkCodeRefused: 'Mit diesem Konto ist diese Anmeldung nicht möglich.',
    loginLinkCodeTooMany: 'Zu viele Versuche. Warten Sie eine Minute und versuchen Sie es erneut.',
    loginLinkResend: 'Erneut senden',
    loginLinkResendIn: 'Erneut senden in {min} Min.',
    loginLinkResendInS: 'Erneut senden in {s} s',
    // #668: mail-first login page, newest-code resend
    loginNoPasswordNeeded: 'Für die Anmeldung brauchen Sie kein Passwort.',
    loginWithPasswordToggle: 'Sie haben ein Passwort? Mit Passwort anmelden',
    loginLinkResent: 'Wir haben einen neuen Code gesendet. Nur der neueste ist gültig.',
    loginLinkResendUnsure: 'Die Verbindung zum Server wurde unterbrochen. Senden Sie den Code erneut. Falls inzwischen eine neue E-Mail angekommen ist, verwenden Sie den Link darin.',
    verifyingEmail: 'E-Mail-Adresse wird bestätigt...',
    emailVerified: 'E-Mail bestätigt!',
    emailVerifiedSuccess: 'Ihre E-Mail-Adresse wurde erfolgreich bestätigt. Sie können sich jetzt anmelden.',
    verificationFailed: 'Bestätigung fehlgeschlagen',
    verificationFailedMessage: 'Der Bestätigungslink ist ungültig oder abgelaufen.',
    requestNewLink: 'Neuen Bestätigungslink anfordern',

    // Verify email page
    continueToMap: 'Weiter zur Karte',
    backToLogin: 'Zurück zur Anmeldung',
    verificationLinkInvalid: 'Der Bestätigungslink ist ungültig oder abgelaufen.',
    enterEmailForNewLink: 'Geben Sie Ihre E-Mail-Adresse ein, um einen neuen Bestätigungslink zu erhalten:',
    resendVerificationEmail: 'Bestätigungs-E-Mail erneut senden',
    sending: 'Wird gesendet...',
    verificationEmailSent: '✓ Bestätigungs-E-Mail gesendet! Bitte prüfen Sie Ihren Posteingang.',

    // Verify email page — pending activation
    pendingTitle: 'Prüfen Sie Ihren Posteingang',
    pendingSentTo: 'Wir haben einen Bestätigungslink gesendet an',
    pendingExpiry: 'Der Link ist 24 Stunden gültig. Ihr Konto wird aktiviert, sobald Sie ihn anklicken.',
    pendingWrongAddress: 'Falsche E-Mail-Adresse?',
    pendingRegisterAgain: 'Registrieren Sie sich erneut mit der richtigen Adresse.',
    sendFailedTitle: 'Die E-Mail konnte nicht gesendet werden',
    sendFailedInfo: 'Die Bestätigungs-E-Mail konnte nicht gesendet werden an',
    sendFailedRetry: 'Versuchen Sie es unten erneut.',
    continueToLogin: 'Weiter zur Anmeldung',
    resendAlreadyVerified: 'Diese E-Mail-Adresse ist bereits bestätigt — Sie können sich anmelden.',
    resendInvalidEmail: 'Diese E-Mail-Adresse ist ungültig. Bitte prüfen Sie die Schreibweise.',
    resendTooMany: 'Zu viele Versuche. Bitte warten Sie einige Minuten und versuchen Sie es erneut.',
    emailNotVerifiedError: 'Ihre E-Mail-Adresse ist noch nicht bestätigt. Ihr Konto wird aktiviert, sobald Sie den Link in der Bestätigungs-E-Mail anklicken.',
    verifyEmailCta: 'E-Mail-Adresse bestätigen',
    resendNotReceived: 'Keine Bestätigungs-E-Mail erhalten?',
    oauthNoEmail: 'Ihr Microsoft-Konto hat keine E-Mail-Adresse. Erstellen Sie ein Konto mit einer E-Mail-Adresse.',
    emailAlreadyRegistered: 'Ein Konto mit dieser E-Mail-Adresse existiert bereits.',
    conflictSignIn: 'Melden Sie sich an',
    conflictOr: 'oder',
    conflictReset: 'setzen Sie Ihr Passwort zurück',
    conflictResetNote: 'Das Zurücksetzen des Passworts bestätigt zugleich Ihre E-Mail-Adresse.',

    // Password strength
    passwordStrength: 'Passwortstärke',
    passwordTooWeak: 'Zu schwach',
    passwordWeak: 'Schwach - Ergänzen Sie',
    passwordFair: 'Ausreichend - Ergänzen Sie',
    passwordGood: 'Gut - Das Passwort erfüllt die Anforderungen',
    passwordStrong: 'Stark - Ausgezeichnetes Passwort!',
    atLeast8Chars: 'mindestens 8 Zeichen',
    anUppercase: 'einen Großbuchstaben',
    aLowercase: 'einen Kleinbuchstaben',
    aNumber: 'eine Ziffer',

    // User menu
    logout: 'Abmelden',
    loggingOut: 'Abmeldung läuft...',
    emailAccount: 'E-Mail-Konto',
    googleAccount: 'Google-Konto',
    microsoftAccount: 'Microsoft-Konto',
    legacyAccount: 'Altkonto',

    // Errors
    loginFailed: 'Anmeldung fehlgeschlagen. Bitte prüfen Sie Ihre Zugangsdaten.',
    registrationFailed: 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    // #675: signup without a password, the name step, the KingCAD done page
    registerSubmit: 'Konto erstellen und Code senden',
    registerNoPasswordNote: 'Kein Passwort nötig. Sie erhalten einen Anmeldecode per E-Mail.',
    registerCodeSent: 'Wir haben einen Code an {email} gesendet. Prüfen Sie Ihren Posteingang.',
    registerFixAddress: 'Falsche E-Mail-Adresse?',
    registerFixAddressLink: 'Korrigieren',
    registerNameRequired: 'Geben Sie Vor- und Nachnamen ein.',
    registerDoneTitle: 'Ihr Konto ist bereit',
    registerDoneBody: 'Kehren Sie zu {product} zurück und klicken Sie auf »Im Browser anmelden«.',
    registerDoneOldBuild: 'Wenn Ihre Programmversion diese Schaltfläche nicht hat, legen Sie im Profil ein Passwort fest.',
    registerDoneProfileLink: 'Profil öffnen',
    welcomeTitle: 'Wie dürfen wir Sie nennen?',
    welcomeIntro: 'Kollegen sehen Ihren Namen bei Aufgaben, Kommentaren und Lizenzen.',
    welcomeSave: 'Speichern und weiter',
    welcomeSkip: 'Überspringen',
    welcomeSaveFailed: 'Speichern fehlgeschlagen. Bitte versuchen Sie es erneut.',
    oauthEmailConflict: 'Ein Konto mit dieser E-Mail-Adresse existiert bereits. Bitte melden Sie sich mit Ihrem Passwort oder der bisher genutzten Methode an.',
    emailVerificationFailed: 'Die E-Mail-Adresse konnte nicht bestätigt werden. Bitte versuchen Sie es erneut oder fordern Sie einen neuen Bestätigungslink an.',
    networkError: 'Verbindung zum Server fehlgeschlagen. Bitte prüfen Sie Ihre Internetverbindung.',
    serverUnavailable: 'Der Server ist derzeit nicht erreichbar. Bitte versuchen Sie es in wenigen Augenblicken erneut.',

    // Auth pages — desktop-client mode + inline copy (kingcad#426)
    optional: '(optional)',
    desktopRegisterHint: 'Mit diesem Konto melden Sie sich in {product} an.',
    desktopVerifiedReturnHint: 'Ihr Konto ist aktiviert — kehren Sie zu {product} zurück und melden Sie sich an.',
    alreadyHaveAccount: 'Sie haben bereits ein Konto?',
    signInLink: 'Anmelden',
    noAccountYet: 'Noch kein Konto?',
    createAccountLink: 'Jetzt erstellen',

    // Forgot password page
    forgotPasswordTitle: 'Passwort vergessen?',
    forgotPasswordIntro: 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen des Passworts.',
    sendResetLink: 'Link zum Zurücksetzen senden',
    forgotPasswordFailed: 'Der Link zum Zurücksetzen konnte nicht gesendet werden.',
    forgotCooldownNotice: 'Bitte warten Sie {seconds} s, bevor Sie einen neuen Link anfordern.',
    checkYourEmail: 'Prüfen Sie Ihre E-Mails',
    resetLinkSent: 'Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir Ihnen einen Link zum Zurücksetzen des Passworts gesendet. Bitte prüfen Sie Ihren Posteingang.',
    genericError: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',

    // Reset password page
    resetPasswordTitle: 'Passwort zurücksetzen',
    resetPasswordIntro: 'Geben Sie unten Ihr neues Passwort ein.',
    newPassword: 'Neues Passwort',
    newPasswordPlaceholder: 'Neues Passwort eingeben',
    confirmNewPassword: 'Neues Passwort bestätigen',
    confirmNewPasswordPlaceholder: 'Neues Passwort erneut eingeben',
    resetPasswordButton: 'Passwort zurücksetzen',
    resetting: 'Wird zurückgesetzt...',
    passwordResetTitle: 'Passwort zurückgesetzt!',
    passwordResetSuccess: 'Ihr Passwort wurde erfolgreich zurückgesetzt. Sie können sich jetzt mit dem neuen Passwort anmelden.',
    invalidLinkTitle: 'Ungültiger oder abgelaufener Link',
    invalidLinkMessage: 'Dieser Link zum Zurücksetzen des Passworts ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen an.',
    requestNewResetLink: 'Neuen Link anfordern',
    passwordsDoNotMatch: 'Die Passwörter stimmen nicht überein.',
    passwordMinLength: 'Das Passwort muss mindestens 8 Zeichen lang sein.',
    passwordNeedsUppercase: 'Das Passwort muss mindestens einen Großbuchstaben enthalten.',
    passwordNeedsLowercase: 'Das Passwort muss mindestens einen Kleinbuchstaben enthalten.',
    passwordNeedsNumber: 'Das Passwort muss mindestens eine Ziffer enthalten.',
    resetPasswordFailed: 'Das Passwort konnte nicht zurückgesetzt werden.',
    strengthWeak: 'Schwach',
    strengthFair: 'Ausreichend',
    strengthGood: 'Gut',
    strengthStrong: 'Stark',
    authErrInvalidCredentials: 'Falsche E-Mail-Adresse oder falsches Passwort.',
    authErrAccountDisabled: 'Dieses Konto ist deaktiviert.',
    authErrTooMany: 'Zu viele Anmeldeversuche. Versuchen Sie es in {seconds} s erneut.',
    authErrWeakPassword: 'Das Passwort braucht mindestens 8 Zeichen mit Groß- und Kleinbuchstaben und einer Ziffer.',
    authErrEmailTaken: 'Ein Konto mit dieser E-Mail-Adresse existiert bereits.',

    // #885: the map UI and the site pages in German — a DRAFT, machine-quality, pending a
    // native review (like the Czech set of #597). Same keys as sl / en / cs.
    deviceLoginTitleProduct: 'Bei {product} anmelden',
    deviceLoginSwitchAccount: 'Mit einem anderen Konto anmelden',
    deviceLoginApprovedWaiting: 'Bestätigt. Warten auf das Programm…',
    deviceLoginNotCollected: 'Das Programm hat die Anmeldung nicht übernommen. Klicken Sie im Programm erneut auf die Schaltfläche und lassen Sie das Fenster geöffnet.',
    deviceLoginHintFresh: 'Tipp: Wenn Sie sich mit Google oder per E-Mail anmelden, brauchen Sie diesen Code nicht.',
    deviceLimitTitle: 'Die Geräteliste ist voll',
    deviceLimitBody: 'Mit diesem Konto sind bereits {count} Geräte angemeldet (höchstens {cap}). Entfernen Sie eines, um die Anmeldung auf diesem Gerät zu erlauben.',
    deviceLimitCooldown: 'Sie haben dieses Gerät vor Kurzem entfernt. Es kann am {date} wieder hinzugefügt werden.',
    deviceLimitRemove: 'Entfernen',
    deviceLimitRemoving: 'Wird entfernt…',
    deviceLimitRemoved: 'Das Gerät wurde entfernt.',
    deviceLimitFreed: 'Ein Platz ist frei — Sie können die Anmeldung erlauben.',
    deviceLimitRemoveFailed: 'Das Gerät konnte nicht entfernt werden. Versuchen Sie es erneut.',
    deviceLimitRefreshFailed: 'Das Gerät wurde entfernt, aber der Status konnte nicht aktualisiert werden. Versuchen Sie es erneut.',
    deviceLimitRetry: 'Erneut versuchen',
    deviceLimitRemoveCapped: 'Die in diesem Monat erlaubte Anzahl an Geräteentfernungen ist erreicht.',
    deviceLimitDeviceLine: '{name} · zuletzt verwendet am {date}',
    deviceLinkLimited: 'Mit diesem Konto ist bereits die höchstzulässige Anzahl an Geräten angemeldet. Melden Sie sich im Browser an und entfernen Sie ein Gerät; danach ist diese Anmeldung möglich.',
    deviceLinkLimitedCooldown: 'Sie haben dieses Gerät vor Kurzem entfernt; es kann am {date} wieder hinzugefügt werden.',
    deviceLinkManage: 'Anmelden und Geräte verwalten',
    deviceSlowdownTitle: 'Häufige Anmeldungen auf verschiedenen Computern',
    deviceSlowdownCode: 'Wegen häufiger Anmeldungen auf verschiedenen Computern wird der Code um {time} gesendet.',
    deviceSlowdownApprove: 'Wegen häufiger Anmeldungen auf verschiedenen Computern ist die Anmeldung um {time} möglich.',
    deviceSlowdownExpires: 'Diese Anmeldeanfrage ist bis dahin abgelaufen — starten Sie sie zu diesem Zeitpunkt im Programm erneut.',
    deviceSlowdownNow: 'Sie können jetzt fortfahren.',
    deviceLinkSlowdown: 'Wegen häufiger Anmeldungen auf verschiedenen Computern ist die Anmeldung um {time} möglich. Falls diese Anfrage inzwischen abläuft, starten Sie sie im Programm erneut.',
    contours: 'Höhenlinien',
    zoomInToView: 'Zum Anzeigen hineinzoomen',
    zoomOutToView: 'Zum Anzeigen herauszoomen',
    zoomInForAll: 'Nur Haupttrassen, für alle hineinzoomen',
    gjiNoKindSelected: 'Keine GJI-Art ausgewählt',
    layerKindRaster: 'Raster',
    layerKindVector: 'Vektor',
    layerOpacity: 'Deckkraft',
    layersOnCount: 'Aktiv',
    view25d: '2.5D-Ansicht',
    rightControlsPitch: 'Neigung',
    rightControlsContours: 'Höhenlinien',
    rightControlsHillshade: 'Schummerung',
    rightControlsTerrain: '2.5D-Ansicht',
    rightControlsBuildings3d: '2.5D-Gebäude',
    rightControlsTitle: 'Kartensteuerung',
    toolbarParcelValue: 'Grundstückswert',
    toolbarAdminStatus: 'Verwaltungsstatus',
    toolbarConnections: 'Anschlüsse',
    toolbarYearOfConstruction: 'Baujahr',
    basemapRaster: 'Orthofoto',
    basemapCuzkOrtofoto: 'ČÚZK-Orthofoto',
    basemapMore: 'Mehr',
    basemapStyleTitle: 'Stil der Grundkarte',
    searchPlaceholder: 'Karte durchsuchen',
    toastCopied: 'In die Zwischenablage kopiert!',
    toastShowingDistance: 'Entfernung wird angezeigt!',
    printTitle: 'Drucktitel',
    printDescription: 'Die nächstgelegene Adresse ist eingetragen – bearbeiten Sie sie nach Belieben.',
    printDescriptionSearching: 'Nächstgelegene Adresse wird gesucht …',
    printDescriptionNoAddress: 'Keine Adresse im Umkreis von {m} m – geben Sie einen beliebigen Titel ein.',
    printDescriptionLookupFailed: 'Die nächstgelegene Adresse konnte nicht ermittelt werden – geben Sie einen beliebigen Titel ein.',
    printDescriptionNearestHint: 'Nächstgelegene Adresse: {naslov}',
    printPlaceholder: 'z. B. Projektkarte',
    printCancel: 'Abbrechen',
    printContinue: 'Weiter zum Drucken',
    printOrientation: 'Ausrichtung',
    printLandscape: 'Querformat',
    printPortrait: 'Hochformat',
    printPaperSize: 'Papierformat',
    sectionActivity: 'Aktivität',
    sectionLayers: 'Ebenen',
    sectionPhotos: 'Fotos',
    sectionTasks: 'Aufgaben',
    sectionBasemap: 'Grundkarte',
    sectionDocs: 'Dokumente',
    sectionApp: 'Anwendung',
    sectionPobude: 'Initiativen',
    sectionTraffic: 'Verkehr',
    sectionAirQuality: 'Luftqualität',
    sectionTrees: 'Bäume',
    sectionWorks: 'Investitionen',
    sectionGraditve: 'Bauvorhaben',
    worksOpenGraditevPage: 'King.Graditev öffnen',
    sectionSewer: 'Kanalisation',
    sectionOptGap: 'OPT ohne Netz',
    sectionOpnConditions: 'OPN-Bebauungsbedingungen',
    sectionParcelHistory: 'Grundstückshistorie',
    railTabOpnShort: 'OPN-Bedingungen',
    railTabHistoryShort: 'Historie',
    railNavigation: 'Navigation',
    railSettings: 'Einstellungen',
    bottomBarAi: 'KI',
    topbarAiAssistant: `${BRAND.name}-Assistent`,
    czkGroupKatastr: 'Liegenschaftskataster',
    czkGroupSpravni: 'Verwaltungsgliederung',
    czkGroupAdresy: 'Adressen',
    czkGroupTematicke: 'Thematische Ansichten',
    czkLayerParcely: 'Grundstücke',
    czkLayerBudovy: 'Gebäude',
    czkLayerHranice: 'Grundstücksgrenzen',
    czkLayerKatastralniUzemi: 'Katastralgemeinden',
    czkLayerStat: 'Staat',
    czkLayerRegiony: 'Kohäsionsregionen',
    czkLayerVusc: 'Regionen (VÚSC)',
    czkLayerOkresy: 'Bezirke',
    czkLayerOrp: 'ORP',
    czkLayerPou: 'POU',
    czkLayerObce: 'Gemeinden',
    czkLayerMomc: 'Stadtteile (MOMC)',
    czkLayerPraha: 'Prag - Stadtbezirke',
    czkLayerZsj: 'Grundsiedlungseinheiten',
    czkLayerAdresy: 'Adressen',
    czkLayerDruhPozemku: 'Grundstücksart',
    czkLayerStavebniParcely: 'Bauparzellen',
    czkLayerVyuzitiBudov: 'Gebäudenutzung',
    czkLayerRokDokonceni: 'Fertigstellungsjahr',
    layersPanelGroupGurs: 'GURS-Ebenen',
    layersPanelGroupCuzk: 'ČÚZK-Ebenen',
    layersPanelGroupPresets: 'Sonderansichten',
    layersPanelGroupView: 'Ansicht',
    layersPanelPresetUtilityConnections: 'Versorgungsanschlüsse',
    layersPanelBuildings3d: '3D-Gebäude',
    shareViewButton: 'Ansicht teilen',
    shareViewCopied: 'Link kopiert',
    commonClose: 'Schließen',
    commonBack: 'Zurück',
    contextAllProjects: 'Alle Projekte',
    docsNone: 'Keine Dokumente verfügbar',
    mobileMenuTitle: 'Menü',
    mobileMenuWorksSub: 'Gebiete und Skizzen',
    mobileMenuSewerSub: 'Variantenauswahl',
    mobileMenuPobudeSub: 'MOL × VVO-Analyse',
    mobileMenuTrafficSub: 'Telraam-Sensoren',
    mobileMenuTreesSub: 'Baumkataster — Filter',
    mobileMenuOptGapSub: 'Anschlüsse ohne Trasse / Kabel im Kartenausschnitt',
    mobileMenuOpnSub: 'Aus der Gemeindeverordnung (OPN)',
    mobileMenuAiAssistant: 'KI-Assistent',
    mobileMenuPzDraw: 'Gebiet zeichnen',
    mobileMenuPzSearch: 'Grundstück oder Adresse suchen',
    fabAddPhotoAria: 'Foto hinzufügen',
    fabAddPhoto: 'Foto hinzufügen',
    fabPhoto: 'Foto',
    fabNewTaskAria: 'Neue Aufgabe',
    fabTask: 'Aufgabe',
    fabMeasure: 'Messen',
    fabAddPost: 'Beitrag hinzufügen',
    popupFullscreen: 'Vollbild',
    popupOpenLink: 'Öffnen',
    popupClickCoordinates: 'Koordinaten des Klickpunkts',
    popupCopyToClipboard: 'In die Zwischenablage kopieren',
    popupShareFeature: 'Link zu diesem Objekt kopieren',
    popupNavigateTo: 'Zu diesem Objekt navigieren',
    popupPrevCard: 'Vorherige Infokarte',
    popupNextCard: 'Nächste Infokarte',
    popupParcelLoadError: 'Fehler beim Laden des Grundstücks. Bitte versuchen Sie es erneut.',
    popupParcelNotFoundKo: 'Grundstück KG {ko}, Nr. {st} wurde nicht gefunden.',
    popupParcelNotFound: 'Das Grundstück wurde nicht gefunden.',
    popupObjectNotFound: 'Objekt nicht gefunden',
    zkButton: 'Grundbuch',
    zkGetExtract: 'Grundbuchsauszug abrufen',
    zkKo: 'KG',
    zkParcel: 'Grundstück',
    zkCopy: 'Kopieren',
    zkCopied: 'Kopiert',
    zkHint: 'Klicken Sie auf der Seite e-Sodstvo auf das Lesezeichen »King.Prostor ZK«.',
    zkSetupLink: 'Lesezeichen einrichten',
    zkCopyOwners: 'Eigentümer auf das Grundstück übernehmen',
    zkCopying: 'Eigentümer werden übernommen …',
    zkCopied2: 'Die Eigentümer wurden übernommen ({n}). Quelle: Grundbuch.',
    zkCopyJoint: 'Gemeinschaftliches Eigentum: Es sind keine Anteile festgelegt, daher wird das Grundstück als »Eigentümer unvollständig« geführt, bis Sie die Eigentümer bestätigen.',
    zkCopySeal: 'Für die Liegenschaft ist eine Eintragung anhängig.',
    zkCopyNewerExport: 'Übernommen, aber für dieses Grundstück gilt ein neuerer Export eines Vermessers.',
    zkCopyStavba: 'Das Grundstück gehört dem jeweiligen Eigentümer eines Gebäudes (den Eigentümern seiner Teile) — fügen Sie die Eigentümer des Grundstücks manuell hinzu.',
    zkCopyReload: 'Diese Seite ist veraltet — laden Sie sie neu und übernehmen Sie den Auszug erneut.',
    zkCopyNotInChain: 'Dieses Gebäude oder dieser Gebäudeteil ist nicht in der Eigentümerkette dieses Grundstücks.',
    zkCopyChainNoParcel: 'Übernehmen Sie zuerst den Auszug des Grundstücks.',
    zkVsakokratni: 'der jeweilige Eigentümer der Liegenschaft',
    zkCopyNotRegular: 'Nur ein regulärer Auszug kann übernommen werden.',
    zkCopyOtherParcel: 'Der Auszug betrifft ein anderes Grundstück.',
    zkCopyIncomplete: 'Der Auszug wurde nicht vollständig gelesen und wird daher nicht übernommen.',
    zkCopyNoOwners: 'Der Auszug enthält keine Eigentümer.',
    zkCopyOlder: 'Für dieses Grundstück liegt bereits ein neuerer Auszug vor.',
    zkCopyDuplicate: 'Dieser Auszug wurde bereits übernommen.',
    zkCopyNoWork: 'Dieses Projekt hat noch kein Bauvorhaben.',
    zkCopyNoRight: 'Für die Übernahme benötigen Sie das Recht »Eigentümer und Dienstbarkeiten«.',
    zkCopyError: 'Die Eigentümer konnten nicht übernommen werden.',
    zkPopupBlocked: 'Der Browser hat das Fenster von e-Sodstvo blockiert. Erlauben Sie Pop-ups und versuchen Sie es erneut.',
    zkOpened: 'e-Sodstvo ist in einem neuen Tab geöffnet. Klicken Sie nach der Anmeldung auf das Lesezeichen »King.Prostor ZK«.',
    zkReading: 'Auszug wird gelesen …',
    zkExtractOf: 'Auszug vom {date}',
    zkOwners: 'Eigentümer',
    zkNoOwners: 'Der Auszug enthält kein eingetragenes Eigentumsrecht.',
    mpOwnersSection: 'Eigentümer (Anteil, Nummer, Name, Adresse, Quelle)',
    mpOwnersSectionOne: 'Eigentümer',
    mpOwnersLoading: 'wird geladen …',
    mpOwnersError: 'Die Eigentümer konnten nicht geladen werden.',
    mpOwnersErrorAuth: 'Ihre Anmeldung ist abgelaufen. Laden Sie die Seite neu.',
    ekHsSection: 'Hausnummern',
    ekHsLoading: 'wird geladen …',
    ekHsEmpty: 'Mit dieser Muffe ist keine Hausnummer verknüpft.',
    ekHsError: 'Die Hausnummern konnten nicht geladen werden.',
    ekHsErrorForbidden: 'Sie haben keinen Zugriff auf diese Muffe.',
    ekHsErrorAuth: 'Ihre Anmeldung ist abgelaufen. Laden Sie die Seite neu.',
    ekHsGone: 'nicht mehr im Register',
    ekHsNoPoint: 'ohne Standort',
    ekHsElsewhere: 'auch verknüpft mit {oznaka}',
    ekHsWithheld: '{n} in Projekten ohne Zugriff',
    ekHsSpliter: 'Splitter',
    ekHsProjectNone: 'Wählen Sie ein Projekt.',
    ekHsProjectLinked: 'verknüpft: {n}',
    ekHsProjectUnlinked: 'nicht verknüpft: {n}',
    ekHsProjectInView: 'im Kartenausschnitt',
    ekHsProjectNoArea: 'Das Projekt hat kein Gebiet – nicht verknüpfte Adressen werden nicht angezeigt.',
    ekHsProjectCapped: '{n} von {m} angezeigt – zoomen Sie hinein',
    ekHsProjectForbidden: 'Sie haben keinen Zugriff auf die Daten dieses Projekts.',
    zkShare: 'Anteil',
    zkRights: 'Abgeleitete Rechte und Anmerkungen',
    zkMainParcel: 'Hauptliegenschaft',
    zkWarnings: 'Warnungen',
    zkError: 'Der Auszug konnte nicht gelesen werden.',
    popupErrorNoAccessTitle: 'Kein Zugriff auf die Daten',
    popupErrorNoAccessMessage: 'Sie haben keine Berechtigung, diese Ebene anzuzeigen.',
    popupErrorServerTitle: 'Serverfehler',
    popupErrorServerMessage: 'Beim Abrufen der Daten ist ein Fehler aufgetreten.\nVersuchen Sie es erneut oder wenden Sie sich an den Support.',
    popupErrorConnectionTitle: 'Verbindungsfehler',
    popupErrorConnectionMessage: 'Die Daten konnten nicht abgerufen werden.\nPrüfen Sie Ihre Internetverbindung.',
    pageTitleMap: 'Aktivität',
    pageAiOpenAria: `${BRAND.name}-Assistent öffnen`,
    pageErrorTitle: 'Fehler',
    pageMapLoadFailed: 'Die Karte konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
    pageInitFailed: 'Die Anwendung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
    pageBackHome: 'Zurück zur Startseite',
    pageNoAccessTitle: 'Kein Zugriff',
    pageNoAccessBody: 'Sie haben auf keine Anwendung Zugriff. Wenden Sie sich an Ihren Administrator.',
    cadOverlayTitle: 'Zeichnung',
    mapCanvasAria: 'Karte',
    commonRequestAccess: 'Zugriff anfordern',
    basemapButtonAria: 'Grundkarte auswählen',
    bannerSignin: 'Melden Sie sich an, um diese Karte anzuzeigen.',
    bannerSessionExpired: 'Ihre Sitzung ist abgelaufen. Melden Sie sich erneut an, um fortzufahren.',
    bannerForbidden: 'Sie haben keinen Zugriff auf diese Anwendung.',
    bannerForbiddenProject: 'Sie haben keinen Zugriff auf dieses Projekt.',
    bannerNotFoundApp: 'Die Anwendung wurde nicht gefunden. Prüfen Sie den Link.',
    bannerNotFoundProject: 'Das Projekt wurde nicht gefunden. Prüfen Sie den Link.',
    bannerNewVersion: 'Eine neue Version ist verfügbar.',
    bannerReload: 'Aktualisieren',
    userMenuAuthEmailLegacy: 'E-Mail-Konto (alt)',
    userMenuAuthEmail: 'E-Mail-Konto',
    userMenuAuthGoogle: 'Google-Konto',
    userMenuAuthMicrosoft: 'Microsoft-Konto',
    userMenuAuthEmailLink: 'E-Mail-Konto (Anmeldung mit Code)',
    userMenuLogoutFailed: 'Abmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    languageSwitchLabel: 'Sprache',
    languageSwitchBusy: 'Speichern oder schließen Sie zuerst die offene Bearbeitung und wählen Sie dann die Sprache.',
    languageSwitchNoStorage: 'Der Browser erlaubt nicht, die Sprachwahl zu speichern.',
    userMenuLoggingOut: 'Abmeldung läuft...',
    userMenuLogout: 'Abmelden',
    userMenuDashboard: 'Übersicht',
    userMenuProfile: 'Profil',
    userMenuLoading: 'Wird geladen...',
    userMenuUserFallback: 'Benutzer',
    aiAssistantLabel: 'Assistent',
    aiStatusAvailable: 'Verfügbar',
    aiInputPlaceholder: 'Fragen Sie etwas...',
    aiNewChat: 'Neues Gespräch',
    aiCloseAria: 'KI-Assistenten schließen',
    aiSendAria: 'Senden',
    aiLockText: 'Melden Sie sich an, um den KI-Assistenten zu nutzen',
    aiGreeting: 'Guten Tag! Ich bin Ihr KI-Assistent für Geodaten. Wie kann ich Ihnen helfen?',
    aiQuick1Label: 'Gebäude an einer Adresse',
    aiQuick1Question: 'Welches Gebäude steht an der Adresse Prešernov trg 1, Ljubljana?',
    aiQuick2Label: 'Grundstück an einer Adresse',
    aiQuick2Question: 'Welches Grundstück liegt an der Adresse Soča 28, Bovec?',
    aiQuick3Label: 'Umwelteinschränkungen auf einem Grundstück',
    aiQuick3Question: 'Prüfen Sie die Umwelteinschränkungen auf dem Grundstück 63/4, Katastralgemeinde SOČA DESNA (2209).',
    aiQuick4Label: 'Nächster Hydrant',
    aiQuick4Question: 'Welcher Hydrant liegt dem Grundstück 2189/7, Katastralgemeinde VIČ (1723), am nächsten?',
    aiQuickCz1Label: 'Gebäude an einer Adresse',
    aiQuickCz1Question: 'Welches Gebäude steht an der Adresse Staroměstské náměstí 1, Praha?',
    aiQuickCz2Label: 'Grundstück an einer Adresse',
    aiQuickCz2Question: 'Welches Grundstück liegt an der Adresse Karlova 1, Praha?',
    aiQuickCz3Label: 'Grundstück nach Nummer',
    aiQuickCz3Question: 'Wo liegt das Grundstück 1090, Katastralgebiet Staré Město (727024)?',
    aiQuickCz4Label: 'Katastralgebiet',
    aiQuickCz4Question: 'Wo liegt das Katastralgebiet Abertamy?',
    aiThinking: 'Denkt nach…',
    aiProcessing: 'Wird bearbeitet…',
    aiErrorOverloaded: 'Der KI-Dienst ist gerade überlastet. Versuchen Sie es in einigen Sekunden erneut.',
    aiErrorNoResponse: 'Der KI-Dienst hat nicht geantwortet. Versuchen Sie es erneut.',
    aiErrorGeneric: 'Es ist ein Fehler aufgetreten. Versuchen Sie es erneut.',
    aiErrorInterrupted: 'Die Antwort wurde während des Nachdenkens abgebrochen. Versuchen Sie es erneut.',
    aiErrorEmpty: 'Es kam keine Antwort. Der KI-Dienst ist möglicherweise überlastet.',
    aiErrorComm: 'Die Kommunikation mit der KI ist fehlgeschlagen.',
    aiErrorMonthlyLimit: 'Das monatliche KI-Nutzungslimit ist erreicht. Versuchen Sie es nächsten Monat erneut.',
    aiRetry: 'Erneut versuchen',
    aiToolSearchParcel: 'Grundstück wird gesucht…',
    aiToolSearchAddress: 'Adresse wird gesucht…',
    aiToolSearchPlace: 'Ort wird gesucht…',
    aiToolGetParcelInfo: 'Grundstücksdaten werden abgerufen…',
    aiToolFindOwner: 'Eigentümer wird gesucht…',
    aiToolSearchParcelsByOwner: 'Grundstücke nach Eigentümer werden gesucht…',
    aiToolGetBuildingInfo: 'Gebäudedaten werden abgerufen…',
    aiToolGetInfrastructureInfo: 'Infrastrukturdaten werden abgerufen…',
    aiToolFindParcelsNear: 'Grundstücke in der Nähe werden gesucht…',
    aiToolFindInfrastructureNear: 'Infrastruktur in der Nähe wird gesucht…',
    aiToolSearchEkFeatures: 'EK-Objekte werden gesucht…',
    aiToolGetRouteInfo: 'Trassendaten werden abgerufen…',
    aiToolGetCableInfo: 'Kabeldaten werden abgerufen…',
    aiToolGetSpliceInfo: 'Muffendaten werden abgerufen…',
    aiToolFindEkFeaturesNear: 'EK-Objekte in der Nähe werden gesucht…',
    aiToolGetProjectEkSummary: 'Projektzusammenfassung wird abgerufen…',
    aiToolFindHouseConnectionsNear: 'Hausanschlüsse werden gesucht…',
    aiToolGetRoutePipes: 'Rohre der Trasse werden abgerufen…',
    aiToolGetRouteCables: 'Kabel der Trasse werden abgerufen…',
    aiToolGetSpliceCables: 'Kabel der Muffe werden abgerufen…',
    aiToolGetCableFibers: 'Fasern des Kabels werden abgerufen…',
    aiToolGetCableReserves: 'Kabelreserven werden abgerufen…',
    aiToolLocateCableFault: 'Fehlerstelle wird ermittelt…',
    aiToolQueryEkLengths: 'Längen des EK-Netzes werden berechnet…',
    aiToolSearchLayers: 'Ebenen werden gesucht…',
    aiToolGetLayerSchema: 'Schema der Ebene wird abgerufen…',
    aiToolGetAppMapCatalog: 'Ebenenkatalog wird abgerufen…',
    aiToolGetEnvironmentalConstraints: 'Umwelteinschränkungen werden geprüft…',
    aiToolGetEnvironmentalConstraintsForParcel: 'Einschränkungen für das Grundstück werden geprüft…',
    aiToolFindArsoFeaturesNear: 'ARSO-Objekte in der Nähe werden gesucht…',
    aiToolGetParcelZoning: 'Flächenwidmung wird geprüft…',
    aiToolGetParcelConditions: 'Bebauungsbestimmungen aus der Verordnung werden gelesen…',
    aiToolListProjectOpinions: 'Stellungnahmen werden gelesen…',
    aiToolReadOpinionDocument: 'Dokument der Stellungnahme wird gelesen…',
    aiToolGetGraditevConstraints: 'Einschränkungen im Gebiet werden geprüft…',
    aiToolGetGraditevConstraintDetails: 'Einschränkung und Auszüge aus Vorschriften werden gelesen…',
    aiToolListGraditevReports: 'Technische Berichte werden gesucht…',
    aiToolReadGraditevReport: 'Technischer Bericht wird gelesen…',
    aiToolFindInLaw: 'Text der Vorschriften wird durchsucht…',
    aiToolGetProjectConsentSummary: 'Zustimmungen der Grundstücke werden geprüft…',
    aiToolGetRouteConsentStatus: 'Zustimmungen entlang der Trasse werden geprüft…',
    aiToolFindParcelsMissingConsent: 'Grundstücke ohne Zustimmung werden gesucht…',
    aiToolControlMapLayers: 'Ebenen werden umgeschaltet…',
    aiToolSwitchBasemap: 'Grundkarte wird gewechselt…',
    aiToolToggleSpecialView: 'Ansicht wird umgeschaltet…',
    ctxSearchAria: 'Kontextsuche',
    ctxRecent: 'Zuletzt verwendet',
    ctxOtherApps: 'Andere Anwendungen',
    ctxNoResults: 'Keine Treffer',
    ctxNoResultsAlt: 'Keine Ergebnisse',
    ctxApps: 'Anwendungen',
    ctxAppsCount: 'Anwendungen ({n})',
    ctxProjects: 'Projekte',
    ctxCurrentApp: 'Aktuelle Anwendung',
    ctxCurrentAppSr: '(aktuelle Anwendung)',
    ctxSwitchTitle: 'Kontext wechseln',
    ctxSearchPlaceholder: 'Suche...',
    ctxAllApps: 'Alle Anwendungen',
    ctxLoading: 'Wird geladen...',
    ctxProjectsOfCount: '{n} von {total} Projekten',
    ctxFindProject: 'Projekt suchen …',
    ctxFindApp: 'Anwendung suchen …',
    ctxDashboardLink: 'Übersicht →',
    ctxHints: '↑↓ bewegen · Enter auswählen · Esc schließen',
    ctxHintsRequired: '↑↓ bewegen · Enter auswählen',
    ctxLoadingProjects: 'Projekte werden geladen …',
    ctxNoProjects: 'Keine Projekte',
    ctxRequiredNote: 'Wählen Sie ein Projekt, um seine Daten anzuzeigen. Sie haben Zugriff auf einzelne Projekte, nicht auf die gesamte Anwendung.',
    ctxOtherProjectsTitle: 'Weitere Projekte in dieser Anwendung',
    ctxNoAccessNote: 'Auf diese Projekte haben Sie keinen Zugriff.',
    ctxUnnamed: '(ohne Namen)',
    ctxLastOpened: 'zuletzt geöffnet',
    ctxProjectChoiceAria: 'Projektauswahl',
    ctxDeniedAppNote: 'Sie haben keinen Zugriff auf die Anwendung »{app}«. Die für Sie verfügbaren Anwendungen sind unten aufgeführt.',
    appPanelPublicPill: 'Öffentlich',
    appPanelExplore: 'Erkunden',
    appPanelFindParcel: 'Grundstück suchen',
    appPanelFindAddress: 'Adresse suchen',
    appPanelOrgQuestion: 'Gehören Sie zu einer Organisation?',
    appPanelLoginBody: 'Melden Sie sich an, um Ihre Anwendungen, Projekte und die Daten Ihres Netzes zu öffnen.',
    appPanelNetwork: 'Netz',
    appPanelNetworkProject: 'Netz des Projekts',
    appPanelRecent: 'Letzte Aktivitäten',
    appPanelAll: 'Alle →',
    appPanelDashboard: 'Übersicht',
    appPanelMyApps: 'Meine Anwendungen',
    appPanelProject: 'Projekt',
    appPanelNow: 'jetzt',
    appPanelMinAgo: 'vor {n} Min.',
    appPanelHoursAgo: 'vor {n} Std.',
    appPanelYesterday: 'gestern',
    appPanelDaysAgo: 'vor {n} Tagen',
    appPanelProjectsOne: 'Projekt',
    appPanelProjectsTwo: 'Projekte',
    appPanelProjectsFew: 'Projekte',
    appPanelProjectsMany: 'Projekte',
    appPanelActivityCount: 'Aktivitäten',
    appPanelLastActivity: 'letzte Aktivität',
    appPanelPostFallback: 'Beitrag',
    appPanelReopened: 'Wieder geöffnet',
    activityNew: 'Neu',
    activityEdited: 'Aktualisiert',
    activityCommented: 'Kommentar',
    activityResolved: 'Gelöst',
    activityUnresolved: 'Ungelöst',
    activityAssigned: 'Zugewiesen',
    activityReacted: 'Reaktion',
    postTypeNote: 'Notiz',
    postTypeIssue: 'Aufgabe',
    postTypeInspection: 'Inspektion',
    postStatusOpen: 'Offen',
    postNoTitle: 'ohne Titel',
    postPhotoUnit: 'Fotos',
    layersPanelShowAll: 'Alle anzeigen',
    layersPanelGjiKinds: 'GJI-Arten',
    layerControlsEmpty: 'Keine Ebenen zum Anzeigen',
    popupFooterLayer: 'Ebene',
    popupFeatureFallback: 'Objekt {index}',
    popupFeatureTitleFallback: 'Objekt',
    measureUndoAria: 'Letzten Messpunkt entfernen',
    measureUndoTitle: 'Letzten Punkt entfernen (oder Rechtsklick auf die Karte)',
    locateUnsupported: 'Ihr Browser unterstützt keine Standortbestimmung.',
    locateGeneric: 'Ihr Standort konnte nicht ermittelt werden.',
    locatePermissionDenied: 'Der Zugriff auf den Standort wurde verweigert. Erlauben Sie ihn in den Browsereinstellungen.',
    locateUnavailable: 'Standortdaten sind nicht verfügbar.',
    locateTimeout: 'Zeitüberschreitung bei der Standortbestimmung. Bitte versuchen Sie es erneut.',
    locateTimeoutSearching: 'Zeitüberschreitung bei der Standortbestimmung. Suche läuft weiter…',
    locateUnavailableSearching: 'Standortdaten sind nicht verfügbar. Suche läuft weiter…',
    pageTitleLogin: 'Anmeldung',
    pageTitleRegister: 'Registrierung',
    pageTitleVerifyEmail: 'E-Mail-Bestätigung',
    pageTitleForgotPassword: 'Passwort vergessen',
    pageTitleResetPassword: 'Passwort zurücksetzen',
    pageTitleLoginLink: 'Anmeldung',
    pageTitleDeviceLogin: 'Geräteanmeldung',
    pageTitleLogout: 'Abmeldung',
    pageTitleDashboard: 'Übersicht',
    pageTitleProfile: 'Profil',
    pageTitleCommits: '{name} · Änderungsverlauf',
    pageTitleRequestAccess: 'Zugriffsanfrage - {name}',
    pageTitleLimitedAccess: 'Eingeschränkter Zugriff',
    pageTitleLimitedAccessFor: 'Eingeschränkter Zugriff - {name}',
    pageTitleNoAccess: 'Kein Zugriff - {name}',
    pageTitleInvite: 'Einladung · {name}',
    pageTitleInvitePlain: 'Einladung',
    logoutLoading: 'Wird geladen...',
    logoutTitle: 'Abmelden',
    logoutButton: 'Abmelden',
    logoutWorking: 'Abmeldung läuft...',
    logoutCancel: 'Abbrechen',
    logoutGoToLogin: 'Zur Anmeldung',
    logoutNotSignedIn: 'Sie sind nicht angemeldet.',
    logoutLoadFailed: 'Die Benutzerdaten konnten nicht geladen werden.',
    relTimeNow: 'gerade eben',
    relTimeYesterday: 'gestern',
    relTimeSecondsOne: 'vor {n} Sekunde',
    relTimeSecondsTwo: 'vor {n} Sekunden',
    relTimeSecondsMany: 'vor {n} Sekunden',
    relTimeMinutesOne: 'vor {n} Minute',
    relTimeMinutesTwo: 'vor {n} Minuten',
    relTimeMinutesMany: 'vor {n} Minuten',
    relTimeHoursOne: 'vor {n} Stunde',
    relTimeHoursTwo: 'vor {n} Stunden',
    relTimeHoursMany: 'vor {n} Stunden',
    relTimeDaysOne: 'vor {n} Tag',
    relTimeDaysTwo: 'vor {n} Tagen',
    relTimeDaysMany: 'vor {n} Tagen',
    relTimeWeeksOne: 'vor {n} Woche',
    relTimeWeeksTwo: 'vor {n} Wochen',
    relTimeWeeksMany: 'vor {n} Wochen',
    relTimeMonthsOne: 'vor {n} Monat',
    relTimeMonthsTwo: 'vor {n} Monaten',
    relTimeMonthsMany: 'vor {n} Monaten',
    relTimeYearsOne: 'vor {n} Jahr',
    relTimeYearsTwo: 'vor {n} Jahren',
    relTimeYearsMany: 'vor {n} Jahren',
    dashWelcome: 'Willkommen, {name}',
    dashContinue: 'Weiterarbeiten',
    dashPublicMap: 'Öffentliche Karte',
    dashPublicMapBody: 'Grundstücke, Gebäude, Adressen und Geodaten für das ganze Land.',
    dashOpenPublicMap: 'Karte öffnen',
    dashLayerDocs: 'Dokumentation der Ebenen',
    dashCountrySlovenia: 'Slowenien',
    dashCountryCzechia: 'Tschechien',
    dashMapStatLayers: 'Ebenen auf der Karte',
    dashMapStatThemes: 'Themen',
    dashMapStatPublishers: 'Herausgeber',
    dashMapOnMap: 'Was die Karte zeigt',
    dashMapCatalogue: 'Ebenenkatalog',
    dashMapShowAllThemes: 'Alle Themen anzeigen ({n})',
    dashMapColData: 'Daten',
    dashMapColSource: 'Quelldatum',
    dashMapSources: 'Quellen und Lizenzen',
    dashMapPublishers: 'Herausgeber',
    dashMapStatParcels: 'Grundstücke, Stand vom',
    dashMapCountsNote: 'Gezählt werden die Ebenen, die diese Karte zeigt. Die Ebenendokumentation umfasst auch die Ebenen für KingCAD und Tschechien.',
    dashMapWeekly: 'Wöchentlich aktualisierte Daten',
    dashMapColOnMap: 'Auf der Karte',
    dashMapAdmin: 'Administrator',
    dashMapSummaryFailed: 'Die Angaben zu den Ebenen können derzeit nicht angezeigt werden.',
    dashAppsCount: '{n} Anwendungen',
    dashProjectsCount: '{n} Projekte',
    dashRecentlyVisited: 'Zuletzt besucht',
    dashOpenMap: 'Karte öffnen',
    dashShowLess: 'Weniger anzeigen',
    dashShowMore: 'Mehr anzeigen',
    dashShowMoreN: 'Mehr anzeigen ({n})',
    dashShowAllN: 'Alle anzeigen ({n})',
    dashShowAllMore: 'Alle anzeigen ({n} weitere)',
    dashApps: 'Anwendungen',
    dashSearchApps: 'Anwendungen suchen...',
    dashProjAbbrev: '{n} Proj.',
    dashRecentActivity: 'Letzte Aktivitäten',
    dashAllApps: 'Alle Anwendungen',
    dashNoRecentActivity: 'Keine Aktivitäten in letzter Zeit.',
    dashHome: 'Start',
    dashMap: 'Karte',
    dashSettings: 'Einstellungen',
    dashSettingsAdminTitle: 'Anwendungseinstellungen (Administrator)',
    dashReports: 'Berichte',
    dashSluznosti: 'Dienstbarkeiten',
    dashSluznostiTitle: 'Dienstbarkeitsregister (Grundstücke, Eigentümer, Verträge)',
    dashReportsWriteTitle: 'Berichte (Schreibzugriff)',
    dashProjectSettingsTitle: 'Projekteinstellungen (Administrator)',
    dashWorks: 'Bauvorhaben',
    dashWorksNew: 'Neues Bauvorhaben',
    dashWorksNewTitle: 'Neues Bauvorhaben — Namen eingeben, den Rest Schritt für Schritt ergänzen',
    dashWorksEmpty: 'Noch keine Bauvorhaben. Beginnen Sie mit »Neues Bauvorhaben« — Namen eingeben, den Rest Schritt für Schritt ergänzen.',
    dashWorksOpinions: 'Stellungnahmen {done}/{total}',
    dashParcelsCount: '{n} Grundstücke',
    dashProjects: 'Projekte',
    dashNewProject: 'Neues Projekt',
    dashSearchProjects: 'Projekte suchen...',
    dashNoProjects: 'Keine Projekte.',
    dashUpdated: 'Aktualisiert {when}',
    dashProjectName: 'Projektname',
    dashProjectNamePlaceholder: 'z. B. Neue Siedlung Polje',
    dashProjectKind: 'Projektart',
    dashDescription: 'Beschreibung',
    dashOptional: '(optional)',
    dashDescriptionPlaceholder: 'Kurze Projektbeschreibung',
    dashCancel: 'Abbrechen',
    dashCreateProject: 'Projekt erstellen',
    dashCreating: 'Wird erstellt...',
    dashActivity: 'Aktivität',
    dashFilterAll: 'Alle',
    dashFilterTasks: 'Aufgaben',
    dashFilterPhotos: 'Fotos',
    dashFilterComments: 'Kommentare',
    dashFilterOpinions: 'Stellungnahmen',
    dashLatestChanges: 'Letzte Änderungen',
    dashAllChanges: 'Alle Änderungen',
    dashNoEdits30: 'Keine Bearbeitungen in den letzten 30 Tagen',
    dashEditProject: 'Projekt bearbeiten',
    dashDrawOnMap: 'Projekt auf der gemeinsamen Karte der Anwendung anzeigen',
    dashDrawOnMapHint: 'Wenn ausgeschaltet, wird das Projekt nicht auf der gemeinsamen Karte mit allen Projekten gezeichnet. Es bleibt in der Liste, Sie können es einzeln öffnen, und in der Desktop-Anwendung (KingCAD) ändert sich nichts.',
    dashSave: 'Speichern',
    dashSaving: 'Wird gespeichert...',
    dashShowOnMap: 'Auf der Karte anzeigen',
    dashElaboratTitle: 'Objekte für den GJI-Bericht',
    dashElaboratSubtitle: 'In Betrieb ohne GURS-Kennung, Eigentümer GVO (laut Codeliste) oder ohne Eigentümer · insgesamt {n}',
    dashElaboratAllOk: 'Alle von GVO erfassten Objekte in Betrieb haben eine GURS-Kennung',
    dashReportLink: 'Bericht →',
    dashOpinionsTitle: 'Stellungnahmen / Zustimmungen',
    dashEditing: 'Bearbeiten →',
    dashNoOpinions: 'Keine Stellungnahmen für dieses Projekt.',
    dashDocsAbbrev: '{n} Dok.',
    dashNoDocs: 'Keine Dokumente.',
    dashTagTask: 'Aufgabe',
    dashTagNote: 'Notiz',
    dashTagInspection: 'Inspektion',
    dashUsersOne: '{n} Benutzer',
    dashUsersTwo: '{n} Benutzer',
    dashUsersFew: '{n} Benutzer',
    dashUsersMany: '{n} Benutzer',
    dashNever: 'nie',
    dashUnknownUser: 'Unbekannter Benutzer',
    dashUnknownUserAlt: 'Unbekannter Benutzer',
    dashSectionToday: 'Heute',
    dashSectionWeek: 'Letzte 7 Tage',
    dashSectionMonth: 'Letzte 30 Tage',
    dashChangesOne: '{n} Änderung',
    dashChangesTwo: '{n} Änderungen',
    dashChangesFew: '{n} Änderungen',
    dashChangesMany: '{n} Änderungen',
    dashCommitOne: '{n} Commit',
    dashCommitMany: '{n} Commits',
    dashAndOtherOne: ' und {n} weitere Person',
    dashAndOthers: ' und {n} weitere',
    dashActiveProjectsZero: '0 Projekte in den letzten 30 Tagen aktiv',
    dashActiveProjectsOne: '1 Projekt in den letzten 30 Tagen aktiv',
    dashActiveProjectsTwo: '2 Projekte in den letzten 30 Tagen aktiv',
    dashActiveProjectsFew: '{n} Projekte in den letzten 30 Tagen aktiv',
    dashActiveProjectsMany: '{n} Projekte in den letzten 30 Tagen aktiv',
    dashOpinionsSummary: '{yes} von {total} erhalten',
    dashStageConditionsRequest: 'Antrag auf Bedingungen',
    dashStageConditions: 'Bedingungen',
    dashStageOpinionRequest: 'Antrag auf Stellungnahme',
    dashStageOpinion: 'Stellungnahme',
    dashStatusObtained: 'Erhalten',
    dashStatusRejected: 'Abgelehnt',
    dashStatusPending: 'In Bearbeitung',
    dashRequestFailed: 'Die Anfrage ist fehlgeschlagen.',
    dashErrorStatus: 'Fehler ({status})',
    profileEditName: 'Namen bearbeiten',
    profileFirstName: 'Vorname',
    profileLastName: 'Nachname',
    profileMemberSince: 'Mitglied seit {date}',
    profileAppsOne: '{n} Anwendung',
    profileAppsTwo: '{n} Anwendungen',
    profileAppsFew: '{n} Anwendungen',
    profileAppsMany: '{n} Anwendungen',
    profileAdminPanel: 'Administrationsbereich',
    profileDesktopPasswordTitle: 'Passwort für ältere Programmversionen',
    profileDesktopPasswordBody: 'Für die Anmeldung brauchen Sie es nicht. Legen Sie nur dann eines fest, wenn Sie eine ältere Programmversion verwenden (vor 27.2).',
    profilePasswordChangeSignsOut: 'Nachdem Sie das Passwort festgelegt oder geändert haben, werden Sie auf allen Geräten abgemeldet und müssen sich erneut anmelden.',
    profileSendPasswordLink: 'Link zum Festlegen eines Passworts senden',
    profileSendPasswordChangeLink: 'Link zum Ändern des Passworts senden',
    profileSending: 'Wird gesendet ...',
    profilePasswordLinkSent: 'Wir haben den Link an {email} gesendet. Öffnen Sie die Nachricht und folgen Sie den Anweisungen.',
    profileDevices: 'Meine Geräte',
    profileDevicesOne: '{n} Gerät',
    profileDevicesTwo: '{n} Geräte',
    profileDevicesFew: '{n} Geräte',
    profileDevicesMany: '{n} Geräte',
    profileNoDevices: 'Derzeit ist kein Computer mit KingCAD mit diesem Konto angemeldet.',
    profileDeviceLabel: 'Gerät {code}',
    profileLastUsed: 'zuletzt verwendet',
    profileDevicesError: 'Die Geräteliste konnte nicht geladen werden.',
    profileSignOutAll: 'Mich überall abmelden',
    profileSignOutAllConfirm: 'Alle Geräte werden abgemeldet; auf jedem müssen Sie sich erneut über den Browser anmelden. Fortfahren?',
    profileSignOutAllYes: 'Ja, überall abmelden',
    profileSigningOut: 'Wird abgemeldet …',
    profileSignedOutAll: 'Alle Geräte sind abgemeldet.',
    profileSignOutAllNeedsBrowser: 'Melden Sie sich für diese Aktion im Browser an (Passwort, Google oder E-Mail-Link).',
    profileDevicesIntro: 'Computer mit KingCAD, die mit diesem Konto angemeldet sind. Das Abmelden eines Computers entfernt ihn nicht aus der Liste — das macht »Entfernen«.',
    profileDevicesPrivacyLink: 'Welche Daten wir erfassen und wie lange',
    profileDevicesLimit: 'Sie verwenden {count} von {cap} Geräten.',
    profileDeviceRemove: 'Entfernen',
    profileDeviceRemoveConfirm: 'Das Gerät {name} aus der Liste entfernen? Sie müssen sich darauf erneut anmelden, und zwar erst nach der Wartezeit.',
    profileDeviceRemoveYes: 'Ja, entfernen',
    profileDeviceRemoving: 'Wird entfernt …',
    profileDeviceRemoved: 'Das Gerät {name} wurde entfernt.',
    profileDeviceRemoveCapped: 'Die in diesem Monat erlaubte Anzahl an Geräteentfernungen ist erreicht.',
    profileDeviceRemoveFailed: 'Das Gerät konnte nicht entfernt werden. Versuchen Sie es erneut.',
    profileNoApps: 'Sie haben auf keine Anwendung Zugriff',
    profileConnectionError: 'Verbindungsfehler. Bitte versuchen Sie es erneut.',
    profileTooMany: 'Zu viele Anfragen. Warten Sie eine Minute und versuchen Sie es erneut.',
    profileSendFailed: 'Senden fehlgeschlagen. Bitte versuchen Sie es erneut.',
    profileSaveFailed: 'Speichern fehlgeschlagen.',
    profileConnectionFailed: 'Verbindungsfehler.',
    profileLoadAppsFailed: 'Die Anwendungen konnten nicht geladen werden.',
    profileDateUnknown: 'k. A.',
    commitsCrumb: 'Änderungsverlauf',
    commitsSearchUsers: 'Benutzer suchen...',
    commitsOn: 'Commits vom {date}',
    commitsInserted: '+{n} eingefügt',
    commitsUpdated: '~{n} aktualisiert',
    commitsDeleted: '-{n} gelöscht',
    commitsNoChanges: 'Keine Änderungen',
    commitsPageInfo: 'Seite {page} von {pages} · insgesamt {total} Commits',
    commitsContributors: 'Mitwirkende',
    commitsMissingParam: 'Parameter sxapp oder sxproject fehlt',
    commitsAppInfoError: 'Die Angaben zur Anwendung konnten nicht geladen werden',
    commitsProjectNotFound: 'Projekt nicht gefunden',
    commitsProjectInfoError: 'Die Angaben zum Projekt konnten nicht geladen werden',
    commitsLoadError: 'Der Commit-Verlauf konnte nicht geladen werden',
    commitsDateTime: '{date} um {time}',
    commitsAllUsers: 'Alle Benutzer',
    commitsAllDates: 'Alle Zeiträume',
    commitsWeekdays: 'Mo,Di,Mi,Do,Fr,Sa,So',
    commitsMonths: 'Januar,Februar,März,April,Mai,Juni,Juli,August,September,Oktober,November,Dezember',
    accessRequestTitle: 'Zugriffsanfrage',
    accessNoAccessTo: 'Sie haben keinen Zugriff auf:',
    accessMessageLabel: 'Nachricht an den Administrator',
    accessMessagePlaceholder: 'Schreiben Sie kurz, wer Sie sind und warum Sie Zugriff benötigen.',
    accessSend: 'Anfrage senden',
    accessSending: 'Wird gesendet...',
    accessSentTitle: 'Anfrage gesendet',
    accessSentBody: 'Der Administrator prüft Ihre Anfrage und benachrichtigt Sie per E-Mail.',
    accessAlreadyRequested: 'Eine Zugriffsanfrage wurde bereits gesendet.',
    accessSubmitFailed: 'Die Anfrage konnte nicht gesendet werden.',
    accessLimitedTitle: 'Eingeschränkter Zugriff',
    accessLimitedBody: 'Sie haben keinen Zugriff auf die gesamte Anwendung:',
    accessLoading: 'Wird geladen...',
    accessYouHave: 'Sie haben Zugriff auf {projects}:',
    accessNoProjects: 'Sie haben auf kein Projekt in dieser Anwendung Zugriff.',
    accessAppMissing: 'Die Anwendung existiert nicht.',
    accessProjectsLoadError: 'Die Projekte konnten nicht geladen werden.',
    accessProjectsZero: 'kein Projekt',
    accessContactAdmin: 'Für den Zugriff wenden Sie sich an einen Administrator:',
    accessContactOrgAdmin: 'Für den Zugriff wenden Sie sich an den Administrator der Organisation.',
    accessCheckFailed: 'Der Zugriff konnte nicht geprüft werden. Wenden Sie sich an den Support.',
    accessRequestFull: 'Vollen Zugriff anfordern',
    popupNoProperties: 'Keine Eigenschaften vorhanden',
    popupErrorSessionTitle: 'Anmeldung ungültig',
    popupErrorSessionMessage: 'Melden Sie sich erneut an. Wenn das Problem bestehen bleibt, wenden Sie sich an den Support.',
    popupNoData: 'Keine Angabe',
    commonYes: 'Ja',
    commonNo: 'Nein',
    measureUndoLabel: 'Schritt zurück',
    inviteNotFound: 'Einladung nicht gefunden',
    inviteNotFoundBody: 'Der Einladungslink ist möglicherweise falsch, oder die Einladung wurde gelöscht.',
    inviteNoLongerValid: 'Einladung nicht mehr gültig',
    inviteAccepted: 'Diese Einladung wurde bereits angenommen.',
    inviteCancelled: 'Diese Einladung wurde widerrufen.',
    inviteExpired: 'Diese Einladung ist abgelaufen.',
    inviteAskNew: 'Bitten Sie den Administrator, eine neue Einladung zu senden.',
    inviteTitle: 'Einladung zu einer Anwendung',
    inviteOrg: 'Organisation: {org}',
    inviteFrom: 'Eingeladen von:',
    inviteForEmail: 'Für die E-Mail-Adresse:',
    inviteValidUntil: 'Gültig bis:',
    inviteSignInOrRegister: 'Um die Einladung anzunehmen, erstellen Sie ein Konto mit dieser E-Mail-Adresse. Ein Passwort ist nicht nötig. Wenn Sie bereits ein Konto haben, melden Sie sich an.',
    inviteCreateAccount: 'Konto erstellen',
    inviteSignedInAs: 'Sie sind angemeldet als {email}. Klicken Sie auf die Schaltfläche, um die Einladung anzunehmen.',
    inviteAccept: 'Einladung annehmen',
    inviteAccepting: 'Wird angenommen…',
    inviteWrongAccount: 'Sie sind angemeldet als {current}, die Einladung gilt aber für {invited}. Melden Sie sich ab und mit der Adresse an, an die die Einladung gesendet wurde.',
    inviteSignOut: 'Abmelden',
    inviteErr401: 'Ihre Anmeldung ist ungültig oder das Konto kann nicht verwendet werden.',
    inviteErr403: 'Diese Einladung wurde für eine andere E-Mail-Adresse ausgestellt.',
    inviteErr404: 'Die Einladung wurde nicht gefunden.',
    inviteErr409: 'Diese Einladung ist nicht mehr gültig.',
    inviteErrGeneric: 'Die Einladung konnte nicht angenommen werden. Versuchen Sie es erneut oder informieren Sie den Administrator.',
    inviteInvalidPath: 'Ungültiger Pfad.',
    inviteLoadFailed: 'Die Einladung konnte nicht geladen werden.',
  },

  cs: {
    // Auth page - Tabs
    login: 'Přihlášení',
    register: 'Registrace',

    // Auth page - Form fields
    email: 'E-mail',
    password: 'Heslo',
    firstName: 'Jméno',
    lastName: 'Příjmení',

    // Auth page - Placeholders
    emailPlaceholder: 'vas.email@priklad.cz',
    passwordPlaceholder: 'Zadejte heslo',
    firstNamePlaceholder: 'Jan',
    lastNamePlaceholder: 'Novák',

    // Auth page - Buttons
    loginButton: 'Přihlásit se',
    createAccount: 'Vytvořit účet',
    creatingAccount: 'Vytvářím účet...',
    forgotPassword: 'Zapomenuté heslo?',
    orContinueWith: 'nebo pokračujte přes',

    // OAuth buttons
    continueWithGoogle: 'Pokračovat přes Google',
    continueWithMicrosoft: 'Pokračovat přes Microsoft',

    // Email verification status
    // #569 desktop browser login + email login link
    deviceLoginTitle: 'Přihlášení zařízení KingCAD',
    deviceLoginTitleProduct: 'Přihlášení do {product}',
    deviceLoginLoading: 'Načítám požadavek na přihlášení…',
    deviceLoginSignedInAs: 'Jste přihlášeni jako',
    deviceLoginQuestion: 'Povolit přihlášení do {product} na tomto zařízení?',
    deviceLoginMachine: 'Zařízení',
    deviceLoginWindowsUser: 'Uživatel Windows',
    deviceLoginProgram: 'Program',
    deviceLoginRemote: 'vzdálená relace',
    deviceLoginExpiresIn: 'Požadavek vyprší za {min} min.',
    deviceLoginAllow: 'Povolit',
    deviceLoginApproving: 'Potvrzuji…',
    deviceLoginNotYou: 'Nejste to vy? Přihlásit se jiným účtem',
    // #667 G16/G17: the waiting screen after »Povolit«, the honest message when
    // the program never collected the approval, the footer switch, the one-day rule
    deviceLoginSwitchAccount: 'Přihlásit se jiným účtem',
    deviceLoginApprovedWaiting: 'Povoleno. Čekám na program …',
    deviceLoginNotCollected: 'Program přihlášení nepřevzal. V programu stiskněte tlačítko znovu a nechte okno otevřené.',
    deviceLoginHintFresh: 'Tip: pokud se přihlašujete přes Google nebo e-mailem, tento kód nepotřebujete.',
    deviceLoginCancel: 'Zrušit',
    deviceLoginDone: 'Hotovo — vraťte se do {product}.',
    deviceLoginDoneClose: 'Tuto kartu můžete zavřít.',
    deviceLoginExpired: 'Požadavek vypršel. V programu znovu klikněte na „Přihlásit v prohlížeči“.',
    deviceLoginDenied: 'Přihlášení bylo zrušeno.',
    deviceLoginConsumed: 'Tento požadavek už byl použit.',
    deviceLoginNotFound: 'Neznámý požadavek na přihlášení.',
    deviceLoginError: 'Došlo k chybě. Zkuste to znovu.',
    deviceLoginBackHome: 'Na úvodní stránku',
    // #580 mailbox gate
    deviceConfirmNeeded: 'Pro přidání nového zařízení potvrďte přístup k e-mailu účtu.',
    deviceConfirmSend: 'Poslat potvrzovací kód',
    deviceConfirmSending: 'Odesílám…',
    deviceConfirmSentTo: 'Kód odeslán na {email}. Zkontrolujte poštu (i spam).',
    deviceConfirmCodeLabel: 'Zadejte 6místný kód z e-mailu',
    deviceConfirmSubmit: 'Potvrdit kód',
    deviceConfirmChecking: 'Ověřuji…',
    deviceConfirmResend: 'Poslat znovu',
    deviceConfirmExpiresIn: 'Kód vyprší za {min} min.',
    deviceConfirmWrong: 'Nesprávný kód. Zkuste to znovu.',
    deviceConfirmTooMany: 'Příliš mnoho pokusů. Použijte odkaz z e-mailu nebo v programu začněte nové přihlášení.',
    deviceConfirmExpiredCode: 'Kód vypršel. Pošlete nový.',
    deviceConfirmCooldown: 'Před dalším odesláním chvíli počkejte.',
    deviceConfirmCapped: 'Limit odeslání byl dosažen. Zkuste to později nebo se přihlaste přes Google nebo e-mailem.',
    deviceConfirmSendFailed: 'Odeslání e-mailu se nezdařilo. Zkuste to znovu.',
    deviceConfirmProven: 'E-mail potvrzen — přihlášení můžete povolit.',
    deviceLinkQuestion: 'Povolit přihlášení do {product} pro účet {email}?',
    deviceLinkConfirm: 'Potvrdit zařízení',
    deviceLinkClose: 'Zavřít',
    deviceLinkInvalid: 'Odkaz není platný nebo vypršel. V programu si vyžádejte nové přihlášení.',
    // #791 the device list on the approval page (block mode only)
    deviceLimitTitle: 'Seznam zařízení je plný',
    deviceLimitBody: 'Tímto účtem je už přihlášeno {count} zařízení (nejvýše {cap}). Odeberte jedno, abyste povolili přihlášení na tomto.',
    deviceLimitCooldown: 'Toto zařízení jste nedávno odebrali. Znovu je můžete přidat {date}.',
    deviceLimitRemove: 'Odebrat',
    deviceLimitRemoving: 'Odebírám…',
    deviceLimitRemoved: 'Zařízení bylo odebráno.',
    deviceLimitFreed: 'Místo je volné — přihlášení můžete povolit.',
    deviceLimitRemoveFailed: 'Zařízení se nepodařilo odebrat. Zkuste to znovu.',
    deviceLimitRefreshFailed: 'Zařízení bylo odebráno, ale stav se nepodařilo obnovit. Zkuste to znovu.',
    deviceLimitRetry: 'Zkusit znovu',
    deviceLimitRemoveCapped: 'Povolený počet odebrání zařízení v tomto měsíci byl dosažen.',
    deviceLimitDeviceLine: '{name} · naposledy {date}',
    deviceLinkLimited: 'Tímto účtem je už přihlášen nejvyšší povolený počet zařízení. Přihlaste se v prohlížeči a odeberte jedno zařízení; toto přihlášení pak bude možné.',
    deviceLinkLimitedCooldown: 'Toto zařízení jste nedávno odebrali; znovu je můžete přidat {date}.',
    deviceLinkManage: 'Přihlásit se a spravovat zařízení',
    deviceSlowdownTitle: 'Časté přihlašování na různých počítačích',
    deviceSlowdownCode: 'Kvůli častému přihlašování na různých počítačích bude kód odeslán v {time}.',
    deviceSlowdownApprove: 'Kvůli častému přihlašování na různých počítačích bude přihlášení možné v {time}.',
    deviceSlowdownExpires: 'Tento požadavek na přihlášení do té doby vyprší — v tu dobu jej v programu spusťte znovu.',
    deviceSlowdownNow: 'Nyní můžete pokračovat.',
    deviceLinkSlowdown: 'Kvůli častému přihlašování na různých počítačích bude přihlášení možné v {time}. Pokud tento požadavek mezitím vyprší, spusťte jej v programu znovu.',
    loginLinkRedeeming: 'Přihlašuji…',
    loginLinkSuccess: 'Jste přihlášeni',
    loginLinkContinue: 'Pokračovat',
    loginLinkFailed: 'Odkaz je neplatný nebo vypršel',
    loginLinkFailedHint: 'Odkaz platí 15 minut a lze jej použít pouze jednou.',
    loginLinkRequestNew: 'Zpět na přihlášení',
    loginLinkOption: 'Poslat mi kód pro přihlášení',
    loginLinkSending: 'Odesílám…',
    loginLinkSent: 'Pokud účet existuje, poslali jsme kód na {email}. Zkontrolujte poštu.',
    loginLinkNeedEmail: 'Nejprve zadejte e-mail.',
    loginLinkError: 'Odeslání se nezdařilo. Zkuste to znovu.',
    // #667 code door — on the main road since #668, so in every language
    loginLinkSentCode: 'Zadejte sem kód z e-mailu, nebo klikněte na odkaz v něm.',
    loginLinkCodeLabel: 'Kód z e-mailu',
    loginLinkCodeSubmit: 'Potvrdit kód',
    loginLinkCodeChecking: 'Ověřuji…',
    loginLinkCodeWrong: 'Kód není správný.',
    loginLinkCodeExpired: 'Platnost kódu vypršela. Pošlete si nový.',
    loginLinkCodeUsed: 'Tento kód už neplatí: byl použit (například odkazem v jiném prohlížeči) nebo ho nahradil novější. Pošlete si nový.',
    loginLinkCodeRefused: 'S tímto účtem se takto přihlásit nelze.',
    loginLinkCodeTooMany: 'Příliš mnoho pokusů. Počkejte minutu a zkuste to znovu.',
    loginLinkResend: 'Poslat znovu',
    loginLinkResendIn: 'Znovu odeslat za {min} min',
    loginLinkResendInS: 'Znovu odeslat za {s} s',
    // #668: mail-first login page, newest-code resend
    loginNoPasswordNeeded: 'K přihlášení nepotřebujete heslo.',
    loginWithPasswordToggle: 'Máte heslo? Přihlášení heslem',
    loginLinkResent: 'Poslali jsme nový kód. Platí jen ten poslední.',
    loginLinkResendUnsure: 'Spojení se serverem bylo přerušeno. Pošlete kód znovu. Pokud vám mezitím přišel nový e-mail, použijte odkaz v něm.',
    verifyingEmail: 'Ověřuji e-mail...',
    emailVerified: 'E-mail ověřen!',
    emailVerifiedSuccess: 'Váš e-mail byl úspěšně ověřen. Nyní se můžete přihlásit.',
    verificationFailed: 'Ověření se nezdařilo',
    verificationFailedMessage: 'Ověřovací odkaz je neplatný nebo vypršel.',
    requestNewLink: 'Vyžádat nový ověřovací odkaz',

    // Verify email page
    continueToMap: 'Pokračovat na mapu',
    backToLogin: 'Zpět na přihlášení',
    verificationLinkInvalid: 'Ověřovací odkaz je neplatný nebo vypršel.',
    enterEmailForNewLink: 'Zadejte svůj e-mail a pošleme vám nový ověřovací odkaz:',
    resendVerificationEmail: 'Znovu odeslat ověřovací e-mail',
    sending: 'Odesílám...',
    verificationEmailSent: '✓ Ověřovací e-mail odeslán! Zkontrolujte svou schránku.',

    // Verify email page — pending activation
    pendingTitle: 'Zkontrolujte svou e-mailovou schránku',
    pendingSentTo: 'Ověřovací odkaz jsme odeslali na',
    pendingExpiry: 'Odkaz platí 24 hodin. Účet bude aktivní, jakmile na něj kliknete.',
    pendingWrongAddress: 'Špatná e-mailová adresa?',
    pendingRegisterAgain: 'Zaregistrujte se znovu se správnou adresou.',
    sendFailedTitle: 'E-mail se nepodařilo odeslat',
    sendFailedInfo: 'Ověřovací e-mail se nepodařilo odeslat na',
    sendFailedRetry: 'Zkuste jej odeslat znovu níže.',
    continueToLogin: 'Pokračovat na přihlášení',
    resendAlreadyVerified: 'Tato e-mailová adresa je již ověřena — můžete se přihlásit.',
    resendInvalidEmail: 'Tato e-mailová adresa není platná. Zkontrolujte, zda je zapsána správně.',
    resendTooMany: 'Příliš mnoho pokusů. Počkejte několik minut a zkuste to znovu.',
    emailNotVerifiedError: 'Vaše e-mailová adresa zatím nebyla ověřena. Účet bude aktivní, jakmile kliknete na odkaz v ověřovacím e-mailu.',
    verifyEmailCta: 'Ověřte svůj e-mail',
    resendNotReceived: 'Nedostali jste ověřovací e-mail?',
    oauthNoEmail: 'Váš účet Microsoft nemá e-mailovou adresu. Vytvořte si účet s e-mailovou adresou.',
    emailAlreadyRegistered: 'Účet s tímto e-mailem již existuje.',
    conflictSignIn: 'Přihlaste se',
    conflictOr: 'nebo',
    conflictReset: 'obnovte si heslo',
    conflictResetNote: 'Obnovení hesla zároveň ověří vaši e-mailovou adresu.',

    // Password strength
    passwordStrength: 'Síla hesla',
    passwordTooWeak: 'Příliš slabé',
    passwordWeak: 'Slabé - Doplňte',
    passwordFair: 'Dostatečné - Doplňte',
    passwordGood: 'Dobré - Heslo splňuje požadavky',
    passwordStrong: 'Silné - Výborné heslo!',
    atLeast8Chars: 'alespoň 8 znaků',
    anUppercase: 'velké písmeno',
    aLowercase: 'malé písmeno',
    aNumber: 'číslici',

    // User menu
    logout: 'Odhlásit se',
    loggingOut: 'Odhlašuji...',
    emailAccount: 'E-mailový účet',
    googleAccount: 'Účet Google',
    microsoftAccount: 'Účet Microsoft',
    legacyAccount: 'Starší účet',

    // Errors
    loginFailed: 'Přihlášení se nezdařilo. Zkontrolujte své přihlašovací údaje.',
    registrationFailed: 'Registrace se nezdařila. Zkuste to prosím znovu.',
    // #675: signup without a password, the name step, the KingCAD done page
    registerSubmit: 'Vytvořit účet a poslat kód',
    registerNoPasswordNote: 'Heslo nepotřebujete. Přihlašovací kód dostanete e-mailem.',
    registerCodeSent: 'Kód jsme poslali na {email}. Zkontrolujte poštu.',
    registerFixAddress: 'Špatná e-mailová adresa?',
    registerFixAddressLink: 'Opravit',
    registerNameRequired: 'Zadejte jméno a příjmení.',
    registerDoneTitle: 'Účet je připraven',
    registerDoneBody: 'Vraťte se do programu {product} a klikněte na »Přihlásit v prohlížeči«.',
    registerDoneOldBuild: 'Pokud vaše verze programu toto tlačítko nemá, nastavte si heslo v profilu.',
    registerDoneProfileLink: 'Otevřít profil',
    welcomeTitle: 'Jak vám máme říkat?',
    welcomeIntro: 'Kolegové vidí vaše jméno u úkolů, komentářů a licencí.',
    welcomeSave: 'Uložit a pokračovat',
    welcomeSkip: 'Přeskočit',
    welcomeSaveFailed: 'Uložení se nezdařilo. Zkuste to prosím znovu.',
    oauthEmailConflict: 'Účet s tímto e-mailem již existuje. Přihlaste se heslem nebo způsobem, který jste používali dosud.',
    emailVerificationFailed: 'Ověření e-mailu se nezdařilo. Zkuste to prosím znovu nebo si vyžádejte nový ověřovací odkaz.',
    networkError: 'Připojení k serveru se nezdařilo. Zkontrolujte své internetové připojení.',
    serverUnavailable: 'Server je momentálně nedostupný. Zkuste to prosím za chvíli znovu.',

    // Auth pages — desktop-client mode + inline copy (kingcad#426)
    optional: '(nepovinné)',
    desktopRegisterHint: 'Tímto účtem se budete přihlašovat do programu {product}.',
    desktopVerifiedReturnHint: 'Účet je aktivní — vraťte se do programu {product} a přihlaste se.',
    alreadyHaveAccount: 'Už máte účet?',
    signInLink: 'Přihlaste se',
    noAccountYet: 'Nemáte účet?',
    createAccountLink: 'Vytvořte si ho',

    // Forgot password page
    forgotPasswordTitle: 'Zapomněli jste heslo?',
    forgotPasswordIntro: 'Zadejte svou e-mailovou adresu a pošleme vám odkaz pro obnovení hesla.',
    sendResetLink: 'Odeslat odkaz pro obnovení',
    forgotPasswordFailed: 'Odkaz pro obnovení se nepodařilo odeslat.',
    forgotCooldownNotice: 'Počkejte prosím {seconds} s, než si vyžádáte nový odkaz.',
    checkYourEmail: 'Zkontrolujte svůj e-mail',
    resetLinkSent: 'Pokud účet s touto e-mailovou adresou existuje, poslali jsme vám odkaz pro obnovení hesla. Zkontrolujte svou schránku.',
    genericError: 'Došlo k chybě. Zkuste to prosím znovu.',

    // Reset password page
    resetPasswordTitle: 'Obnovení hesla',
    resetPasswordIntro: 'Níže zadejte nové heslo.',
    newPassword: 'Nové heslo',
    newPasswordPlaceholder: 'Zadejte nové heslo',
    confirmNewPassword: 'Potvrďte nové heslo',
    confirmNewPasswordPlaceholder: 'Zadejte nové heslo znovu',
    resetPasswordButton: 'Obnovit heslo',
    resetting: 'Obnovuji...',
    passwordResetTitle: 'Heslo bylo obnoveno!',
    passwordResetSuccess: 'Vaše heslo bylo úspěšně obnoveno. Nyní se můžete přihlásit novým heslem.',
    invalidLinkTitle: 'Neplatný nebo vypršelý odkaz',
    invalidLinkMessage: 'Tento odkaz pro obnovení hesla je neplatný nebo vypršel. Vyžádejte si prosím nový.',
    requestNewResetLink: 'Vyžádat nový odkaz',
    passwordsDoNotMatch: 'Hesla se neshodují.',
    passwordMinLength: 'Heslo musí mít alespoň 8 znaků.',
    passwordNeedsUppercase: 'Heslo musí obsahovat alespoň jedno velké písmeno.',
    passwordNeedsLowercase: 'Heslo musí obsahovat alespoň jedno malé písmeno.',
    passwordNeedsNumber: 'Heslo musí obsahovat alespoň jednu číslici.',
    resetPasswordFailed: 'Heslo se nepodařilo obnovit.',
    strengthWeak: 'Slabé',
    strengthFair: 'Dostatečné',
    strengthGood: 'Dobré',
    strengthStrong: 'Silné',

    // ── Map UI — DRAFT (#597, added 2026-09) ──────────────────────────────
    // Drafted from the sl/en pairs for the CZK web map; pending review by a
    // native speaker (the Czech reseller) before it reaches prod. Keep the key
    // set equal to the sl map-UI block — tests/unit/client/utils/i18n.test.ts
    // fails on any sl key without a cs line.

    // Map toolbar
    contours: 'Vrstevnice',
    zoomInToView: 'Přibližte pro zobrazení',
    zoomOutToView: 'Oddalte pro zobrazení',
    zoomInForAll: 'Zobrazeny jen hlavní trasy, přibližte pro všechny',
    gjiNoKindSelected: 'Není vybrán žádný druh GJI',
    layerKindRaster: 'rastr',
    layerKindVector: 'vektor',
    layerOpacity: 'Průhlednost',
    layersOnCount: 'Zapnuto',
    view25d: 'Pohled 2.5D',

    // Right controls
    rightControlsPitch: 'Náklon',
    rightControlsContours: 'Vrstevnice',
    rightControlsHillshade: 'Stínování',
    rightControlsTerrain: '2.5D pohled',
    rightControlsBuildings3d: '2.5D budovy',
    rightControlsTitle: 'Nastavení mapy',

    // Toolbar
    toolbarParcelValue: 'Hodnota parcel',
    toolbarAdminStatus: 'Správní status',
    toolbarConnections: 'Přípojky',
    toolbarYearOfConstruction: 'Rok výstavby',

    // Basemap
    basemapRaster: 'Ortofoto',
    basemapCuzkOrtofoto: 'Ortofoto ČÚZK',
    basemapMore: 'Více',
    basemapStyleTitle: 'Styl podkladové mapy',

    // SearchBar
    searchPlaceholder: 'Hledat na mapě',

    // FeatureCard
    toastCopied: 'Zkopírováno!',
    toastShowingDistance: 'Zobrazena vzdálenost!',

    // Print dialog
    printTitle: 'Název',
    printDescription: 'Je vyplněna nejbližší adresa – podle potřeby ji upravte.',
    printDescriptionSearching: 'Hledám nejbližší adresu …',
    printDescriptionNoAddress: 'V okruhu {m} m není žádná adresa – zadejte libovolný název.',
    printDescriptionLookupFailed: 'Nejbližší adresu se nepodařilo najít – zadejte libovolný název.',
    printDescriptionNearestHint: 'Nejbližší adresa: {naslov}',
    printPlaceholder: 'např. Můj projekt',
    printCancel: 'Zrušit',
    printContinue: 'Pokračovat k tisku',
    printOrientation: 'Orientace',
    printLandscape: 'Na šířku',
    printPortrait: 'Na výšku',
    printPaperSize: 'Formát papíru',
    // ── V6 map shell (#597 pass 2) — DRAFT, same review status as above ──
    sectionActivity: 'Aktivita',
    sectionLayers: 'Vrstvy',
    sectionPhotos: 'Fotky',
    sectionTasks: 'Úkoly',
    sectionBasemap: 'Mapa',
    sectionDocs: 'Dokumenty',
    sectionApp: 'Aplikace',
    sectionPobude: 'Podněty',
    sectionTraffic: 'Doprava',
    sectionAirQuality: 'Kvalita ovzduší',
    sectionTrees: 'Stromy',
    sectionWorks: 'Investice',
    sectionGraditve: 'Stavby',
    worksOpenGraditevPage: 'Otevřít King.Graditev',
    sectionSewer: 'Kanalizace',
    sectionOptGap: 'OPT bez sítě',
    sectionOpnConditions: 'Podmínky OPN pro výstavbu',
    sectionParcelHistory: 'Historie parcel',

    // Icon rail
    railTabOpnShort: 'Podmínky OPN',
    railTabHistoryShort: 'Historie',
    railNavigation: 'Navigace',
    railSettings: 'Nastavení',
    bottomBarAi: 'AI',

    // Floating top bar
    topbarAiAssistant: `Asistent ${BRAND.name}`,

    // Layers panel
    // CZK layers panel (#598) - the Czech reference layers from the map style
    layersPanelGroupCuzk: 'Vrstvy ČÚZK',
    czkGroupKatastr: 'Katastr nemovitostí',
    czkGroupSpravni: 'Administrativní členění (RÚIAN)',
    czkGroupAdresy: 'Adresy',
    czkGroupTematicke: 'Tematické pohledy',
    czkLayerParcely: 'Parcely',
    czkLayerBudovy: 'Budovy',
    czkLayerHranice: 'Hranice parcel',
    czkLayerKatastralniUzemi: 'Katastrální území',
    czkLayerStat: 'Stát',
    czkLayerRegiony: 'Regiony soudržnosti',
    czkLayerVusc: 'Kraje (VÚSC)',
    czkLayerOkresy: 'Okresy',
    czkLayerOrp: 'ORP',
    czkLayerPou: 'POU',
    czkLayerObce: 'Obce',
    czkLayerMomc: 'Městské části (MOMC)',
    czkLayerPraha: 'Praha - obvody',
    czkLayerZsj: 'Základní sídelní jednotky',
    czkLayerAdresy: 'Adresy',
    czkLayerDruhPozemku: 'Druh pozemku',
    czkLayerStavebniParcely: 'Stavební parcely',
    czkLayerVyuzitiBudov: 'Způsob využití budov',
    czkLayerRokDokonceni: 'Rok dokončení',

    layersPanelGroupGurs: 'Vrstvy GURS',
    layersPanelGroupPresets: 'Speciální pohledy',
    layersPanelGroupView: 'Pohled',
    layersPanelPresetUtilityConnections: 'Inženýrské přípojky',
    layersPanelBuildings3d: '3D budovy',

    // Share view
    shareViewButton: 'Sdílet pohled',
    shareViewCopied: 'Odkaz zkopírován',

    // Generic chrome
    commonClose: 'Zavřít',
    commonBack: 'Zpět',
    contextAllProjects: 'Všechny projekty',
    docsNone: 'Žádné dokumenty k dispozici',

    // Mobile hamburger drawer
    mobileMenuTitle: 'Menu',
    mobileMenuWorksSub: 'Oblasti a náčrty',
    mobileMenuSewerSub: 'Výběr varianty',
    mobileMenuPobudeSub: 'Analýza MOL × VVO',
    mobileMenuTrafficSub: 'Senzory Telraam',
    mobileMenuTreesSub: 'Katastr stromů — filtry',
    mobileMenuOptGapSub: 'Přípojky bez trasy / kabelu v pohledu',
    mobileMenuOpnSub: 'Z obecní vyhlášky (OPN)',
    mobileMenuAiAssistant: 'AI asistent',
    mobileMenuPzDraw: 'Nakreslit oblast',
    mobileMenuPzSearch: 'Najít parcelu nebo adresu',

    // Quick actions (FAB / sidebar toolbar)
    fabAddPhotoAria: 'Přidat fotografii',
    fabAddPhoto: 'Přidat foto',
    fabPhoto: 'Foto',
    fabNewTaskAria: 'Nový úkol',
    fabTask: 'Úkol',
    fabMeasure: 'Měření',
    fabAddPost: 'Přidat příspěvek',

    // Feature popup chrome
    popupFullscreen: 'Celá obrazovka',
    popupOpenLink: 'Otevřít',
    popupClickCoordinates: 'Souřadnice kliknutí',
    popupCopyToClipboard: 'Kopírovat do schránky',
    popupShareFeature: 'Kopírovat odkaz na objekt',
    popupNavigateTo: 'Navigovat k objektu',
    popupPrevCard: 'Předchozí karta',
    popupNextCard: 'Další karta',
    popupParcelLoadError: 'Chyba při načítání parcely. Zkuste to znovu.',
    popupParcelNotFoundKo: 'Parcelu KO {ko}, č. {st} nelze najít.',
    popupParcelNotFound: 'Parcelu nelze najít.',
    popupObjectNotFound: 'Objekt nebyl nalezen',

    // Zemljiška knjiga link on the parcel card (#684)
    zkButton: 'Katastr nemovitostí (ZK)',
    zkGetExtract: 'Získat výpis ze ZK',
    zkKo: 'KO',
    zkParcel: 'parcela',
    zkCopy: 'Kopírovat',
    zkCopied: 'Zkopírováno',
    zkHint: 'Na stránce e-Sodstvo klikněte na záložku »King.Prostor ZK«.',
    zkSetupLink: 'Nastavení záložky',
    zkCopyOwners: 'Přepsat vlastníky na parcelu',
    zkCopying: 'Přepisuji vlastníky …',
    zkCopied2: 'Vlastníci jsou přepsáni ({n}). Zdroj: pozemková kniha.',
    zkCopyJoint: 'Společné vlastnictví: podíly nejsou určeny, parcela je označena jako neúplná, dokud vlastníky nepotvrdíte.',
    zkCopySeal: 'Na nemovitosti je plomba.',
    zkCopyNewerExport: 'Přepsáno, ale na této parcele platí novější export geodeta.',
    zkCopyStavba: 'Vlastníkem parcely je vlastník budovy (vlastníci jejích jednotek) — vlastníky parcely doplňte ručně.',
    zkCopyReload: 'Stránka je zastaralá — obnovte ji a výpis přepište znovu.',
    zkCopyNotInChain: 'Tato budova nebo jednotka není v řetězci vlastníků této parcely.',
    zkCopyChainNoParcel: 'Nejprve přepište výpis parcely.',
    zkVsakokratni: 'každý vlastník nemovitosti',
    zkCopyNotRegular: 'Přepsat lze jen běžný výpis.',
    zkCopyOtherParcel: 'Výpis je pro jinou parcelu.',
    zkCopyIncomplete: 'Výpis nebyl přečten celý, proto se nepřepisuje.',
    zkCopyNoOwners: 'Ve výpisu nejsou vlastníci.',
    zkCopyOlder: 'Na této parcele je již novější výpis.',
    zkCopyDuplicate: 'Tento výpis je již přepsán.',
    zkCopyNoWork: 'Tento projekt ještě nemá stavbu.',
    zkCopyNoRight: 'Pro přepis potřebujete oprávnění »Vlastníci a služebnosti«.',
    zkCopyError: 'Vlastníky se nepodařilo přepsat.',
    zkPopupBlocked: 'Prohlížeč zablokoval okno e-Sodstvo. Povolte vyskakovací okna a zkuste to znovu.',
    zkOpened: 'e-Sodstvo je otevřeno v nové kartě. Po přihlášení klikněte na záložku »King.Prostor ZK«.',
    zkReading: 'Čtu výpis …',
    zkExtractOf: 'Výpis ze dne {date}',
    zkOwners: 'Vlastníci',
    zkNoOwners: 'Výpis neobsahuje zapsané vlastnické právo.',

    // »Hišne številke« fold on the EK spojka card (#891)
    mpOwnersSection: 'Vlastníci (podíl, číslo, název, adresa, zdroj)',
    mpOwnersSectionOne: 'Vlastník',
    mpOwnersLoading: 'načítám …',
    mpOwnersError: 'Vlastníky se nepodařilo načíst.',
    mpOwnersErrorAuth: 'Přihlášení vypršelo. Obnovte stránku.',
    ekHsSection: 'Čísla popisná',
    ekHsLoading: 'načítám …',
    ekHsEmpty: 'K této spojce není přiřazeno žádné číslo popisné.',
    ekHsError: 'Čísla popisná se nepodařilo načíst.',
    ekHsErrorForbidden: 'K údajům této spojky nemáte přístup.',
    ekHsErrorAuth: 'Přihlášení vypršelo. Obnovte stránku.',
    ekHsGone: 'již není v registru',
    ekHsNoPoint: 'bez polohy',
    ekHsElsewhere: 'přiřazeno také k {oznaka}',
    ekHsWithheld: '{n} v projektech bez přístupu',
    ekHsSpliter: 'splitter',
    ekHsProjectNone: 'Vyberte projekt.',
    ekHsProjectLinked: 'připojené: {n}',
    ekHsProjectUnlinked: 'nepřipojené: {n}',
    ekHsProjectInView: 'v zobrazení',
    ekHsProjectNoArea: 'Projekt nemá území – nepřipojené adresy se nezobrazují.',
    ekHsProjectCapped: 'zobrazeno {n} z {m} – přibližte',
    ekHsProjectForbidden: 'K datům tohoto projektu nemáte přístup.',
    zkShare: 'podíl',
    zkRights: 'Odvozená práva a poznámky',
    zkMainParcel: 'hlavní nemovitost',
    zkWarnings: 'Upozornění',
    zkError: 'Výpis se nepodařilo přečíst.',
    popupErrorNoAccessTitle: 'Bez přístupu k datům',
    popupErrorNoAccessMessage: 'Nemáte oprávnění k zobrazení této vrstvy.',
    popupErrorServerTitle: 'Chyba serveru',
    popupErrorServerMessage: 'Při načítání dat došlo k chybě.\nZkuste to znovu nebo kontaktujte podporu.',
    popupErrorConnectionTitle: 'Chyba připojení',
    popupErrorConnectionMessage: 'Data se nepodařilo načíst.\nZkontrolujte připojení k internetu.',
    // ── Map screens (#597 pass 3) — DRAFT, same review status as above ──
    // Page shell
    pageTitleMap: 'Aktivita',
    pageAiOpenAria: `Otevřít asistenta ${BRAND.name}`,
    pageErrorTitle: 'Chyba',
    pageMapLoadFailed: 'Mapu se nepodařilo načíst. Zkuste to znovu.',
    pageInitFailed: 'Aplikaci se nepodařilo spustit. Zkuste to znovu.',
    pageBackHome: 'Zpět na úvodní stránku',
    pageNoAccessTitle: 'Bez přístupu',
    pageNoAccessBody: 'Nemáte přístup k žádné aplikaci. Kontaktujte správce.',
    cadOverlayTitle: 'Výkres',
    mapCanvasAria: 'Mapa',
    commonRequestAccess: 'Požádat o přístup',
    basemapButtonAria: 'Vybrat podkladovou mapu',

    // Access banner (MapAccessBanner)
    bannerSignin: 'Pro zobrazení této mapy se přihlaste.',
    bannerSessionExpired: 'Relace vypršela. Pro pokračování se přihlaste znovu.',
    bannerForbidden: 'K této aplikaci nemáte přístup.',
    bannerForbiddenProject: 'K tomuto projektu nemáte přístup.',
    bannerNotFoundApp: 'Aplikaci se nepodařilo najít. Zkontrolujte odkaz.',
    bannerNotFoundProject: 'Projekt se nepodařilo najít. Zkontrolujte odkaz.',
    bannerNewVersion: 'Je k dispozici nová verze.',
    bannerReload: 'Obnovit',

    // User menu (UserAvatar)
    userMenuAuthEmailLegacy: 'E-mailový účet (starší)',
    userMenuAuthEmail: 'E-mailový účet',
    userMenuAuthGoogle: 'Účet Google',
    userMenuAuthMicrosoft: 'Účet Microsoft',
    userMenuAuthEmailLink: 'E-mailový účet (přihlášení kódem)',
    userMenuLogoutFailed: 'Odhlášení se nezdařilo. Zkuste to znovu.',
    // #885: the language switch in the user menu
    languageSwitchLabel: 'Jazyk',
    languageSwitchBusy: 'Nejprve uložte nebo zavřete otevřenou úpravu, pak zvolte jazyk.',
    languageSwitchNoStorage: 'Prohlížeč nedovoluje uložit volbu jazyka.',
    userMenuLoggingOut: 'Odhlašuji…',
    userMenuLogout: 'Odhlásit se',
    userMenuDashboard: 'Řídicí panel',
    userMenuProfile: 'Profil',
    userMenuLoading: 'Načítání…',
    userMenuUserFallback: 'Uživatel',

    // AI sidebar — client text only
    aiAssistantLabel: 'Asistent',
    aiStatusAvailable: 'K dispozici',
    aiInputPlaceholder: 'Zeptejte se...',
    aiNewChat: 'Nová konverzace',
    aiCloseAria: 'Zavřít AI asistenta',
    aiSendAria: 'Odeslat',
    aiLockText: 'Pro AI asistenta se přihlaste',
    aiGreeting: 'Dobrý den! Jsem váš AI asistent pro prostorová data. Jak vám mohu pomoci?',
    aiQuick1Label: 'Budova na adrese',
    aiQuick1Question: 'Jaká budova je na adrese Prešernov trg 1, Ljubljana?',
    aiQuick2Label: 'Parcela na adrese',
    aiQuick2Question: 'Jaká parcela je na adrese Soča 28, Bovec?',
    aiQuick3Label: 'Environmentální omezení na parcele',
    aiQuick3Question: 'Zkontroluj environmentální omezení na parcele 63/4, k.ú. SOČA DESNA (2209).',
    aiQuick4Label: 'Nejbližší hydrant',
    aiQuick4Question: 'Který hydrant je nejblíže parcele 2189/7, k.ú. VIČ (1723)?',
    aiQuickCz1Label: 'Budova na adrese',
    aiQuickCz1Question: 'Jaká budova je na adrese Staroměstské náměstí 1, Praha?',
    aiQuickCz2Label: 'Parcela na adrese',
    aiQuickCz2Question: 'Jaká parcela je na adrese Karlova 1, Praha?',
    aiQuickCz3Label: 'Parcela podle čísla',
    aiQuickCz3Question: 'Kde je parcela 1090, k.ú. Staré Město (727024)?',
    aiQuickCz4Label: 'Katastrální území',
    aiQuickCz4Question: 'Kde je katastrální území Abertamy?',
    aiThinking: 'Přemýšlím…',
    aiProcessing: 'Zpracovávám…',
    aiErrorOverloaded: 'Služba AI je momentálně přetížená. Zkuste to za několik sekund.',
    aiErrorNoResponse: 'Služba AI neodpověděla. Zkuste to znovu.',
    aiErrorGeneric: 'Došlo k chybě. Zkuste to znovu.',
    aiErrorInterrupted: 'Odpověď se přerušila během přemýšlení. Zkuste to znovu.',
    aiErrorEmpty: 'Nepřišla žádná odpověď. Služba AI je možná přetížená.',
    aiErrorComm: 'Chyba při komunikaci s AI.',
    aiErrorMonthlyLimit: 'Měsíční limit používání AI byl vyčerpán. Zkuste to příští měsíc.',
    aiRetry: 'Zkusit znovu',
    // AI tool indicators
    aiToolSearchParcel: 'Hledám parcelu…',
    aiToolSearchAddress: 'Hledám adresu…',
    aiToolSearchPlace: 'Hledám místo…',
    aiToolGetParcelInfo: 'Načítám údaje o parcele…',
    aiToolFindOwner: 'Hledám vlastníka…',
    aiToolSearchParcelsByOwner: 'Hledám parcely podle vlastníka…',
    aiToolGetBuildingInfo: 'Načítám údaje o budově…',
    aiToolGetInfrastructureInfo: 'Načítám údaje o infrastruktuře…',
    aiToolFindParcelsNear: 'Hledám blízké parcely…',
    aiToolFindInfrastructureNear: 'Hledám blízkou infrastrukturu…',
    aiToolSearchEkFeatures: 'Hledám objekty EK…',
    aiToolGetRouteInfo: 'Načítám údaje o trase…',
    aiToolGetCableInfo: 'Načítám údaje o kabelu…',
    aiToolGetSpliceInfo: 'Načítám údaje o spojce…',
    aiToolFindEkFeaturesNear: 'Hledám blízké objekty EK…',
    aiToolGetProjectEkSummary: 'Načítám souhrn projektu…',
    aiToolFindHouseConnectionsNear: 'Hledám domovní přípojky…',
    aiToolGetRoutePipes: 'Načítám trubky trasy…',
    aiToolGetRouteCables: 'Načítám kabely trasy…',
    aiToolGetSpliceCables: 'Načítám kabely spojky…',
    aiToolGetCableFibers: 'Načítám vlákna kabelu…',
    aiToolGetCableReserves: 'Načítám rezervy kabelu…',
    aiToolLocateCableFault: 'Počítám místo poruchy…',
    aiToolQueryEkLengths: 'Počítám délky sítě EK…',
    aiToolSearchLayers: 'Hledám vrstvy…',
    aiToolGetLayerSchema: 'Načítám schéma vrstvy…',
    aiToolGetAppMapCatalog: 'Načítám katalog vrstev…',
    aiToolGetEnvironmentalConstraints: 'Kontroluji environmentální omezení…',
    aiToolGetEnvironmentalConstraintsForParcel: 'Kontroluji omezení pro parcelu…',
    aiToolFindArsoFeaturesNear: 'Hledám blízké objekty ARSO…',
    aiToolGetParcelZoning: 'Kontroluji využití území…',
    aiToolGetParcelConditions: 'Čtu podmínky výstavby z vyhlášky…',
    aiToolListProjectOpinions: 'Čtu stanoviska…',
    aiToolReadOpinionDocument: 'Čtu dokument stanoviska…',
    aiToolGetGraditevConstraints: 'Kontroluji omezení v území záměru…',
    aiToolGetGraditevConstraintDetails: 'Čtu omezení a výňatky z předpisů…',
    aiToolListGraditevReports: 'Hledám technické zprávy…',
    aiToolReadGraditevReport: 'Čtu technickou zprávu…',
    aiToolFindInLaw: 'Hledám v textu předpisů…',
    aiToolGetProjectConsentSummary: 'Kontroluji souhlasy parcel…',
    aiToolGetRouteConsentStatus: 'Kontroluji souhlasy podél trasy…',
    aiToolFindParcelsMissingConsent: 'Hledám parcely bez souhlasu…',
    aiToolControlMapLayers: 'Přepínám vrstvy…',
    aiToolSwitchBasemap: 'Měním podkladovou mapu…',
    aiToolToggleSpecialView: 'Přepínám pohled…',

    // Context switching
    ctxSearchAria: 'Hledání kontextu',
    ctxRecent: 'Nedávné',
    ctxOtherApps: 'Další aplikace',
    ctxNoResults: 'Žádné shody',
    ctxNoResultsAlt: 'Žádné výsledky',
    ctxApps: 'Aplikace',
    ctxAppsCount: 'Aplikace ({n})',
    ctxProjects: 'Projekty',
    ctxCurrentApp: 'Aktuální aplikace',
    ctxCurrentAppSr: '(aktuální aplikace)',
    ctxSwitchTitle: 'Změnit kontext',
    ctxSearchPlaceholder: 'Hledat...',
    ctxAllApps: 'Všechny aplikace',
    ctxLoading: 'Načítám...',
    ctxProjectsOfCount: '{n} z {total} projektů',
    ctxFindProject: 'Najít projekt …',
    ctxFindApp: 'Najít aplikaci …',
    ctxDashboardLink: 'Řídicí panel →',
    ctxHints: '↑↓ pohyb · Enter výběr · Esc zavřít',
    ctxHintsRequired: '↑↓ pohyb · Enter výběr',
    ctxLoadingProjects: 'Načítání projektů …',
    ctxNoProjects: 'Žádné projekty',
    ctxRequiredNote:
      'Vyberte projekt, aby se zobrazila data. Máte přístup k jednotlivým projektům, ne k celé aplikaci.',
    ctxOtherProjectsTitle: 'Další projekty v této aplikaci',
    ctxNoAccessNote: 'K těmto projektům nemáte přístup.',
    ctxUnnamed: '(bez názvu)',
    ctxLastOpened: 'naposledy otevřeno',
    ctxProjectChoiceAria: 'Výběr projektu',
    ctxDeniedAppNote: 'K aplikaci »{app}« nemáte přístup. Níže jsou aplikace, které máte k dispozici.',

    // App panel
    appPanelPublicPill: 'Veřejné',
    appPanelExplore: 'Prozkoumat',
    appPanelFindParcel: 'Najít parcelu',
    appPanelFindAddress: 'Najít adresu',
    appPanelOrgQuestion: 'Jste součástí organizace?',
    appPanelLoginBody: 'Přihlaste se a otevřete své aplikace, projekty a data své sítě.',
    appPanelNetwork: 'Síť',
    appPanelNetworkProject: 'Síť projektu',
    appPanelRecent: 'Poslední dění',
    appPanelAll: 'Vše →',
    appPanelDashboard: 'Řídicí panel',
    appPanelMyApps: 'Moje aplikace',
    appPanelProject: 'Projekt',
    appPanelNow: 'nyní',
    appPanelMinAgo: 'před {n} min',
    appPanelHoursAgo: 'před {n} h',
    appPanelYesterday: 'včera',
    appPanelDaysAgo: 'před {n} dny',
    appPanelProjectsOne: 'projekt',
    appPanelProjectsTwo: 'projekty',
    appPanelProjectsFew: 'projekty',
    appPanelProjectsMany: 'projektů',
    appPanelActivityCount: 'aktivit',
    appPanelLastActivity: 'poslední aktivita',
    appPanelPostFallback: 'Příspěvek',
    appPanelReopened: 'Znovu otevřeno',
    // Activity vocabulary
    activityNew: 'Nové',
    activityEdited: 'Aktualizováno',
    activityCommented: 'Komentář',
    activityResolved: 'Vyřešeno',
    activityUnresolved: 'Nevyřešeno',
    activityAssigned: 'Přiděleno',
    activityReacted: 'Reakce',
    postTypeNote: 'Poznámka',
    postTypeIssue: 'Úkol',
    postTypeInspection: 'Kontrola',
    postStatusOpen: 'Otevřeno',
    postNoTitle: 'bez názvu',
    postPhotoUnit: 'foto',

    // Layers panel + layer controls leftovers
    layersPanelShowAll: 'Zobrazit vše',
    layersPanelGjiKinds: 'Druhy GJI',
    layerControlsEmpty: 'Žádné vrstvy k zobrazení',

    // Feature popup leftovers
    popupFooterLayer: 'Vrstva',
    popupFeatureFallback: 'Objekt {index}',
    popupFeatureTitleFallback: 'Objekt',

    // Measure tool
    measureUndoAria: 'Odebrat poslední bod měření',
    measureUndoTitle: 'Odebrat poslední bod (nebo pravý klik na mapu)',

    // Locate me
    locateUnsupported: 'Váš prohlížeč nepodporuje určování polohy.',
    locateGeneric: 'Vaši polohu se nepodařilo zjistit.',
    locatePermissionDenied: 'Přístup k poloze byl odepřen. Povolte jej v nastavení prohlížeče.',
    locateUnavailable: 'Informace o poloze nejsou k dispozici.',
    locateTimeout: 'Určování polohy vypršelo. Zkuste to znovu.',
    locateTimeoutSearching: 'Určování polohy vypršelo. Hledám dál…',
    locateUnavailableSearching: 'Informace o poloze nejsou k dispozici. Hledám dál…',
    // ── Site pages (#597 pass 4) — DRAFT, same review status as above ──
    pageTitleLogin: 'Přihlášení',
    pageTitleRegister: 'Registrace',
    pageTitleVerifyEmail: 'Ověření e-mailu',
    pageTitleForgotPassword: 'Zapomenuté heslo',
    pageTitleResetPassword: 'Obnovení hesla',
    pageTitleLoginLink: 'Přihlášení',
    pageTitleDeviceLogin: 'Přihlášení zařízení',
    pageTitleLogout: 'Odhlášení',
    pageTitleDashboard: 'Řídicí panel',
    pageTitleProfile: 'Profil',
    pageTitleCommits: '{name} · historie změn',
    pageTitleRequestAccess: 'Žádost o přístup - {name}',
    pageTitleLimitedAccess: 'Omezený přístup',
    pageTitleLimitedAccessFor: 'Omezený přístup - {name}',
    pageTitleNoAccess: 'Bez přístupu - {name}',
    pageTitleInvite: 'Pozvánka · {name}',
    pageTitleInvitePlain: 'Pozvánka',
    authErrInvalidCredentials: 'Nesprávný e-mail nebo heslo.',
    authErrAccountDisabled: 'Tento účet je zablokován.',
    authErrTooMany: 'Příliš mnoho pokusů o přihlášení. Zkuste to znovu za {seconds} s.',
    authErrWeakPassword: 'Heslo musí mít alespoň 8 znaků, velké a malé písmeno a číslici.',
    authErrEmailTaken: 'Účet s tímto e-mailem už existuje.',

    // Logout page
    logoutLoading: 'Načítání…',
    logoutTitle: 'Odhlášení',
    logoutButton: 'Odhlásit se',
    logoutWorking: 'Odhlašuji…',
    logoutCancel: 'Zrušit',
    logoutGoToLogin: 'Přejít na přihlášení',
    logoutNotSignedIn: 'Nejste přihlášeni.',
    logoutLoadFailed: 'Údaje o uživateli se nepodařilo načíst.',

    // Relative time
    relTimeNow: 'právě teď',
    relTimeYesterday: 'včera',
    relTimeSecondsOne: 'před {n} sekundou',
    relTimeSecondsTwo: 'před {n} sekundami',
    relTimeSecondsMany: 'před {n} sekundami',
    relTimeMinutesOne: 'před {n} minutou',
    relTimeMinutesTwo: 'před {n} minutami',
    relTimeMinutesMany: 'před {n} minutami',
    relTimeHoursOne: 'před {n} hodinou',
    relTimeHoursTwo: 'před {n} hodinami',
    relTimeHoursMany: 'před {n} hodinami',
    relTimeDaysOne: 'před {n} dnem',
    relTimeDaysTwo: 'před {n} dny',
    relTimeDaysMany: 'před {n} dny',
    relTimeWeeksOne: 'před {n} týdnem',
    relTimeWeeksTwo: 'před {n} týdny',
    relTimeWeeksMany: 'před {n} týdny',
    relTimeMonthsOne: 'před {n} měsícem',
    relTimeMonthsTwo: 'před {n} měsíci',
    relTimeMonthsMany: 'před {n} měsíci',
    relTimeYearsOne: 'před {n} rokem',
    relTimeYearsTwo: 'před {n} lety',
    relTimeYearsMany: 'před {n} lety',

    // Dashboard
    dashWelcome: 'Vítejte, {name}',
    dashContinue: 'Pokračujte',
    dashPublicMap: 'Veřejná mapa',
    dashPublicMapBody: 'Parcely, budovy, adresy a prostorová data pro celou zemi.',
    dashOpenPublicMap: 'Otevřít mapu',
    dashLayerDocs: 'Dokumentace vrstev',
    dashCountrySlovenia: 'Slovinsko',
    dashCountryCzechia: 'Česko',
    dashMapStatLayers: 'Vrstev na mapě',
    dashMapStatThemes: 'Témat',
    dashMapStatPublishers: 'Vydavatelů',
    dashMapOnMap: 'Co je na mapě',
    dashMapCatalogue: 'Katalog vrstev',
    dashMapShowAllThemes: 'Zobrazit všechna témata ({n})',
    dashMapColData: 'Data',
    dashMapColSource: 'Datum zdroje',
    dashMapSources: 'Zdroje a licence',
    dashMapPublishers: 'Vydavatelé',
    dashMapStatParcels: 'Parcely k datu',
    dashMapCountsNote: 'Čísla počítají vrstvy, které tato mapa zobrazuje. Dokumentace vrstev zahrnuje i vrstvy pro KingCAD a Česko.',
    dashMapWeekly: 'Týdně aktualizovaná data',
    dashMapColOnMap: 'Na mapě',
    dashMapAdmin: 'Správce',
    dashMapSummaryFailed: 'Údaje o vrstvách nyní nelze zobrazit.',
    dashAppsCount: '{n} aplikací',
    dashProjectsCount: '{n} projektů',
    dashRecentlyVisited: 'Nedávno navštívené',
    dashOpenMap: 'Otevřít mapu',
    dashShowLess: 'Zobrazit méně',
    dashShowMore: 'Zobrazit více',
    dashShowMoreN: 'Zobrazit více ({n})',
    dashShowAllN: 'Zobrazit vše ({n})',
    dashShowAllMore: 'Zobrazit vše (ještě {n})',
    dashApps: 'Aplikace',
    dashSearchApps: 'Hledat aplikace...',
    dashProjAbbrev: '{n} proj',
    dashRecentActivity: 'Nedávná aktivita',
    dashAllApps: 'Všechny aplikace',
    dashNoRecentActivity: 'Žádná nedávná aktivita.',
    dashHome: 'Domů',
    dashMap: 'Mapa',
    dashSettings: 'Nastavení',
    dashSettingsAdminTitle: 'Nastavení aplikace (správce)',
    dashReports: 'Reporty',
    dashSluznosti: 'Věcná břemena',
    dashSluznostiTitle: 'Registr věcných břemen (parcely, vlastníci, smlouvy)',
    dashReportsWriteTitle: 'Reporty (přístup pro zápis)',
    dashProjectSettingsTitle: 'Nastavení projektu (správce)',
    dashWorks: 'Stavby',
    dashWorksNew: 'Nová stavba',
    dashWorksNewTitle: 'Nová stavba — zadejte název, ostatní doplníte po krocích',
    dashWorksEmpty: 'Zatím žádné stavby. Začněte tlačítkem »Nová stavba« — zadejte název, ostatní doplníte po krocích.',
    dashWorksOpinions: 'Stanoviska {done}/{total}',
    dashParcelsCount: '{n} parcel',
    dashProjects: 'Projekty',
    dashNewProject: 'Nový projekt',
    dashSearchProjects: 'Hledat projekty...',
    dashNoProjects: 'Žádné projekty.',
    dashUpdated: 'Aktualizováno {when}',
    dashProjectName: 'Název projektu',
    dashProjectNamePlaceholder: 'např. Nová čtvrť Polje',
    dashProjectKind: 'Typ projektu',
    dashDescription: 'Popis',
    dashOptional: '(nepovinné)',
    dashDescriptionPlaceholder: 'Krátký popis projektu',
    dashCancel: 'Zrušit',
    dashCreateProject: 'Vytvořit projekt',
    dashCreating: 'Vytvářím...',
    dashActivity: 'Aktivita',
    dashFilterAll: 'Vše',
    dashFilterTasks: 'Úkoly',
    dashFilterPhotos: 'Fotky',
    dashFilterComments: 'Komentáře',
    dashFilterOpinions: 'Stanoviska',
    dashLatestChanges: 'Poslední změny',
    dashAllChanges: 'Všechny změny',
    dashNoEdits30: 'Žádné úpravy za posledních 30 dní',
    dashEditProject: 'Upravit projekt',
    dashDrawOnMap: 'Zobrazit projekt na společné mapě aplikace',
    dashDrawOnMapHint:
      'Pokud je vypnuto, projekt se nekreslí na společné mapě se všemi projekty. Zůstává v seznamu, můžete ho otevřít samostatně a v desktopové aplikaci (KingCAD) se nic nemění.',
    dashSave: 'Uložit',
    dashSaving: 'Ukládám...',
    dashShowOnMap: 'Zobrazit na mapě',
    dashElaboratTitle: 'Objekty pro elaborát GJI',
    dashElaboratSubtitle:
      'V provozu bez identifikátoru GURS, vlastník GVO (podle číselníku) nebo bez vlastníka · celkem {n}',
    dashElaboratAllOk: 'Všechny objekty v provozu zapsané GVO mají identifikátor GURS',
    dashReportLink: 'Report →',
    dashOpinionsTitle: 'Stanoviska / Souhlasy',
    dashEditing: 'Upravit →',
    dashNoOpinions: 'Pro tento projekt nejsou žádná stanoviska.',
    dashDocsAbbrev: '{n} dok.',
    dashNoDocs: 'Žádné dokumenty.',
    dashTagTask: 'úkol',
    dashTagNote: 'poznámka',
    dashTagInspection: 'kontrola',
    dashUsersOne: '{n} uživatel',
    dashUsersTwo: '{n} uživatelé',
    dashUsersFew: '{n} uživatelé',
    dashUsersMany: '{n} uživatelů',
    dashNever: 'nikdy',
    dashUnknownUser: 'Neznámý uživatel',
    dashUnknownUserAlt: 'Neznámý uživatel',
    dashSectionToday: 'Dnes',
    dashSectionWeek: 'Posledních 7 dní',
    dashSectionMonth: 'Posledních 30 dní',
    dashChangesOne: '{n} změna',
    dashChangesTwo: '{n} změny',
    dashChangesFew: '{n} změny',
    dashChangesMany: '{n} změn',
    dashCommitOne: '{n} commit',
    dashCommitMany: '{n} commitů',
    dashAndOtherOne: ' a další {n}',
    dashAndOthers: ' a další {n}',
    dashActiveProjectsZero: '0 projektů aktivních za posledních 30 dní',
    dashActiveProjectsOne: '1 projekt aktivní za posledních 30 dní',
    dashActiveProjectsTwo: '2 projekty aktivní za posledních 30 dní',
    dashActiveProjectsFew: '{n} projekty aktivní za posledních 30 dní',
    dashActiveProjectsMany: '{n} projektů aktivních za posledních 30 dní',
    dashOpinionsSummary: '{yes} z {total} získaných',
    dashStageConditionsRequest: 'Žádost o podmínky',
    dashStageConditions: 'Podmínky',
    dashStageOpinionRequest: 'Žádost o stanovisko',
    dashStageOpinion: 'Stanovisko',
    dashStatusObtained: 'Získáno',
    dashStatusRejected: 'Zamítnuto',
    dashStatusPending: 'Zpracovává se',
    dashRequestFailed: 'Požadavek se nezdařil.',
    dashErrorStatus: 'Chyba ({status})',

    // Profile
    profileEditName: 'Upravit jméno',
    profileFirstName: 'Jméno',
    profileLastName: 'Příjmení',
    profileMemberSince: 'Členem od {date}',
    profileAppsOne: '{n} aplikace',
    profileAppsTwo: '{n} aplikace',
    profileAppsFew: '{n} aplikace',
    profileAppsMany: '{n} aplikací',
    profileAdminPanel: 'Panel správce',
    profileDesktopPasswordTitle: 'Heslo pro starší verze programu',
    profileDesktopPasswordBody: 'K přihlášení ho nepotřebujete. Nastavte ho jen tehdy, pokud používáte starší verzi programu (před 27.2).',
    profilePasswordChangeSignsOut: 'Po nastavení nebo změně hesla budete odhlášeni na všech zařízeních a budete se muset znovu přihlásit.',
    profileSendPasswordLink: 'Poslat odkaz pro nastavení hesla',
    profileSendPasswordChangeLink: 'Poslat odkaz pro změnu hesla',
    profileSending: 'Odesílám ...',
    profilePasswordLinkSent: 'Odkaz jsme poslali na {email}. Otevřete zprávu a postupujte podle pokynů.',
    profileDevices: 'Moje zařízení',
    profileDevicesOne: '{n} zařízení',
    profileDevicesTwo: '{n} zařízení',
    profileDevicesFew: '{n} zařízení',
    profileDevicesMany: '{n} zařízení',
    profileNoDevices: 'Žádný počítač s KingCAD není momentálně přihlášen tímto účtem.',
    profileDeviceLabel: 'Zařízení {code}',
    profileLastUsed: 'naposledy',
    profileDevicesError: 'Seznam zařízení se nepodařilo načíst.',
    profileSignOutAll: 'Odhlásit mě všude',
    profileSignOutAllConfirm:
      'Všechna zařízení budou odhlášena; na každém se budete muset znovu přihlásit přes prohlížeč. Pokračovat?',
    profileSignOutAllYes: 'Ano, odhlásit všude',
    profileSigningOut: 'Odhlašuji …',
    profileSignedOutAll: 'Všechna zařízení jsou odhlášena.',
    profileSignOutAllNeedsBrowser: 'Pro tuto akci se přihlaste v prohlížeči (heslo, Google nebo odkaz e-mailem).',
    // #791 the device list: name + last used, »Odebrat« per device
    profileDevicesIntro: 'Počítače s KingCAD přihlášené tímto účtem. Odhlášení počítače jej ze seznamu neodebere — to udělá »Odebrat«.',
    profileDevicesPrivacyLink: 'Jaké údaje zaznamenáváme a jak dlouho',
    profileDevicesLimit: 'Používáte {count} z {cap} zařízení.',
    profileDeviceRemove: 'Odebrat',
    profileDeviceRemoveConfirm: 'Odebrat zařízení {name} ze seznamu? Budete se na něm muset znovu přihlásit, a to až po čekací době.',
    profileDeviceRemoveYes: 'Ano, odebrat',
    profileDeviceRemoving: 'Odebírám …',
    profileDeviceRemoved: 'Zařízení {name} bylo odebráno.',
    profileDeviceRemoveCapped: 'Povolený počet odebrání zařízení v tomto měsíci byl dosažen.',
    profileDeviceRemoveFailed: 'Zařízení se nepodařilo odebrat. Zkuste to znovu.',
    profileNoApps: 'Nemáte přístup k žádné aplikaci',
    profileConnectionError: 'Chyba připojení. Zkuste to znovu.',
    profileTooMany: 'Příliš mnoho požadavků. Počkejte minutu a zkuste to znovu.',
    profileSendFailed: 'Odeslání se nezdařilo. Zkuste to znovu.',
    profileSaveFailed: 'Chyba při ukládání.',
    profileConnectionFailed: 'Chyba připojení.',
    profileLoadAppsFailed: 'Aplikace se nepodařilo načíst.',
    profileDateUnknown: '–',

    // Commits
    commitsCrumb: 'Historie změn',
    commitsSearchUsers: 'Hledat uživatele...',
    commitsOn: 'Commity {date}',
    commitsInserted: '+{n} vložených',
    commitsUpdated: '~{n} aktualizovaných',
    commitsDeleted: '-{n} smazaných',
    commitsNoChanges: 'Beze změn',
    commitsPageInfo: 'Strana {page} z {pages} · celkem {total} commitů',
    commitsContributors: 'Přispěli',
    commitsMissingParam: 'Chybí parametr sxapp nebo sxproject',
    commitsAppInfoError: 'Chyba při načítání informací o aplikaci',
    commitsProjectNotFound: 'Projekt nenalezen',
    commitsProjectInfoError: 'Chyba při načítání informací o projektu',
    commitsLoadError: 'Chyba při načítání historie commitů',
    commitsDateTime: '{date} v {time}',
    commitsAllUsers: 'Všichni uživatelé',
    commitsAllDates: 'Všechna data',
    commitsWeekdays: 'Po,Út,St,Čt,Pá,So,Ne',
    commitsMonths: 'Leden,Únor,Březen,Duben,Květen,Červen,Červenec,Srpen,Září,Říjen,Listopad,Prosinec',

    // Access pages + invitation
    accessRequestTitle: 'Žádost o přístup',
    accessNoAccessTo: 'Nemáte přístup k:',
    accessMessageLabel: 'Zpráva správci',
    accessMessagePlaceholder: 'Stručně napište, kdo jste a proč potřebujete přístup.',
    accessSend: 'Odeslat žádost',
    accessSending: 'Odesílám...',
    accessSentTitle: 'Žádost odeslána',
    accessSentBody: 'Správce vaši žádost posoudí a dá vám vědět e-mailem.',
    accessAlreadyRequested: 'Žádost o přístup už byla odeslána.',
    accessSubmitFailed: 'Žádost se nepodařilo odeslat.',
    accessLimitedTitle: 'Omezený přístup',
    accessLimitedBody: 'Nemáte přístup k celé aplikaci:',
    accessLoading: 'Načítání...',
    accessYouHave: 'Máte přístup k: {projects}:',
    accessNoProjects: 'Nemáte přístup k žádnému projektu v této aplikaci.',
    accessAppMissing: 'Aplikace neexistuje.',
    accessProjectsLoadError: 'Chyba při načítání projektů.',
    accessProjectsZero: 'žádný projekt',
    accessContactAdmin: 'Pro přístup kontaktujte správce:',
    accessContactOrgAdmin: 'Pro přístup kontaktujte správce organizace.',
    accessCheckFailed: 'Přístup se nepodařilo ověřit. Kontaktujte podporu.',
    accessRequestFull: 'Požádat o plný přístup',
    popupNoProperties: 'Žádné vlastnosti',
    popupErrorSessionTitle: 'Přihlášení není platné',
    popupErrorSessionMessage: 'Přihlaste se znovu. Pokud problém přetrvává, kontaktujte podporu.',
    popupNoData: 'Bez údaje',
    commonYes: 'Ano',
    commonNo: 'Ne',
    measureUndoLabel: 'Krok zpět',
    inviteNotFound: 'Pozvánka nenalezena',
    inviteNotFoundBody: 'Odkaz na pozvánku je možná chybný, nebo byla pozvánka smazána.',
    inviteNoLongerValid: 'Pozvánka už není platná',
    inviteAccepted: 'Tato pozvánka už byla přijata.',
    inviteCancelled: 'Tato pozvánka byla zrušena.',
    inviteExpired: 'Platnost této pozvánky vypršela.',
    inviteAskNew: 'Požádejte správce o novou pozvánku.',
    inviteTitle: 'Pozvánka do aplikace',
    inviteOrg: 'Organizace: {org}',
    inviteFrom: 'Zve vás:',
    inviteForEmail: 'Pro e-mail:',
    inviteValidUntil: 'Platí do:',
    inviteSignInOrRegister: 'Pro přijetí pozvánky si vytvořte účet s tímto e-mailem. Heslo nepotřebujete. Pokud účet již máte, přihlaste se.',
    inviteCreateAccount: 'Vytvořit účet',
    inviteSignedInAs: 'Jste přihlášeni jako {email}. Stiskněte tlačítko pro přijetí pozvánky.',
    inviteAccept: 'Přijmout pozvánku',
    inviteAccepting: 'Přijímám…',
    inviteWrongAccount:
      'Jste přihlášeni jako {current}, pozvánka je ale pro {invited}. Odhlaste se a přihlaste se adresou, na kterou byla pozvánka poslána.',
    inviteSignOut: 'Odhlásit se',
    inviteErr401: 'Přihlášení není platné nebo účet nelze použít.',
    inviteErr403: 'Tato pozvánka je vystavena na jiný e-mail.',
    inviteErr404: 'Pozvánku se nepodařilo najít.',
    inviteErr409: 'Tato pozvánka už není platná.',
    inviteErrGeneric: 'Pozvánku se nepodařilo přijmout. Zkuste to znovu nebo informujte správce.',
    inviteInvalidPath: 'Neplatná cesta.',
    inviteLoadFailed: 'Chyba při načítání pozvánky.',
  },

  hr: {
    // #569 desktop browser login + email login link
    deviceLoginTitle: 'Prijava uređaja KingCAD',
    deviceLoginLoading: 'Učitavam zahtjev za prijavu…',
    deviceLoginSignedInAs: 'Prijavljeni ste kao',
    deviceLoginQuestion: 'Dopustiti prijavu u {product} na ovom uređaju?',
    deviceLoginMachine: 'Uređaj',
    deviceLoginWindowsUser: 'Windows korisnik',
    deviceLoginProgram: 'Program',
    deviceLoginRemote: 'udaljena sesija',
    deviceLoginExpiresIn: 'Zahtjev istječe za {min} min.',
    deviceLoginAllow: 'Dopusti',
    deviceLoginApproving: 'Potvrđujem…',
    deviceLoginNotYou: 'Niste vi? Prijava drugim računom',
    deviceLoginCancel: 'Odustani',
    deviceLoginDone: 'Gotovo — vratite se u {product}.',
    deviceLoginDoneClose: 'Ovu karticu možete zatvoriti.',
    deviceLoginExpired: 'Zahtjev je istekao. U programu ponovno kliknite „Prijava u pregledniku“.',
    deviceLoginDenied: 'Prijava je otkazana.',
    deviceLoginConsumed: 'Ovaj je zahtjev već iskorišten.',
    deviceLoginNotFound: 'Nepoznat zahtjev za prijavu.',
    deviceLoginError: 'Došlo je do pogreške. Pokušajte ponovno.',
    deviceLoginBackHome: 'Na početnu stranicu',
    loginLinkRedeeming: 'Prijavljujem…',
    loginLinkSuccess: 'Prijavljeni ste',
    loginLinkContinue: 'Nastavi',
    loginLinkFailed: 'Poveznica nije valjana ili je istekla',
    loginLinkFailedHint: 'Poveznica vrijedi 15 minuta i može se upotrijebiti samo jednom.',
    loginLinkRequestNew: 'Natrag na prijavu',
    loginLinkOption: 'Pošalji mi kod za prijavu',
    loginLinkSending: 'Šaljem…',
    loginLinkSent: 'Ako račun postoji, poslali smo kod na {email}. Provjerite poštu.',
    loginLinkNeedEmail: 'Najprije unesite e-adresu.',
    loginLinkError: 'Slanje nije uspjelo. Pokušajte ponovno.',
    // #667 code door — on the main road since #668, so in every language
    loginLinkSentCode: 'Ovdje upišite kod iz e-poruke ili kliknite poveznicu u njoj.',
    loginLinkCodeLabel: 'Kod iz e-poruke',
    loginLinkCodeSubmit: 'Potvrdi kod',
    loginLinkCodeChecking: 'Provjeravam…',
    loginLinkCodeWrong: 'Kod nije ispravan.',
    loginLinkCodeExpired: 'Kod je istekao. Pošaljite si novi.',
    loginLinkCodeUsed: 'Ovaj kod više ne vrijedi: iskorišten je (na primjer poveznicom u drugom pregledniku) ili ga je zamijenio noviji. Pošaljite si novi.',
    loginLinkCodeRefused: 'S ovim računom prijava na ovaj način nije moguća.',
    loginLinkCodeTooMany: 'Previše pokušaja. Pričekajte minutu i pokušajte ponovno.',
    loginLinkResend: 'Pošalji ponovno',
    loginLinkResendIn: 'Ponovno slanje za {min} min',
    loginLinkResendInS: 'Ponovno slanje za {s} s',
    // #668: mail-first login page, newest-code resend
    loginNoPasswordNeeded: 'Za prijavu vam ne treba lozinka.',
    loginWithPasswordToggle: 'Imate lozinku? Prijava lozinkom',
    loginLinkResent: 'Poslali smo novi kod. Vrijedi samo zadnji.',
    loginLinkResendUnsure: 'Veza s poslužiteljem je prekinuta. Pošaljite kod ponovno. Ako ste u međuvremenu primili novu e-poruku, upotrijebite poveznicu u njoj.',
    // Auth page - Tabs
    login: 'Prijava',
    register: 'Registracija',

    // Auth page - Form fields
    email: 'E-pošta',
    password: 'Lozinka',
    firstName: 'Ime',
    lastName: 'Prezime',

    // Auth page - Placeholders
    emailPlaceholder: 'vasa.posta@primjer.hr',
    passwordPlaceholder: 'Unesite lozinku',
    firstNamePlaceholder: 'Ivan',
    lastNamePlaceholder: 'Horvat',

    // Auth page - Buttons
    loginButton: 'Prijavi se',
    createAccount: 'Otvori račun',
    creatingAccount: 'Otvaram račun...',
    forgotPassword: 'Zaboravljena lozinka?',
    orContinueWith: 'ili nastavite s',

    // OAuth buttons
    continueWithGoogle: 'Nastavi s Google',
    continueWithMicrosoft: 'Nastavi s Microsoft',

    // Email verification status
    verifyingEmail: 'Provjeravam e-poštu...',
    emailVerified: 'E-pošta potvrđena!',
    emailVerifiedSuccess: 'Vaša e-pošta uspješno je potvrđena. Sada se možete prijaviti.',
    verificationFailed: 'Potvrda nije uspjela',
    verificationFailedMessage: 'Poveznica za potvrdu nije važeća ili je istekla.',
    requestNewLink: 'Zatraži novu poveznicu za potvrdu',

    // Verify email page
    continueToMap: 'Nastavi na kartu',
    backToLogin: 'Natrag na prijavu',
    verificationLinkInvalid: 'Poveznica za potvrdu nije važeća ili je istekla.',
    enterEmailForNewLink: 'Unesite svoju e-poštu za primanje nove poveznice za potvrdu:',
    resendVerificationEmail: 'Ponovno pošalji e-poštu za potvrdu',
    sending: 'Šaljem...',
    verificationEmailSent: '✓ E-pošta za potvrdu poslana! Provjerite svoj sandučić.',

    // Verify email page — pending activation
    pendingTitle: 'Provjerite svoj e-poštanski sandučić',
    pendingSentTo: 'Poveznicu za potvrdu poslali smo na',
    pendingExpiry: 'Poveznica vrijedi 24 sata. Račun će biti aktivan kada je otvorite.',
    pendingWrongAddress: 'Pogrešna adresa e-pošte?',
    pendingRegisterAgain: 'Registrirajte se ponovno s ispravnom adresom.',
    sendFailedTitle: 'E-poštu nije bilo moguće poslati',
    sendFailedInfo: 'E-poštu za potvrdu nije bilo moguće poslati na',
    sendFailedRetry: 'Pokušajte je poslati ponovno u nastavku.',
    continueToLogin: 'Nastavi na prijavu',
    resendAlreadyVerified: 'Ova adresa e-pošte već je potvrđena — možete se prijaviti.',
    resendInvalidEmail: 'Adresa e-pošte nije važeća. Provjerite je li ispravno upisana.',
    resendTooMany: 'Previše pokušaja. Pričekajte nekoliko minuta pa pokušajte ponovno.',
    emailNotVerifiedError: 'Vaša adresa e-pošte još nije potvrđena. Račun će biti aktivan kada otvorite poveznicu iz e-pošte za potvrdu.',
    verifyEmailCta: 'Potvrdite svoju e-poštu',
    resendNotReceived: 'Niste primili e-poštu za potvrdu?',
    oauthNoEmail: 'Vaš Microsoft račun nema adresu e-pošte. Stvorite račun s adresom e-pošte.',
    emailAlreadyRegistered: 'Račun s ovom adresom e-pošte već postoji.',
    conflictSignIn: 'Prijavite se',
    conflictOr: 'ili',
    conflictReset: 'zatražite novu lozinku',
    conflictResetNote: 'Promjena lozinke ujedno potvrđuje vašu adresu e-pošte.',

    // Password strength
    passwordStrength: 'Jačina lozinke',
    passwordTooWeak: 'Preslaba',
    passwordWeak: 'Slaba - Dodajte',
    passwordFair: 'Dovoljna - Dodajte',
    passwordGood: 'Dobra - Lozinka ispunjava uvjete',
    passwordStrong: 'Jaka - Izvrsna lozinka!',
    atLeast8Chars: 'najmanje 8 znakova',
    anUppercase: 'veliko slovo',
    aLowercase: 'malo slovo',
    aNumber: 'broj',

    // User menu
    logout: 'Odjava',
    loggingOut: 'Odjavljujem...',
    emailAccount: 'Račun e-pošte',
    googleAccount: 'Google račun',
    microsoftAccount: 'Microsoft račun',
    legacyAccount: 'Naslijeđeni račun',

    // Errors
    loginFailed: 'Prijava nije uspjela. Provjerite svoje podatke za prijavu.',
    registrationFailed: 'Registracija nije uspjela. Pokušajte ponovno.',
    // #675: signup without a password, the name step, the KingCAD done page
    registerSubmit: 'Stvori račun i pošalji kod',
    registerNoPasswordNote: 'Lozinka nije potrebna. Kod za prijavu dobit ćete e-poštom.',
    registerCodeSent: 'Kod smo poslali na {email}. Provjerite poštu.',
    registerFixAddress: 'Pogrešna e-adresa?',
    registerFixAddressLink: 'Ispravite je',
    registerNameRequired: 'Upišite ime i prezime.',
    registerDoneTitle: 'Račun je spreman',
    registerDoneBody: 'Vratite se u program {product} i kliknite »Prijava u pregledniku«.',
    registerDoneOldBuild: 'Ako vaša verzija programa nema taj gumb, postavite lozinku u profilu.',
    registerDoneProfileLink: 'Otvori profil',
    welcomeTitle: 'Kako da vas zovemo?',
    welcomeIntro: 'Kolege vide vaše ime kod zadataka, komentara i licenci.',
    welcomeSave: 'Spremi i nastavi',
    welcomeSkip: 'Preskoči',
    welcomeSaveFailed: 'Spremanje nije uspjelo. Pokušajte ponovno.',
    oauthEmailConflict: 'Račun s ovom adresom e-pošte već postoji. Prijavite se lozinkom ili načinom koji ste dosad koristili.',
    emailVerificationFailed: 'Potvrda e-pošte nije uspjela. Pokušajte ponovno ili zatražite novu poveznicu za potvrdu.',
    networkError: 'Povezivanje s poslužiteljem nije uspjelo. Provjerite internetsku vezu.',
    serverUnavailable: 'Poslužitelj trenutačno nije dostupan. Pokušajte ponovno za nekoliko trenutaka.',

    // Auth pages — desktop-client mode + inline copy (kingcad#426)
    optional: '(neobavezno)',
    desktopRegisterHint: 'Ovim ćete se računom prijavljivati u program {product}.',
    desktopVerifiedReturnHint: 'Račun je aktiviran — vratite se u {product} i prijavite se.',
    alreadyHaveAccount: 'Već imate račun?',
    signInLink: 'Prijavite se',
    noAccountYet: 'Nemate račun?',
    createAccountLink: 'Otvorite ga',

    // Forgot password page
    forgotPasswordTitle: 'Zaboravili ste lozinku?',
    forgotPasswordIntro: 'Unesite svoju adresu e-pošte i poslat ćemo vam poveznicu za promjenu lozinke.',
    sendResetLink: 'Pošalji poveznicu',
    forgotPasswordFailed: 'Poveznicu za promjenu lozinke nije bilo moguće poslati.',
    forgotCooldownNotice: 'Pričekajte {seconds} s prije nego što zatražite novu poveznicu.',
    checkYourEmail: 'Provjerite svoju e-poštu',
    resetLinkSent: 'Ako račun s tom adresom e-pošte postoji, poslali smo vam poveznicu za promjenu lozinke. Provjerite svoj sandučić.',
    genericError: 'Došlo je do pogreške. Pokušajte ponovno.',

    // Reset password page
    resetPasswordTitle: 'Promjena lozinke',
    resetPasswordIntro: 'U nastavku unesite novu lozinku.',
    newPassword: 'Nova lozinka',
    newPasswordPlaceholder: 'Unesite novu lozinku',
    confirmNewPassword: 'Potvrdite novu lozinku',
    confirmNewPasswordPlaceholder: 'Ponovno unesite novu lozinku',
    resetPasswordButton: 'Promijeni lozinku',
    resetting: 'Mijenjam lozinku...',
    passwordResetTitle: 'Lozinka je promijenjena!',
    passwordResetSuccess: 'Vaša lozinka uspješno je promijenjena. Sada se možete prijaviti novom lozinkom.',
    invalidLinkTitle: 'Nevažeća ili istekla poveznica',
    invalidLinkMessage: 'Ova poveznica za promjenu lozinke nije važeća ili je istekla. Zatražite novu.',
    requestNewResetLink: 'Zatraži novu poveznicu',
    passwordsDoNotMatch: 'Lozinke se ne podudaraju.',
    passwordMinLength: 'Lozinka mora imati najmanje 8 znakova.',
    passwordNeedsUppercase: 'Lozinka mora sadržavati barem jedno veliko slovo.',
    passwordNeedsLowercase: 'Lozinka mora sadržavati barem jedno malo slovo.',
    passwordNeedsNumber: 'Lozinka mora sadržavati barem jedan broj.',
    resetPasswordFailed: 'Lozinku nije bilo moguće promijeniti.',
    strengthWeak: 'Slaba',
    strengthFair: 'Dovoljna',
    strengthGood: 'Dobra',
    strengthStrong: 'Jaka',
    authErrInvalidCredentials: 'Pogrešna e-pošta ili lozinka.',
    authErrAccountDisabled: 'Ovaj je račun onemogućen.',
    authErrTooMany: 'Previše pokušaja prijave. Pokušajte ponovno za {seconds} s.',
    authErrWeakPassword: 'Lozinka mora imati najmanje 8 znakova, veliko i malo slovo te znamenku.',
    authErrEmailTaken: 'Račun s ovom e-poštom već postoji.',
  },

  sr: {
    // #569 desktop browser login + email login link
    deviceLoginTitle: 'Prijava uređaja KingCAD',
    deviceLoginLoading: 'Učitavam zahtev za prijavu…',
    deviceLoginSignedInAs: 'Prijavljeni ste kao',
    deviceLoginQuestion: 'Dozvoliti prijavu u {product} na ovom uređaju?',
    deviceLoginMachine: 'Uređaj',
    deviceLoginWindowsUser: 'Windows korisnik',
    deviceLoginProgram: 'Program',
    deviceLoginRemote: 'udaljena sesija',
    deviceLoginExpiresIn: 'Zahtev ističe za {min} min.',
    deviceLoginAllow: 'Dozvoli',
    deviceLoginApproving: 'Potvrđujem…',
    deviceLoginNotYou: 'Niste vi? Prijava drugim nalogom',
    deviceLoginCancel: 'Otkaži',
    deviceLoginDone: 'Gotovo — vratite se u {product}.',
    deviceLoginDoneClose: 'Ovu karticu možete zatvoriti.',
    deviceLoginExpired: 'Zahtev je istekao. U programu ponovo kliknite „Prijava u pregledaču“.',
    deviceLoginDenied: 'Prijava je otkazana.',
    deviceLoginConsumed: 'Ovaj zahtev je već iskorišćen.',
    deviceLoginNotFound: 'Nepoznat zahtev za prijavu.',
    deviceLoginError: 'Došlo je do greške. Pokušajte ponovo.',
    deviceLoginBackHome: 'Na početnu stranu',
    loginLinkRedeeming: 'Prijavljujem…',
    loginLinkSuccess: 'Prijavljeni ste',
    loginLinkContinue: 'Nastavi',
    loginLinkFailed: 'Link nije važeći ili je istekao',
    loginLinkFailedHint: 'Link važi 15 minuta i može se upotrebiti samo jednom.',
    loginLinkRequestNew: 'Nazad na prijavu',
    loginLinkOption: 'Pošalji mi kod za prijavu',
    loginLinkSending: 'Šaljem…',
    loginLinkSent: 'Ako nalog postoji, poslali smo kod na {email}. Proverite poštu.',
    loginLinkNeedEmail: 'Prvo unesite e-adresu.',
    loginLinkError: 'Slanje nije uspelo. Pokušajte ponovo.',
    // #667 code door — on the main road since #668, so in every language
    loginLinkSentCode: 'Ovde upišite kod iz e-poruke ili kliknite link u njoj.',
    loginLinkCodeLabel: 'Kod iz e-poruke',
    loginLinkCodeSubmit: 'Potvrdi kod',
    loginLinkCodeChecking: 'Proveravam…',
    loginLinkCodeWrong: 'Kod nije ispravan.',
    loginLinkCodeExpired: 'Kod je istekao. Pošaljite sebi novi.',
    loginLinkCodeUsed: 'Ovaj kod više ne važi: iskorišćen je (na primer linkom u drugom pregledaču) ili ga je zamenio noviji. Pošaljite sebi novi.',
    loginLinkCodeRefused: 'Sa ovim nalogom prijava na ovaj način nije moguća.',
    loginLinkCodeTooMany: 'Previše pokušaja. Sačekajte minut i pokušajte ponovo.',
    loginLinkResend: 'Pošalji ponovo',
    loginLinkResendIn: 'Ponovno slanje za {min} min',
    loginLinkResendInS: 'Ponovno slanje za {s} s',
    // #668: mail-first login page, newest-code resend
    loginNoPasswordNeeded: 'Za prijavu vam nije potrebna lozinka.',
    loginWithPasswordToggle: 'Imate lozinku? Prijava lozinkom',
    loginLinkResent: 'Poslali smo novi kod. Važi samo poslednji.',
    loginLinkResendUnsure: 'Veza sa serverom je prekinuta. Pošaljite kod ponovo. Ako ste u međuvremenu primili novu e-poruku, upotrebite link u njoj.',
    // Auth page - Tabs
    login: 'Prijava',
    register: 'Registracija',

    // Auth page - Form fields
    email: 'E-pošta',
    password: 'Lozinka',
    firstName: 'Ime',
    lastName: 'Prezime',

    // Auth page - Placeholders
    emailPlaceholder: 'vasa.posta@primer.rs',
    passwordPlaceholder: 'Unesite lozinku',
    firstNamePlaceholder: 'Marko',
    lastNamePlaceholder: 'Marković',

    // Auth page - Buttons
    loginButton: 'Prijavi se',
    createAccount: 'Otvori nalog',
    creatingAccount: 'Otvaram nalog...',
    forgotPassword: 'Zaboravljena lozinka?',
    orContinueWith: 'ili nastavite sa',

    // OAuth buttons
    continueWithGoogle: 'Nastavi sa Google',
    continueWithMicrosoft: 'Nastavi sa Microsoft',

    // Email verification status
    verifyingEmail: 'Proveravam e-poštu...',
    emailVerified: 'E-pošta je potvrđena!',
    emailVerifiedSuccess: 'Vaša e-pošta je uspešno potvrđena. Sada možete da se prijavite.',
    verificationFailed: 'Potvrda nije uspela',
    verificationFailedMessage: 'Link za potvrdu nije važeći ili je istekao.',
    requestNewLink: 'Zatraži novi link za potvrdu',

    // Verify email page
    continueToMap: 'Nastavi na mapu',
    backToLogin: 'Nazad na prijavu',
    verificationLinkInvalid: 'Link za potvrdu nije važeći ili je istekao.',
    enterEmailForNewLink: 'Unesite svoju e-poštu da biste dobili novi link za potvrdu:',
    resendVerificationEmail: 'Ponovo pošalji e-poštu za potvrdu',
    sending: 'Šaljem...',
    verificationEmailSent: '✓ E-pošta za potvrdu je poslata! Proverite svoje sanduče.',

    // Verify email page — pending activation
    pendingTitle: 'Proverite svoje e-poštansko sanduče',
    pendingSentTo: 'Link za potvrdu smo poslali na',
    pendingExpiry: 'Link važi 24 sata. Nalog će biti aktivan čim ga otvorite.',
    pendingWrongAddress: 'Pogrešna adresa e-pošte?',
    pendingRegisterAgain: 'Registrujte se ponovo sa ispravnom adresom.',
    sendFailedTitle: 'E-poštu nije bilo moguće poslati',
    sendFailedInfo: 'E-poštu za potvrdu nije bilo moguće poslati na',
    sendFailedRetry: 'Pokušajte da je pošaljete ponovo ispod.',
    continueToLogin: 'Nastavi na prijavu',
    resendAlreadyVerified: 'Ova adresa e-pošte je već potvrđena — možete da se prijavite.',
    resendInvalidEmail: 'Adresa e-pošte nije važeća. Proverite da li je ispravno upisana.',
    resendTooMany: 'Previše pokušaja. Sačekajte nekoliko minuta pa pokušajte ponovo.',
    emailNotVerifiedError: 'Vaša adresa e-pošte još nije potvrđena. Nalog će biti aktivan čim otvorite link iz e-pošte za potvrdu.',
    verifyEmailCta: 'Potvrdite svoju e-poštu',
    resendNotReceived: 'Niste primili e-poštu za potvrdu?',
    oauthNoEmail: 'Vaš Microsoft nalog nema adresu e-pošte. Napravite nalog sa adresom e-pošte.',
    emailAlreadyRegistered: 'Nalog sa ovom adresom e-pošte već postoji.',
    conflictSignIn: 'Prijavite se',
    conflictOr: 'ili',
    conflictReset: 'zatražite novu lozinku',
    conflictResetNote: 'Promena lozinke istovremeno potvrđuje vašu adresu e-pošte.',

    // Password strength
    passwordStrength: 'Jačina lozinke',
    passwordTooWeak: 'Preslaba',
    passwordWeak: 'Slaba - Dodajte',
    passwordFair: 'Dovoljna - Dodajte',
    passwordGood: 'Dobra - Lozinka ispunjava uslove',
    passwordStrong: 'Jaka - Odlična lozinka!',
    atLeast8Chars: 'najmanje 8 znakova',
    anUppercase: 'veliko slovo',
    aLowercase: 'malo slovo',
    aNumber: 'broj',

    // User menu
    logout: 'Odjava',
    loggingOut: 'Odjavljujem...',
    emailAccount: 'Nalog e-pošte',
    googleAccount: 'Google nalog',
    microsoftAccount: 'Microsoft nalog',
    legacyAccount: 'Nasleđeni nalog',

    // Errors
    loginFailed: 'Prijava nije uspela. Proverite svoje podatke za prijavu.',
    registrationFailed: 'Registracija nije uspela. Pokušajte ponovo.',
    // #675: signup without a password, the name step, the KingCAD done page
    registerSubmit: 'Napravi nalog i pošalji kod',
    registerNoPasswordNote: 'Lozinka nije potrebna. Kod za prijavu dobićete e-poštom.',
    registerCodeSent: 'Kod smo poslali na {email}. Proverite poštu.',
    registerFixAddress: 'Pogrešna e-adresa?',
    registerFixAddressLink: 'Ispravite je',
    registerNameRequired: 'Upišite ime i prezime.',
    registerDoneTitle: 'Nalog je spreman',
    registerDoneBody: 'Vratite se u program {product} i kliknite »Prijava u pregledaču«.',
    registerDoneOldBuild: 'Ako vaša verzija programa nema to dugme, postavite lozinku u profilu.',
    registerDoneProfileLink: 'Otvori profil',
    welcomeTitle: 'Kako da vas zovemo?',
    welcomeIntro: 'Kolege vide vaše ime kod zadataka, komentara i licenci.',
    welcomeSave: 'Sačuvaj i nastavi',
    welcomeSkip: 'Preskoči',
    welcomeSaveFailed: 'Čuvanje nije uspelo. Pokušajte ponovo.',
    oauthEmailConflict: 'Nalog sa ovom adresom e-pošte već postoji. Prijavite se lozinkom ili načinom koji ste do sada koristili.',
    emailVerificationFailed: 'Potvrda e-pošte nije uspela. Pokušajte ponovo ili zatražite novi link za potvrdu.',
    networkError: 'Povezivanje sa serverom nije uspelo. Proverite internet vezu.',
    serverUnavailable: 'Server trenutno nije dostupan. Pokušajte ponovo za nekoliko trenutaka.',

    // Auth pages — desktop-client mode + inline copy (kingcad#426)
    optional: '(opciono)',
    desktopRegisterHint: 'Ovim nalogom ćete se prijavljivati u program {product}.',
    desktopVerifiedReturnHint: 'Nalog je aktiviran — vratite se u {product} i prijavite se.',
    alreadyHaveAccount: 'Već imate nalog?',
    signInLink: 'Prijavite se',
    noAccountYet: 'Nemate nalog?',
    createAccountLink: 'Otvorite ga',

    // Forgot password page
    forgotPasswordTitle: 'Zaboravili ste lozinku?',
    forgotPasswordIntro: 'Unesite svoju adresu e-pošte i poslaćemo vam link za promenu lozinke.',
    sendResetLink: 'Pošalji link',
    forgotPasswordFailed: 'Link za promenu lozinke nije bilo moguće poslati.',
    forgotCooldownNotice: 'Sačekajte {seconds} s pre nego što zatražite novi link.',
    checkYourEmail: 'Proverite svoju e-poštu',
    resetLinkSent: 'Ako nalog sa tom adresom e-pošte postoji, poslali smo vam link za promenu lozinke. Proverite svoje sanduče.',
    genericError: 'Došlo je do greške. Pokušajte ponovo.',

    // Reset password page
    resetPasswordTitle: 'Promena lozinke',
    resetPasswordIntro: 'Ispod unesite novu lozinku.',
    newPassword: 'Nova lozinka',
    newPasswordPlaceholder: 'Unesite novu lozinku',
    confirmNewPassword: 'Potvrdite novu lozinku',
    confirmNewPasswordPlaceholder: 'Ponovo unesite novu lozinku',
    resetPasswordButton: 'Promeni lozinku',
    resetting: 'Menjam lozinku...',
    passwordResetTitle: 'Lozinka je promenjena!',
    passwordResetSuccess: 'Vaša lozinka je uspešno promenjena. Sada možete da se prijavite novom lozinkom.',
    invalidLinkTitle: 'Nevažeći ili istekli link',
    invalidLinkMessage: 'Ovaj link za promenu lozinke nije važeći ili je istekao. Zatražite novi.',
    requestNewResetLink: 'Zatraži novi link',
    passwordsDoNotMatch: 'Lozinke se ne podudaraju.',
    passwordMinLength: 'Lozinka mora imati najmanje 8 znakova.',
    passwordNeedsUppercase: 'Lozinka mora sadržati bar jedno veliko slovo.',
    passwordNeedsLowercase: 'Lozinka mora sadržati bar jedno malo slovo.',
    passwordNeedsNumber: 'Lozinka mora sadržati bar jedan broj.',
    resetPasswordFailed: 'Lozinku nije bilo moguće promeniti.',
    strengthWeak: 'Slaba',
    strengthFair: 'Dovoljna',
    strengthGood: 'Dobra',
    strengthStrong: 'Jaka',
    authErrInvalidCredentials: 'Pogrešna e-pošta ili lozinka.',
    authErrAccountDisabled: 'Ovaj nalog je onemogućen.',
    authErrTooMany: 'Previše pokušaja prijave. Pokušajte ponovo za {seconds} s.',
    authErrWeakPassword: 'Lozinka mora imati najmanje 8 znakova, veliko i malo slovo i cifru.',
    authErrEmailTaken: 'Nalog sa ovom e-poštom već postoji.',
  },

  bs: {
    // #569 desktop browser login + email login link
    deviceLoginTitle: 'Prijava uređaja KingCAD',
    deviceLoginLoading: 'Učitavam zahtjev za prijavu…',
    deviceLoginSignedInAs: 'Prijavljeni ste kao',
    deviceLoginQuestion: 'Dozvoliti prijavu u {product} na ovom uređaju?',
    deviceLoginMachine: 'Uređaj',
    deviceLoginWindowsUser: 'Windows korisnik',
    deviceLoginProgram: 'Program',
    deviceLoginRemote: 'udaljena sesija',
    deviceLoginExpiresIn: 'Zahtjev ističe za {min} min.',
    deviceLoginAllow: 'Dozvoli',
    deviceLoginApproving: 'Potvrđujem…',
    deviceLoginNotYou: 'Niste vi? Prijava drugim računom',
    deviceLoginCancel: 'Otkaži',
    deviceLoginDone: 'Gotovo — vratite se u {product}.',
    deviceLoginDoneClose: 'Ovu karticu možete zatvoriti.',
    deviceLoginExpired: 'Zahtjev je istekao. U programu ponovo kliknite „Prijava u pregledniku“.',
    deviceLoginDenied: 'Prijava je otkazana.',
    deviceLoginConsumed: 'Ovaj zahtjev je već iskorišten.',
    deviceLoginNotFound: 'Nepoznat zahtjev za prijavu.',
    deviceLoginError: 'Došlo je do greške. Pokušajte ponovo.',
    deviceLoginBackHome: 'Na početnu stranicu',
    loginLinkRedeeming: 'Prijavljujem…',
    loginLinkSuccess: 'Prijavljeni ste',
    loginLinkContinue: 'Nastavi',
    loginLinkFailed: 'Link nije važeći ili je istekao',
    loginLinkFailedHint: 'Link vrijedi 15 minuta i može se upotrijebiti samo jednom.',
    loginLinkRequestNew: 'Nazad na prijavu',
    loginLinkOption: 'Pošalji mi kod za prijavu',
    loginLinkSending: 'Šaljem…',
    loginLinkSent: 'Ako račun postoji, poslali smo kod na {email}. Provjerite poštu.',
    loginLinkNeedEmail: 'Prvo unesite e-adresu.',
    loginLinkError: 'Slanje nije uspjelo. Pokušajte ponovo.',
    // #667 code door — on the main road since #668, so in every language
    loginLinkSentCode: 'Ovdje upišite kod iz e-poruke ili kliknite link u njoj.',
    loginLinkCodeLabel: 'Kod iz e-poruke',
    loginLinkCodeSubmit: 'Potvrdi kod',
    loginLinkCodeChecking: 'Provjeravam…',
    loginLinkCodeWrong: 'Kod nije ispravan.',
    loginLinkCodeExpired: 'Kod je istekao. Pošaljite sebi novi.',
    loginLinkCodeUsed: 'Ovaj kod više ne vrijedi: iskorišten je (na primjer linkom u drugom pregledniku) ili ga je zamijenio noviji. Pošaljite sebi novi.',
    loginLinkCodeRefused: 'S ovim računom prijava na ovaj način nije moguća.',
    loginLinkCodeTooMany: 'Previše pokušaja. Sačekajte minutu i pokušajte ponovo.',
    loginLinkResend: 'Pošalji ponovo',
    loginLinkResendIn: 'Ponovno slanje za {min} min',
    loginLinkResendInS: 'Ponovno slanje za {s} s',
    // #668: mail-first login page, newest-code resend
    loginNoPasswordNeeded: 'Za prijavu vam ne treba lozinka.',
    loginWithPasswordToggle: 'Imate lozinku? Prijava lozinkom',
    loginLinkResent: 'Poslali smo novi kod. Vrijedi samo zadnji.',
    loginLinkResendUnsure: 'Veza sa serverom je prekinuta. Pošaljite kod ponovo. Ako ste u međuvremenu primili novu e-poruku, upotrijebite link u njoj.',
    // Auth page - Tabs
    login: 'Prijava',
    register: 'Registracija',

    // Auth page - Form fields
    email: 'E-pošta',
    password: 'Lozinka',
    firstName: 'Ime',
    lastName: 'Prezime',

    // Auth page - Placeholders
    emailPlaceholder: 'vasa.posta@primjer.ba',
    passwordPlaceholder: 'Unesite lozinku',
    firstNamePlaceholder: 'Amir',
    lastNamePlaceholder: 'Hodžić',

    // Auth page - Buttons
    loginButton: 'Prijavi se',
    createAccount: 'Otvori račun',
    creatingAccount: 'Otvaram račun...',
    forgotPassword: 'Zaboravljena lozinka?',
    orContinueWith: 'ili nastavite sa',

    // OAuth buttons
    continueWithGoogle: 'Nastavi sa Google',
    continueWithMicrosoft: 'Nastavi sa Microsoft',

    // Email verification status
    verifyingEmail: 'Provjeravam e-poštu...',
    emailVerified: 'E-pošta je potvrđena!',
    emailVerifiedSuccess: 'Vaša e-pošta je uspješno potvrđena. Sada se možete prijaviti.',
    verificationFailed: 'Potvrda nije uspjela',
    verificationFailedMessage: 'Link za potvrdu nije važeći ili je istekao.',
    requestNewLink: 'Zatraži novi link za potvrdu',

    // Verify email page
    continueToMap: 'Nastavi na mapu',
    backToLogin: 'Nazad na prijavu',
    verificationLinkInvalid: 'Link za potvrdu nije važeći ili je istekao.',
    enterEmailForNewLink: 'Unesite svoju e-poštu da biste dobili novi link za potvrdu:',
    resendVerificationEmail: 'Ponovo pošalji e-poštu za potvrdu',
    sending: 'Šaljem...',
    verificationEmailSent: '✓ E-pošta za potvrdu je poslana! Provjerite svoje sanduče.',

    // Verify email page — pending activation
    pendingTitle: 'Provjerite svoje e-poštansko sanduče',
    pendingSentTo: 'Link za potvrdu smo poslali na',
    pendingExpiry: 'Link važi 24 sata. Račun će biti aktivan čim ga otvorite.',
    pendingWrongAddress: 'Pogrešna adresa e-pošte?',
    pendingRegisterAgain: 'Registrujte se ponovo sa ispravnom adresom.',
    sendFailedTitle: 'E-poštu nije bilo moguće poslati',
    sendFailedInfo: 'E-poštu za potvrdu nije bilo moguće poslati na',
    sendFailedRetry: 'Pokušajte je poslati ponovo ispod.',
    continueToLogin: 'Nastavi na prijavu',
    resendAlreadyVerified: 'Ova adresa e-pošte je već potvrđena — možete se prijaviti.',
    resendInvalidEmail: 'Adresa e-pošte nije važeća. Provjerite da li je ispravno upisana.',
    resendTooMany: 'Previše pokušaja. Sačekajte nekoliko minuta pa pokušajte ponovo.',
    emailNotVerifiedError: 'Vaša adresa e-pošte još nije potvrđena. Račun će biti aktivan čim otvorite link iz e-pošte za potvrdu.',
    verifyEmailCta: 'Potvrdite svoju e-poštu',
    resendNotReceived: 'Niste primili e-poštu za potvrdu?',
    oauthNoEmail: 'Vaš Microsoft račun nema adresu e-pošte. Napravite račun s adresom e-pošte.',
    emailAlreadyRegistered: 'Račun sa ovom adresom e-pošte već postoji.',
    conflictSignIn: 'Prijavite se',
    conflictOr: 'ili',
    conflictReset: 'zatražite novu lozinku',
    conflictResetNote: 'Promjena lozinke istovremeno potvrđuje vašu adresu e-pošte.',

    // Password strength
    passwordStrength: 'Jačina lozinke',
    passwordTooWeak: 'Preslaba',
    passwordWeak: 'Slaba - Dodajte',
    passwordFair: 'Dovoljna - Dodajte',
    passwordGood: 'Dobra - Lozinka ispunjava uslove',
    passwordStrong: 'Jaka - Odlična lozinka!',
    atLeast8Chars: 'najmanje 8 znakova',
    anUppercase: 'veliko slovo',
    aLowercase: 'malo slovo',
    aNumber: 'broj',

    // User menu
    logout: 'Odjava',
    loggingOut: 'Odjavljujem...',
    emailAccount: 'Račun e-pošte',
    googleAccount: 'Google račun',
    microsoftAccount: 'Microsoft račun',
    legacyAccount: 'Naslijeđeni račun',

    // Errors
    loginFailed: 'Prijava nije uspjela. Provjerite svoje podatke za prijavu.',
    registrationFailed: 'Registracija nije uspjela. Pokušajte ponovo.',
    // #675: signup without a password, the name step, the KingCAD done page
    registerSubmit: 'Napravi račun i pošalji kod',
    registerNoPasswordNote: 'Lozinka nije potrebna. Kod za prijavu dobit ćete e-poštom.',
    registerCodeSent: 'Kod smo poslali na {email}. Provjerite poštu.',
    registerFixAddress: 'Pogrešna e-adresa?',
    registerFixAddressLink: 'Ispravite je',
    registerNameRequired: 'Upišite ime i prezime.',
    registerDoneTitle: 'Račun je spreman',
    registerDoneBody: 'Vratite se u program {product} i kliknite »Prijava u pregledniku«.',
    registerDoneOldBuild: 'Ako vaša verzija programa nema to dugme, postavite lozinku u profilu.',
    registerDoneProfileLink: 'Otvori profil',
    welcomeTitle: 'Kako da vas zovemo?',
    welcomeIntro: 'Kolege vide vaše ime kod zadataka, komentara i licenci.',
    welcomeSave: 'Sačuvaj i nastavi',
    welcomeSkip: 'Preskoči',
    welcomeSaveFailed: 'Spremanje nije uspjelo. Pokušajte ponovo.',
    oauthEmailConflict: 'Račun sa ovom adresom e-pošte već postoji. Prijavite se lozinkom ili načinom koji ste do sada koristili.',
    emailVerificationFailed: 'Potvrda e-pošte nije uspjela. Pokušajte ponovo ili zatražite novi link za potvrdu.',
    networkError: 'Povezivanje sa serverom nije uspjelo. Provjerite internet vezu.',
    serverUnavailable: 'Server trenutno nije dostupan. Pokušajte ponovo za nekoliko trenutaka.',

    // Auth pages — desktop-client mode + inline copy (kingcad#426)
    optional: '(opcionalno)',
    desktopRegisterHint: 'Ovim računom ćete se prijavljivati u program {product}.',
    desktopVerifiedReturnHint: 'Račun je aktiviran — vratite se u {product} i prijavite se.',
    alreadyHaveAccount: 'Već imate račun?',
    signInLink: 'Prijavite se',
    noAccountYet: 'Nemate račun?',
    createAccountLink: 'Otvorite ga',

    // Forgot password page
    forgotPasswordTitle: 'Zaboravili ste lozinku?',
    forgotPasswordIntro: 'Unesite svoju adresu e-pošte i poslat ćemo vam link za promjenu lozinke.',
    sendResetLink: 'Pošalji link',
    forgotPasswordFailed: 'Link za promjenu lozinke nije bilo moguće poslati.',
    forgotCooldownNotice: 'Sačekajte {seconds} s prije nego što zatražite novi link.',
    checkYourEmail: 'Provjerite svoju e-poštu',
    resetLinkSent: 'Ako račun sa tom adresom e-pošte postoji, poslali smo vam link za promjenu lozinke. Provjerite svoje sanduče.',
    genericError: 'Došlo je do greške. Pokušajte ponovo.',

    // Reset password page
    resetPasswordTitle: 'Promjena lozinke',
    resetPasswordIntro: 'Ispod unesite novu lozinku.',
    newPassword: 'Nova lozinka',
    newPasswordPlaceholder: 'Unesite novu lozinku',
    confirmNewPassword: 'Potvrdite novu lozinku',
    confirmNewPasswordPlaceholder: 'Ponovo unesite novu lozinku',
    resetPasswordButton: 'Promijeni lozinku',
    resetting: 'Mijenjam lozinku...',
    passwordResetTitle: 'Lozinka je promijenjena!',
    passwordResetSuccess: 'Vaša lozinka je uspješno promijenjena. Sada se možete prijaviti novom lozinkom.',
    invalidLinkTitle: 'Nevažeći ili istekli link',
    invalidLinkMessage: 'Ovaj link za promjenu lozinke nije važeći ili je istekao. Zatražite novi.',
    requestNewResetLink: 'Zatraži novi link',
    passwordsDoNotMatch: 'Lozinke se ne podudaraju.',
    passwordMinLength: 'Lozinka mora imati najmanje 8 znakova.',
    passwordNeedsUppercase: 'Lozinka mora sadržavati barem jedno veliko slovo.',
    passwordNeedsLowercase: 'Lozinka mora sadržavati barem jedno malo slovo.',
    passwordNeedsNumber: 'Lozinka mora sadržavati barem jedan broj.',
    resetPasswordFailed: 'Lozinku nije bilo moguće promijeniti.',
    strengthWeak: 'Slaba',
    strengthFair: 'Dovoljna',
    strengthGood: 'Dobra',
    strengthStrong: 'Jaka',
    authErrInvalidCredentials: 'Pogrešna e-pošta ili lozinka.',
    authErrAccountDisabled: 'Ovaj račun je onemogućen.',
    authErrTooMany: 'Previše pokušaja prijave. Pokušajte ponovo za {seconds} s.',
    authErrWeakPassword: 'Lozinka mora imati najmanje 8 znakova, veliko i malo slovo i cifru.',
    authErrEmailTaken: 'Račun s ovom e-poštom već postoji.',
  },
};

/**
 * Map a BCP-47 browser tag to one of the seven supported languages.
 *
 * Matching is on the primary subtag, except for Serbo-Croatian, where the
 * REGION decides: `sr-*` is Serbian, `hr-BA` (Croats in Bosnia) is Croatian,
 * and only `bs-*` is Bosnian. Returns null when nothing matches.
 */
function languageFromBrowserTag(tag: string): Language | null {
  const lower = tag.toLowerCase();
  const primary = lower.split('-')[0];

  switch (primary) {
    case 'sl':
      return 'sl';
    case 'en':
      return 'en';
    case 'de':
      return 'de';
    case 'cs':
      return 'cs';
    case 'hr':
      return 'hr';
    case 'bs':
      return 'bs';
    case 'sr':
      return 'sr';
    // Legacy/aggregate Serbo-Croatian tags still emitted by some browsers.
    case 'sh':
      return 'sr';
    default:
      return null;
  }
}

/**
 * Detect user's preferred language.
 *
 * Priority:
 * 1. URL parameter (?lang=…) — how the KingCAD desktop clients pin the language
 * 2. Saved preference in localStorage
 * 3. Browser language
 * 4. Default to Slovenian (majority users)
 */
export function detectLanguage(): Language {
  // 1. Check URL parameter (the desktop deep links, testing and sharing)
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get('lang');
  if (isSupportedLanguage(urlLang)) {
    // Save to localStorage for persistence
    setLanguage(urlLang);
    return urlLang;
  }

  // 2. Check if user manually set language before
  // Guarded (#668): this runs at module top level on every auth page — a
  // storage that throws must cost the saved preference, not the page.
  let saved: string | null = null;
  try {
    saved = localStorage.getItem('gsx8_language');
  } catch {
    saved = null;
  }
  if (isSupportedLanguage(saved)) {
    return saved;
  }

  // 3. Check browser language
  const fromBrowser = languageFromBrowserTag(navigator.language || '');
  if (fromBrowser) {
    return fromBrowser;
  }

  // 4. Default to Slovenian (majority users are Slovenian)
  return 'sl';
}

/**
 * Resolve the language and stamp it on `<html lang>`.
 *
 * The auth pages render every string from this dictionary, so the document's
 * declared language has to follow the dictionary rather than the `lang="en"`
 * baked into the HTML — otherwise screen readers and the browser's translation
 * prompt both act on the wrong language.
 */
export function applyDocumentLanguage(): Language {
  const lang = detectLanguage();
  document.documentElement.lang = lang;
  return lang;
}

/**
 * Save language preference to localStorage.
 */
export function setLanguage(lang: Language): void {
  // Guarded (#668): a refused or full storage must not stop the caller —
  // detectLanguage still returns the language the URL asked for.
  try {
    localStorage.setItem('gsx8_language', lang);
  } catch {
    /* the preference is not saved; the page works without it */
  }
}

/**
 * #885: keys a page module adds to the dictionary (the Register služnosti keeps
 * its ~700 keys beside its own code, so the map bundle does not carry them).
 * Served by the same `translate()`, right after the core table of the language.
 */
const registered: Record<Language, Record<string, string>> = { sl: {}, en: {}, de: {}, cs: {}, hr: {}, sr: {}, bs: {} };

/**
 * Add a module's keys, per language. A key that already exists in the core
 * table is refused — one key, one meaning, wherever it is read.
 */
export function registerTranslations(dict: Partial<Record<Language, Record<string, string>>>): void {
  for (const lang of SUPPORTED_LANGUAGES) {
    const entries = dict[lang];
    if (!entries) continue;
    for (const key of Object.keys(entries)) {
      if (key in (translations[lang] as Record<string, string>) || key in translations.sl) {
        throw new Error(`i18n: key "${key}" already exists in the core dictionary`);
      }
    }
    Object.assign(registered[lang], entries);
  }
}

/**
 * Get translation for a key in the specified language.
 * Falls back to English if key not found, then returns the key itself.
 */
export function translate(key: string, lang: Language): string {
  const translation = (translations[lang] as any)[key] ?? registered[lang][key];
  if (translation) {
    return translation;
  }

  // Fallback to English
  const englishTranslation = (translations.en as any)[key] ?? registered.en[key];
  if (englishTranslation) {
    return englishTranslation;
  }

  // Last resort: return the key itself
  return key;
}

/**
 * Get current language from localStorage or detect it.
 */
export function getCurrentLanguage(): Language {
  return detectLanguage();
}

/**
 * BCP 47 locale for `Intl` formatting in the UI language — the decimal
 * separator, digit grouping and date order follow the language, not the
 * browser's region (a Czech user with an English browser still reads Czech
 * numbers on a Czech page).
 */
export function localeForLanguage(lang: Language): string {
  switch (lang) {
    case 'sl':
      return 'sl-SI';
    case 'en':
      return 'en-GB';
    case 'de':
      return 'de-DE';
    case 'cs':
      return 'cs-CZ';
    case 'hr':
      return 'hr-HR';
    case 'sr':
      return 'sr-Latn-RS';
    case 'bs':
      return 'bs-BA';
  }
}

/** `Intl.NumberFormat` in the UI language (see `localeForLanguage`). */
export function formatNumber(value: number, lang: Language, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(localeForLanguage(lang), options).format(value);
}
